import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./Generated.css";
import ReactDOM from "react-dom";
import {
  renderOverlapAlert,
  getOverlappingRoomNames,
  isRoomOverlapping,
} from "./features/warning";
import { formatImperialLength, formatImperialArea, coordToInches } from "./features/imperialUtils";
import {
  handleMouseDown,
  handleVertexMouseDown,
  handleTouchStart,
  handleVertexTouchStart,
  handleEdgeMouseDown,
  handleEdgeTouchStart,
  renderEdgeHandles,
} from "./features/resizing";
//import { ensureCoordinateSystem } from "./features/initialData";

import { handleUndoChanges } from "./features/undo1";
import WallThicknessPanel from "./components/WallThicknessPanel";
// import WallDrawingTool from "./components/WallDrawingTool"; // Now handled inline
import {
  calculateRoomCentroid,
  calculateRoomArea,
  calculateRoomDimensions,
} from "./features/roomCalculations";
import {
  calculateBounds,
  useCoordinateTransforms,
} from "./features/coordinates";
import {
  calculateRoomAngles,
  calculateAngleLabelPosition,
} from "./features/angleCalculations";
import { roomColors, floorPlanStyles, wallStyles } from "./features/styles";
import { useInterval } from "./features/intervalHooks";
import {
  useEventHandlers,
  handleRoomSelection,
  getLabelPlacementState,
  setLabelPlacementState,
  getInfoToolPanelState,
  getGlobalSelectedRoomType,
  handleLabelMouseDown,
  handleLabelTouchStart,
  getObjectPlacementState,
  setObjectPlacementState,
  handleObjectMouseDown,
  handleObjectTouchStart,
  getDoorPlacementState,
  setDoorPlacementState,
  getWindowPlacementState,
  setWindowPlacementState,
  handleDoorMouseDown,
  handleDoorTouchStart,
  handleWindowMouseDown,
  handleWindowTouchStart,
  getCornerPlacementState,
  setCornerPlacementState,
  addCornerToRoom,
} from "./features/eventHandlers";
import { isExternalWallSegment } from "./features/wallUtils";
import { saveFloorPlan } from "./features/save";
//import { initialFloorPlanData } from "./features/initialData";
import {
  handleRotateRoom,
  checkAndUpdateOverlaps as checkRoomOverlaps,
} from "./features/rotation";
import { useSimpleUndoHistory, useUndoShortcut } from "./features/history";
import { useFloorPlan } from "./FloorPlanContext";
// import WallDrawingTool from "./components/WallDrawingTool"; // Now handled inline
import RoomDrawingTool from "./components/RoomDrawingTool";
import BoundaryDrawingTool from "./components/BoundaryDrawingTool";
import { addIgnoredOverlaps } from "./features/ignoreoverlaps";
import {
  generateUniqueId,
  createWallPolygon,
  generateRoomPolygon,
} from "./features/drawingTools";
import {
  findNearestWall,
  isValidPlacementPosition,
  getSnappedPlacementResult,
  getAllWallSegments,
} from "./features/wallPlacement";
import {
  Point,
  Room,
  FloorPlanData,
  DragState,
  VisualizationOptions,
  Wall,
} from "./features/types";
import { view } from "framer-motion";

declare global {
  interface Window {
    dimensionFirstPoint?: Point;
    isRoomDraggingLocked?: boolean;
  }
}


const ensureCoordinateSystem = (data: any): any => {
  const visibleRooms = data.rooms?.filter((room: any) => room.room_type !== "Reference") || [];

  const referencePoints = [
    {
      id: "invisible-reference-point-1",
      room_type: "Reference",
      area: 0,
      height: 0,
      width: 0,
      floor_polygon: [
        { x: 0, z: 0 },
        { x: 0, z: 0 }
      ],
      is_regular: 0
    }
  ];

  return {
    ...data,
    rooms: [...visibleRooms, ...referencePoints],
    room_count: visibleRooms.filter((room: any) => room.room_type !== "Reference" && !room.isBoundary).length,
    total_area: visibleRooms.filter((room: any) => room.room_type !== "Reference" && !room.isBoundary).reduce((sum: number, room: any) => sum + (room.area || 0), 0)
  };
};

const getFallbackInitialData = (): FloorPlanData => {
  const referencePoints = [
    {
      id: "invisible-reference-point-1",
      room_type: "Reference",
      area: 0,
      height: 0,
      width: 0,
      floor_polygon: [
        { x: 0, z: 0 },
        { x: 0, z: 0 }
      ],
      is_regular: 0
    }
  ];

  return {
    room_count: 0,
    total_area: 0,
    room_types: [],
    rooms: referencePoints,
    objects: []
  };
};

export default function InteractiveFloorPlan({
  rotation = 0,
  visualizationOptions: propVisualizationOptions = {},
  viewOnly = false
}: {
  rotation?: number;
  visualizationOptions?: Partial<VisualizationOptions>;
  viewOnly?: boolean;
}) {
  const {
    floorPlanData: contextFloorPlanData,
    setFloorPlanData: setContextFloorPlanData,
    activeBuildTool,
    isDrawingActive,
    setIsDrawingActive,
    handleRoomTypeUpdate,
    addLabel,
    addObject,
    updateObject,
    setHasChanges,
    roomRotations,
    setRoomRotations,
    hasChanges,
    captureOriginalState,
    selectedRoomIds: contextSelectedRoomIds,
    setSelectedRoomIds: setContextSelectedRoomIds,
    activeTool,
    setActiveTool,
    openProjectPanel,
    visualizationOptions: contextVisualizationOptions,
    updateVisualizationOption,
    drawingWallWidth,
    updateLabel,
    addDoor,
    addWindow,
    updateDoor,
    updateWindow,
    addDimensionLine,
    updateDimensionLine,
    doorWidths,
    setDoorWidths,
    windowWidths,
    setWindowWidths,
    showIndividualWalls,
    flipDoorHorizontal,
    flipDoorVertical,
    flipWindowHorizontal,
    flipWindowVertical,
    wallWidths,
    setWallWidths,
    updateWallWidth,
    unitSystem,
    lastHistoryRestore,
    predefinedWallLength,
    predefinedWallAngle,
    isCustomRoomDrawing,
    setIsCustomRoomDrawing,
    setActiveBuildTool,
    setFirstWall,
    firstWall,
  } = useFloorPlan();

  const options: VisualizationOptions = {
    ...contextVisualizationOptions,
    ...propVisualizationOptions,
  };

  const [leftPosition, setLeftPosition] = useState("10%");
  const [floorPlanData, setFloorPlanData] = useState<FloorPlanData>(
    contextFloorPlanData || getFallbackInitialData()
  );

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isRoomDraggingLocked, setIsRoomDraggingLocked] = useState(true);

  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([]);
  const [scale, setScale] = useState(window.innerWidth < 850 ? 1 : 1.5);
  const floorPlanRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [isLabelPlacementMode, setIsLabelPlacementMode] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [selectedDoorId, setSelectedDoorId] = useState<string | null>(null);
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null);
  const [isObjectPlacementMode, setIsObjectPlacementMode] = useState(false);
  const [selectedDimensionId, setSelectedDimensionId] = useState<string | null>(
    null
  );
  const [objectRotations, setObjectRotations] = useState<{
    [key: string]: number;
  }>({});
  const [isRotatingObject, setIsRotatingObject] = useState(false);
  const [rotatingObjectId, setRotatingObjectId] = useState<string | null>(null);
  const [rotationStartAngle, setRotationStartAngle] = useState<number>(0);
  const [initialObjectRotation, setInitialObjectRotation] = useState<number>(0);

  const [isDimensionDrawing, setIsDimensionDrawing] = useState(false);
  const [dimensionStartPoint, setDimensionStartPoint] = useState<Point | null>(
    null
  );

  const checkWallModificationRef = useRef<() => void>(() => { });
  const [dimensionCurrentPoint, setDimensionCurrentPoint] =
    useState<Point | null>(null);

  const [overlappingRooms, setOverlappingRooms] = useState<string[][]>([]);
  const handleIgnoreOverlaps = useCallback(() => {
    if (overlappingRooms.length === 0) return;
    
    const projectId = window.location.pathname.split("/")[2];
    const planId = window.location.pathname.split("/")[3];
    console.log("Ignoring overlaps for rooms:", overlappingRooms);
    console.log("Project ID:", projectId, "Plan ID:", planId);

    const updatedData = addIgnoredOverlaps(floorPlanData, overlappingRooms);
    setFloorPlanData(updatedData);
    saveFloorPlan(updatedData,{}, setHasChanges, projectId, planId);
    setOverlappingRooms([]);
  }, [overlappingRooms, floorPlanData]);
  const [lastModifiedRoomId, setLastModifiedRoomId] = useState<string | null>(
    null
  );
  const [doorFlipStates, setDoorFlipStates] = useState<{
    [key: string]: { flipH: boolean; flipV: boolean };
  }>({});
  const [windowFlipStates, setWindowFlipStates] = useState<{
    [key: string]: { flipH: boolean; flipV: boolean };
  }>({});
  const [isDraggingWallWidth, setIsDraggingWallWidth] = useState(false);
  const [isModifyingWall, setIsModifyingWall] = useState(false);
  const [draggingWallId, setDraggingWallId] = useState<string | null>(null);
  const [initialWallWidth, setInitialWallWidth] = useState<number>(0);
  const [showWallThicknessPanel, setShowWallThicknessPanel] = useState(false);

  const [dragState, setDragState] = useState<DragState>({
    active: false,
    roomId: null,
    roomIds: [],
    vertexIndex: null,
    edgeIndices: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    isResizing: false,
    isEdgeResizing: false,
    isGroupOperation: false,
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const [parentScale, setParentScale] = useState(1);

  // Helper function to update context FloorPlanData while preserving history
  const updateContextFloorPlanData = useCallback((updates: Partial<FloorPlanData>) => {
    setContextFloorPlanData((current) => {
      // Explicitly preserve history from current state, never from updates
      const { history: _, ...updatesWithoutHistory } = updates as any;
      return {
        ...current,
        ...updatesWithoutHistory,
        history: current.history || []  // Always use current history
      };
    });
  }, [setContextFloorPlanData]);

  // Load custom textures from floorPlanData when component mounts or data changes
  useEffect(() => {
    // Load floor customization data if available (use contextFloorPlanData from useFloorPlan)
    if (contextFloorPlanData) {
      const customization = (contextFloorPlanData as any)?.customization;
      
      // Check if floor customization exists
      if (customization?.floor) {
        const floorKeys = Object.keys(customization.floor);
        
        // If floor is empty (reset case) or has customizations
        if (floorKeys.length === 0) {
          // Floor customizations were cleared - reset to empty
          const currentTextures = options.customRoomTextures || {};
          if (Object.keys(currentTextures).length > 0) {
            updateVisualizationOption('customRoomTextures', {});
          }
        } else {
          // Floor has customizations - load them
          const newCustomTextures: { [roomId: string]: string } = {};
          
          // Convert from backend format to internal format
          Object.entries(customization.floor).forEach(([roomId, floorData]: [string, any]) => {
            if (floorData.texture) {
              newCustomTextures[roomId] = floorData.texture;
            } else if (floorData.color) {
              newCustomTextures[roomId] = `color:${floorData.color}`;
            }
          });
          
          // Only update if different from current
          const currentTextures = options.customRoomTextures || {};
          const isDifferent = JSON.stringify(currentTextures) !== JSON.stringify(newCustomTextures);
          
          if (isDifferent) {
            updateVisualizationOption('customRoomTextures', newCustomTextures);
          }
        }
      }
    }
  }, [contextFloorPlanData, updateVisualizationOption]); // Use contextFloorPlanData instead

  useEffect(() => {
    const updateParentScale = () => {
      const parent = parentRef.current?.parentElement;
      if (parent) {
        const transform = window.getComputedStyle(parent).transform;
        if (transform && transform !== "none") {
          const matrix = transform.match(/matrix.*\((.+)\)/)?.[1].split(", ");
          if (matrix && matrix.length >= 6) {
            const scaleX = parseFloat(matrix[0]);
            setParentScale(scaleX);
          }
        }
      }
    };

    updateParentScale();

    const observer = new MutationObserver(() => {
      updateParentScale();
    });

    if (parentRef.current?.parentElement) {
      observer.observe(parentRef.current.parentElement, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => observer.disconnect();
  }, []);

  // Sync on initial mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current && contextFloorPlanData) {
      setFloorPlanData(contextFloorPlanData);
      isInitialMount.current = false;
    }
  }, [contextFloorPlanData]);

  // Explicit sync on history restore
  const prevHistoryTimestamp = useRef(0);
  useEffect(() => {
    if (lastHistoryRestore > prevHistoryTimestamp.current && contextFloorPlanData) {
      // console.log("Restoring history in Generated.tsx due to timestamp change");
      setFloorPlanData(contextFloorPlanData);
      prevHistoryTimestamp.current = lastHistoryRestore;
    }
  }, [lastHistoryRestore, contextFloorPlanData]);

  useEffect(() => {
    if (floorPlanData.rooms) {
      let hasUpdates = false;
      const newWallWidths = { ...wallWidths };

      floorPlanData.rooms.forEach((room) => {
        if (room.room_type === "Wall" && !(room.id in newWallWidths)) {
          newWallWidths[room.id] = room.width || 6;
          hasUpdates = true;
        }
      });

      floorPlanData.rooms.forEach((room) => {
        if (room.room_type !== "Wall" && room.floor_polygon.length > 2) {
          for (let i = 0; i < room.floor_polygon.length; i++) {
            const wallId = `${room.id}-wall-${i}`;
            if (!(wallId in newWallWidths)) {
              newWallWidths[wallId] = 4;
              hasUpdates = true;
            }
          }
        }
      });

      if (hasUpdates) {
        setWallWidths(newWallWidths);
      }
    }
  }, [floorPlanData.rooms, wallWidths, setWallWidths]);

  useEffect(() => {
    let hasUpdates = false;
    const newDoorWidths = { ...doorWidths };

    if (floorPlanData.doors) {
      floorPlanData.doors.forEach((door) => {
        if (!(door.id in newDoorWidths)) {
          newDoorWidths[door.id] = door.width || 2;
          hasUpdates = true;
        }
      });
    }

    if (hasUpdates) {
      setDoorWidths(newDoorWidths);
    }
  }, [floorPlanData.doors, doorWidths, setDoorWidths]);

  useEffect(() => {
    let hasUpdates = false;
    const newWindowWidths = { ...windowWidths };

    if (floorPlanData.windows) {
      floorPlanData.windows.forEach((window) => {
        if (!(window.id in newWindowWidths)) {
          newWindowWidths[window.id] = window.width || 2;
          hasUpdates = true;
        }
      });
    }

    if (hasUpdates) {
      setWindowWidths(newWindowWidths);
    }
  }, [floorPlanData.windows, windowWidths, setWindowWidths]);

  useEffect(() => {
    if (selectedRoomIds.length === 1) {
      setSelectedRoomId(selectedRoomIds[0]);

      const selectedRoom = floorPlanData.rooms.find(
        (room) => room.id === selectedRoomIds[0]
      );
      if (
        selectedRoom &&
        selectedRoom.room_type === "Wall" &&
        selectedRoom.floor_polygon.length === 2
      ) {
        setShowWallThicknessPanel(true);
      } else {
        setShowWallThicknessPanel(false);
      }
    } else {
      setSelectedRoomId(null);
      setShowWallThicknessPanel(false);
    }
  }, [selectedRoomIds, floorPlanData.rooms]);

  const { saveState, undo, hasUndoState } = useSimpleUndoHistory();

  const handleUndo = useCallback(() => {
    undo((state) => {
      if (state) {
        const undoData = JSON.parse(JSON.stringify(state.floorPlanData));
        const undoRotations = JSON.parse(JSON.stringify(state.roomRotations));

        setFloorPlanData(state.floorPlanData);
        setRoomRotations(state.roomRotations);
      }
    });
  }, [undo]);

  useUndoShortcut(handleUndo);

  const isCapturingState = useRef(false);

  const captureStateBeforeChange = () => {
    if (!isCapturingState.current) {
      isCapturingState.current = true;
      saveState({
        floorPlanData: JSON.parse(JSON.stringify(floorPlanData)),
        roomRotations: { ...roomRotations },
      });
    }
  };

  useEffect(() => {
    if (!dragState.active) {
      isCapturingState.current = false;

      setTimeout(() => {
        setIsModifyingWall(false);
        checkWallModificationAndFormRooms();

        setFloorPlanData((prevData) => {
          const totalArea = calculateTotalArea(prevData.rooms, roomRotations);
          return {
            ...prevData,
            total_area: parseFloat(totalArea.toFixed(2)),
          };
        });
      }, 100);
    }
  }, [dragState.active, roomRotations]);

  // Set flag when object/label/door/window/room is being dragged to prevent plan panning
  useEffect(() => {
    const isDraggingSomething =
      dragState.isObjectDragging ||
      dragState.isLabelDragging ||
      dragState.isDoorDragging ||
      dragState.isWindowDragging ||
      (dragState.active && (dragState.roomId || dragState.roomIds?.length > 0));

    if (isDraggingSomething) {
      document.body.setAttribute("data-room-touch-interaction", "true");
    } else {
      document.body.removeAttribute("data-room-touch-interaction");
    }

    return () => {
      document.body.removeAttribute("data-room-touch-interaction");
    };
  }, [
    dragState.isObjectDragging,
    dragState.isLabelDragging,
    dragState.isDoorDragging,
    dragState.isWindowDragging,
    dragState.active,
    dragState.roomId,
    dragState.roomIds,
  ]);

  const handleWallThicknessChange = useCallback(
    (newWidth: number) => {
      if (selectedRoomIds.length === 1) {
        const wallId = selectedRoomIds[0];

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        updateWallWidth(wallId, newWidth);
        setHasChanges(true);
      }
    },
    [
      selectedRoomIds,
      hasChanges,
      captureOriginalState,
      captureStateBeforeChange,
      updateWallWidth,
    ]
  );

  const handleMouseDownWithHistory = (
    event: React.MouseEvent,
    roomId: string,
    svgRef: React.RefObject<SVGSVGElement | null>,
    setDragState: React.Dispatch<React.SetStateAction<DragState>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setSelectedRoomIds: React.Dispatch<React.SetStateAction<string[]>>,
    selectedRoomIds: string[]
  ) => {
    if (viewOnly) {
      event.stopPropagation();
      return;
    }

    if (
      dragState.isDoorDragging ||
      dragState.isWindowDragging ||
      dragState.isLabelDragging ||
      dragState.isObjectDragging
    ) {
      event.stopPropagation();
      return;
    }

    if (
      isDrawingActive ||
      activeBuildTool === "drawWall" ||
      activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
    ) {
      return;
    }

    if (window.isRoomDraggingLocked) {
      event.stopPropagation();

      if (
        !selectedRoomIds.includes(roomId) &&
        !(event.ctrlKey || event.metaKey)
      ) {
        setSelectedRoomIds([roomId]);
      }
      return;
    }

    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();
    setLastModifiedRoomId(roomId);

    handleMouseDown(
      event,
      roomId,
      svgRef,
      setDragState,
      setHasChanges,
      setSelectedRoomIds,
      selectedRoomIds
    );
  };

  const handleVertexMouseDownWithHistory = (
    event: React.MouseEvent,
    roomId: string,
    vertexIndex: number,
    svgRef: React.RefObject<SVGSVGElement | null>,
    setDragState: React.Dispatch<React.SetStateAction<DragState>>,
    setSelectedRoomIds: React.Dispatch<React.SetStateAction<string[]>>,
    selectedRoomIds: string[],
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (viewOnly) {
      event.stopPropagation();
      return;
    }

    if (
      isDrawingActive ||
      activeBuildTool === "drawWall" ||
      activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
    ) {
      return;
    }

    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();
    setLastModifiedRoomId(roomId);

    const room = floorPlanData.rooms.find((r) => r.id === roomId);
    if (room && room.room_type === "Wall") {
      setIsModifyingWall(true);
    }

    handleVertexMouseDown(
      event,
      roomId,
      vertexIndex,
      svgRef,
      setDragState,
      setSelectedRoomIds,
      selectedRoomIds,
      setHasChanges,
      floorPlanData
    );
  };

  const getResponsiveSize = (baseSize: number, currentScale: number) => {
    return baseSize * (currentScale / 1.5);
  };

  const handleEdgeMouseDownWithHistory = (
    event: React.MouseEvent,
    roomId: string,
    edgeIndices: number[],
    svgRef: React.RefObject<SVGSVGElement | null>,
    setDragState: React.Dispatch<React.SetStateAction<DragState>>,
    setSelectedRoomIds: React.Dispatch<React.SetStateAction<string[]>>,
    selectedRoomIds: string[],
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (viewOnly) {
      event.stopPropagation();
      return;
    }
    if (
      isDrawingActive ||
      activeBuildTool === "drawWall" ||
      activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
    ) {
      return;
    }

    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();
    setLastModifiedRoomId(roomId);

    const room = floorPlanData.rooms.find((r) => r.id === roomId);
    if (room && room.room_type === "Wall") {
      setIsModifyingWall(true);
    }

    handleEdgeMouseDown(
      event,
      roomId,
      edgeIndices,
      svgRef,
      setDragState,
      setSelectedRoomIds,
      selectedRoomIds,
      setHasChanges,
      floorPlanData
    );
  };

  const shouldShowRotationIcon = () => {
    const infoPanelState = getInfoToolPanelState();
    return !infoPanelState.isActive;
  };

  const handleTouchStartWithHistory = (
    event: React.TouchEvent,
    roomId: string,
    svgRef: React.RefObject<SVGSVGElement | null>,
    setDragState: React.Dispatch<React.SetStateAction<DragState>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setSelectedRoomIds: React.Dispatch<React.SetStateAction<string[]>>,
    selectedRoomIds: string[]
  ) => {
    if (
      isDrawingActive ||
      activeBuildTool === "drawWall" ||
      activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
    ) {
      return;
    }

    if (window.isRoomDraggingLocked) {
      // When locked, allow selection but don't prevent panning
      // Selection happens, but touch event continues to bubble for background panning

      if (selectedRoomIds.length > 0) {
        if (!selectedRoomIds.includes(roomId)) {
          setSelectedRoomIds([...selectedRoomIds, roomId]);
        }
      } else {
        setSelectedRoomIds([roomId]);
      }

      // Don't return - let the touch event continue to bubble so background can pan
      // Don't call handleTouchStart either - we only want selection, not dragging
      return;
    }

    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();

    handleTouchStart(
      event,
      roomId,
      svgRef,
      setDragState,
      setHasChanges,
      setSelectedRoomIds,
      selectedRoomIds
    );
  };

  const findRoomAtPosition = (
    point: Point,
    floorPlanData: FloorPlanData,
    roomRotations: { [key: string]: number }
  ): Room | null => {
    const candidateRooms: { room: Room; area: number }[] = [];

    for (const room of floorPlanData.rooms) {
      if (room.room_type === "Wall" || room.room_type === "Reference" || room.floor_polygon.length < 3) continue;

      const roomRotation = roomRotations[room.id] || 0;
      const centroid = {
        x: room.floor_polygon.reduce((sum, p) => sum + p.x, 0) / room.floor_polygon.length,
        z: room.floor_polygon.reduce((sum, p) => sum + p.z, 0) / room.floor_polygon.length,
      };

      let adjustedPoint = point;
      if (roomRotation !== 0) {
        const radians = (-roomRotation * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const dx = point.x - centroid.x;
        const dz = point.z - centroid.z;

        adjustedPoint = {
          x: centroid.x + dx * cos - dz * sin,
          z: centroid.z + dx * sin + dz * cos,
        };
      }

      const polygon = room.floor_polygon;
      const wallDetectionDistance = 8.0;
      for (let i = 0; i < polygon.length; i++) {
        const startPoint = polygon[i];
        const endPoint = polygon[(i + 1) % polygon.length];

        const A = adjustedPoint.x - startPoint.x;
        const B = adjustedPoint.z - startPoint.z;
        const C = endPoint.x - startPoint.x;
        const D = endPoint.z - startPoint.z;

        const dot = A * C + B * D;
        const lenSquared = C * C + D * D;

        if (lenSquared === 0) continue;

        let param = dot / lenSquared;
        param = Math.max(0, Math.min(1, param));

        const closestX = startPoint.x + param * C;
        const closestZ = startPoint.z + param * D;

        const distance = Math.sqrt(
          Math.pow(closestX - adjustedPoint.x, 2) +
          Math.pow(closestZ - adjustedPoint.z, 2)
        );

        if (distance <= wallDetectionDistance) {
          candidateRooms.push({ room, area: room.area });
          break;
        }
      }

      if (candidateRooms.length === 0 || candidateRooms[candidateRooms.length - 1].room.id !== room.id) {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const condition1 = polygon[i].z > adjustedPoint.z;
          const condition2 = polygon[j].z > adjustedPoint.z;
          const crossProduct = ((polygon[j].x - polygon[i].x) * (adjustedPoint.z - polygon[i].z)) / (polygon[j].z - polygon[i].z) + polygon[i].x;

          if (condition1 !== condition2 && adjustedPoint.x < crossProduct) {
            inside = !inside;
          }
        }

        if (inside) {
          candidateRooms.push({ room, area: room.area });
        }
      }
    }

    if (candidateRooms.length === 0) return null;

    candidateRooms.sort((a, b) => a.area - b.area);
    return candidateRooms[0].room;
  };

  const handleVertexTouchStartWithHistory = (
    event: React.TouchEvent,
    roomId: string,
    vertexIndex: number,
    svgRef: React.RefObject<SVGSVGElement | null>,
    setDragState: React.Dispatch<React.SetStateAction<DragState>>,
    setSelectedRoomIds: React.Dispatch<React.SetStateAction<string[]>>,
    selectedRoomIds: string[],
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (
      isDrawingActive ||
      activeBuildTool === "drawWall" ||
      activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
    ) {
      return;
    }

    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();

    handleVertexTouchStart(
      event,
      roomId,
      vertexIndex,
      svgRef,
      setDragState,
      setSelectedRoomIds,
      selectedRoomIds,
      setHasChanges,
      floorPlanData
    );
  };

  const handleEdgeTouchStartWithHistory = (
    event: React.TouchEvent,
    roomId: string,
    edgeIndices: number[],
    svgRef: React.RefObject<SVGSVGElement | null>,
    setDragState: React.Dispatch<React.SetStateAction<DragState>>,
    setSelectedRoomIds: React.Dispatch<React.SetStateAction<string[]>>,
    selectedRoomIds: string[],
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (
      isDrawingActive ||
      activeBuildTool === "drawWall" ||
      activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
    ) {
      return;
    }

    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();

    handleEdgeTouchStart(
      event,
      roomId,
      edgeIndices,
      svgRef,
      setDragState,
      setSelectedRoomIds,
      selectedRoomIds,
      setHasChanges,
      floorPlanData
    );
  };

  const handleRotateRoomWithHistory = (
    roomId: string,
    direction: "left" | "right",
    roomRotations: { [key: string]: number },
    setRoomRotations: React.Dispatch<
      React.SetStateAction<{ [key: string]: number }>
    >,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    checkAndUpdateOverlaps: () => void
  ) => {
    if (!hasChanges) {
      captureOriginalState();
    }

    captureStateBeforeChange();

    handleRotateRoom(
      roomId,
      direction,
      roomRotations,
      setRoomRotations,
      setHasChanges,
      checkAndUpdateOverlaps,
      selectedRoomIds
    );

    setTimeout(() => {
      isCapturingState.current = false;
    }, 10);
  };

  const checkAndUpdateOverlaps = useCallback(() => {
    return checkRoomOverlaps(floorPlanData, roomRotations, setOverlappingRooms);
  }, [floorPlanData, roomRotations]);

  useEffect(() => {
    checkAndUpdateOverlaps();
  }, []);

  useInterval(() => {
    const hadOverlaps = overlappingRooms.length > 0;
    checkAndUpdateOverlaps();

    setTimeout(() => {
      if (hadOverlaps && overlappingRooms.length === 0 && lastModifiedRoomId) {
        setLastModifiedRoomId(null);
      }
    }, 500);
  }, 100);

  const getRoomType = (roomId: string) => {
    const room = floorPlanData.rooms.find((r) => r.id === roomId);
    return room?.room_type;
  };

  useEffect(() => {
    const handleResize = () => {
      setScale(window.innerWidth < 850 ? 1 : 1.5);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        !dragState.active &&
        !isDrawingActive &&
        !(activeBuildTool === "drawWall" || activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry")
      ) {
        const target = event.target as HTMLElement;

        const isClickInsideRoom = target.closest(".room-polygon");
        const isClickOnRotateButton = target.closest(".rotate-button");
        const isClickOnResizeHandle = target.closest(
          ".resize-handle, .resize-edge"
        );
        const isClickOnToolbar = target.closest(".selection-toolbar");
        const isClickOnLabel = target.closest(".floor-plan-label");
        const isClickOnObject = target.closest(".floor-plan-object");
        const isClickOnDoor = target.closest(".floor-plan-door");
        const isClickOnWindow = target.closest(".floor-plan-window");
        const isClickOnDimension = target.closest(".dimension-line");

        const isClickOnDoorControls = target.closest(
          ".floor-plan-door rect, .floor-plan-door circle"
        );
        const isClickOnWindowControls = target.closest(
          ".floor-plan-window rect"
        );

        if (
          !isClickInsideRoom &&
          !isClickOnRotateButton &&
          !isClickOnResizeHandle &&
          !isClickOnToolbar &&
          !isClickOnLabel &&
          !isClickOnObject &&
          !isClickOnDoor &&
          !isClickOnWindow &&
          selectedRoomIds.length > 0
        ) {
          setSelectedRoomIds([]);
        }

        if (
          !isClickOnLabel &&
          !isClickOnDoorControls &&
          !isClickOnWindowControls &&
          selectedLabelId !== null
        ) {
          setSelectedLabelId(null);
        }

        if (!isClickOnObject && selectedObjectId !== null) {
          setSelectedObjectId(null);
        }

        if (
          !isClickOnDoor &&
          !isClickOnDoorControls &&
          selectedDoorId !== null
        ) {
          setSelectedDoorId(null);
        }

        if (
          !isClickOnWindow &&
          !isClickOnWindowControls &&
          selectedWindowId !== null
        ) {
          setSelectedWindowId(null);
        }

        if (!isClickOnDimension && selectedDimensionId !== null) {
          setSelectedDimensionId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [
    selectedRoomIds,
    selectedLabelId,
    selectedObjectId,
    selectedDoorId,
    selectedWindowId,
    selectedDimensionId,
    dragState.active,
    isDrawingActive,
    activeBuildTool,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        if(editingWallLength){
          return;
        }
        if (selectedLabelId) {
          captureStateBeforeChange();
          setFloorPlanData((prevData) => {
            const updatedData = {
              ...prevData,
              labels: prevData.labels
                ? prevData.labels.filter(
                  (label) => label.id !== selectedLabelId
                )
                : [],
            };
            setTimeout(() => {
              updateContextFloorPlanData({ labels: updatedData.labels });
            }, 0);
            return updatedData;
          });
          setSelectedLabelId(null);
          setHasChanges(true);
        } else if (selectedObjectId) {
          if (isRotatingObject && rotatingObjectId === selectedObjectId) {
            setIsRotatingObject(false);
            setRotatingObjectId(null);
            setRotationStartAngle(0);
            setInitialObjectRotation(0);
          }

          captureStateBeforeChange();
          setFloorPlanData((prevData) => {
            const updatedData = {
              ...prevData,
              objects: prevData.objects
                ? prevData.objects.filter((obj) => obj.id !== selectedObjectId)
                : [],
            };
            setTimeout(() => {
              updateContextFloorPlanData(updatedData);
            }, 0);
            return updatedData;
          });
          setSelectedObjectId(null);
          setHasChanges(true);
        } else if (selectedDoorId) {
          captureStateBeforeChange();
          setFloorPlanData((prevData) => {
            const updatedData = {
              ...prevData,
              doors: prevData.doors
                ? prevData.doors.filter((door) => door.id !== selectedDoorId)
                : [],
            };
            setTimeout(() => {
              updateContextFloorPlanData(updatedData);
            }, 0);
            return updatedData;
          });

          setDoorWidths((prev) => {
            const newWidths = { ...prev };
            delete newWidths[selectedDoorId];
            return newWidths;
          });

          setSelectedDoorId(null);
          setHasChanges(true);
        } else if (selectedWindowId) {
          captureStateBeforeChange();
          setFloorPlanData((prevData) => {
            const updatedData = {
              ...prevData,
              windows: prevData.windows
                ? prevData.windows.filter(
                  (window) => window.id !== selectedWindowId
                )
                : [],
            };
            setTimeout(() => {
              updateContextFloorPlanData(updatedData);
            }, 0);
            return updatedData;
          });

          setWindowWidths((prev) => {
            const newWidths = { ...prev };
            delete newWidths[selectedWindowId];
            return newWidths;
          });

          setSelectedWindowId(null);
          setHasChanges(true);
        } else if (selectedDimensionId) {
          captureStateBeforeChange();
          setFloorPlanData((prevData) => {
            const updatedData = {
              ...prevData,
              dimensionLines: prevData.dimensionLines
                ? prevData.dimensionLines.filter(
                  (dim) => dim.id !== selectedDimensionId
                )
                : [],
            };
            setTimeout(() => {
              updateContextFloorPlanData(updatedData);
            }, 0);
            return updatedData;
          });
          setSelectedDimensionId(null);
          setHasChanges(true);
        } else if (selectedRoomIds.length > 0) {
          captureStateBeforeChange();

          const wallSegmentIds = selectedRoomIds.filter((id) =>
            id.includes("-wall-")
          );
          const roomIds = selectedRoomIds.filter(
            (id) => !id.includes("-wall-") && !floorPlanData.rooms.find(r => r.id === id)?.isBoundary
          );

          setFloorPlanData((prevData) => {
            let updatedRooms = [...prevData.rooms];

            if (wallSegmentIds.length > 0) {
              wallSegmentIds.forEach((wallSegmentId) => {
                const parts = wallSegmentId.split("-wall-");
                if (parts.length === 2) {
                  const roomId = parts[0];
                  const segmentIndex = parseInt(parts[1]);

                  const roomIndex = updatedRooms.findIndex(
                    (room) => room.id === roomId
                  );
                  if (roomIndex !== -1) {
                    const room = updatedRooms[roomIndex];

                    if (room.room_type !== "Wall") {
                      const roomPolygon = room.floor_polygon;
                      const newWallRooms = [];

                      for (let i = 0; i < roomPolygon.length; i++) {
                        if (i === segmentIndex) continue;

                        const startPoint = roomPolygon[i];
                        const endPoint =
                          roomPolygon[(i + 1) % roomPolygon.length];
                        const wallRoom = {
                          id: generateUniqueId("wall"),
                          room_type: "Wall",
                          area: 0,
                          height: 0,
                          width: 6,
                          floor_polygon: [startPoint, endPoint],
                        };

                        newWallRooms.push(wallRoom);
                      }
                      updatedRooms.splice(roomIndex, 1);
                      updatedRooms.push(...newWallRooms);
                    }
                  }
                }
              });
            }

            if (roomIds.length > 0) {
              updatedRooms = updatedRooms.filter(
                (room) => !roomIds.includes(room.id)
              );
            }
            const tempData = {
              ...prevData,
              rooms: updatedRooms,
            };

            const updatedData = ensureCoordinateSystem(tempData);

            setTimeout(() => {
              updateContextFloorPlanData(updatedData);
            }, 0);
            return updatedData;
          });

          setWallWidths((prev) => {
            const newWallWidths = { ...prev };
            wallSegmentIds.forEach((wallSegmentId) => {
              delete newWallWidths[wallSegmentId];
            });
            return newWallWidths;
          });

          setSelectedRoomIds([]);
          setSelectedRoomId(null);
          setHasChanges(true);
          checkAndUpdateOverlaps();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    selectedRoomIds,
    selectedLabelId,
    selectedObjectId,
    selectedDoorId,
    selectedWindowId,
    selectedDimensionId,
    isRotatingObject,
    rotatingObjectId,
    captureStateBeforeChange,
    setContextFloorPlanData,
    checkAndUpdateOverlaps,
    setDoorWidths,
    setWindowWidths,
    setWallWidths,
    wallWidths,
    generateUniqueId,
  ]);

  // Fixed bounds for coordinate transformation (prevents auto-centering)
  const bounds = useMemo(
    () => {
      return { minX: -5000, maxX: 5000, minZ: -5000, maxZ: 5000 };
    },
    []
  );

  // Content bounds based on actual room positions (for UI elements like measurements)
  const contentBounds = useMemo(
    () => {
      const visibleRooms = floorPlanData.rooms.filter(room =>
        room.room_type !== "Reference"
      );
      if (visibleRooms.length === 0) {
        return { minX: -100, maxX: 100, minZ: -100, maxZ: 100 };
      }
      return calculateBounds(visibleRooms);
    },
    [floorPlanData.rooms, roomRotations]
  );

  const tlength = contentBounds.maxZ - contentBounds.minZ;
  const twidth = contentBounds.maxX - contentBounds.minX;

  const padding = 1000;
  const contentWidth = bounds.maxX - bounds.minX + 2 * padding;
  const contentHeight = bounds.maxZ - bounds.minZ + 2 * padding;
  const isMobile = window.innerWidth < 850;

  const {
    transformCoordinates,
    reverseTransformCoordinates: originalReverseTransform,
  } = useCoordinateTransforms(bounds, padding, scale);

  const reverseTransformCoordinates = useCallback(
    (x: number, y: number) => {
      const correctedX = x / parentScale;
      const correctedY = y / parentScale;
      return originalReverseTransform(correctedX, correctedY);
    },
    [originalReverseTransform, parentScale]
  );

  useEffect(() => {
    const handleFloorPlanClick = (e: MouseEvent) => {
      const labelState = getLabelPlacementState();
      const doorState = getDoorPlacementState();
      const windowState = getWindowPlacementState();

      if (labelState.isPlacing && labelState.text) {
        const isObject = labelState.text.startsWith("/Objects/");

        if (isObject) {
          setIsObjectPlacementMode(true);
          if (svgRef.current) {
            const rect = svgRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const floorPlanCoords = reverseTransformCoordinates(x, y);

            if (!hasChanges) {
              captureOriginalState();
            }
            captureStateBeforeChange();
            addObject(labelState.text, floorPlanCoords);

            setHasChanges(true);
            setLabelPlacementState(false, null);
            setIsObjectPlacementMode(false);
            window.dispatchEvent(new CustomEvent("labelPlaced"));

            e.stopPropagation();
            e.preventDefault();
          }
        } else {
          setIsLabelPlacementMode(true);
          if (svgRef.current) {
            const rect = svgRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const floorPlanCoords = reverseTransformCoordinates(x, y);

            if (!hasChanges) {
              captureOriginalState();
            }
            captureStateBeforeChange();
            addLabel(labelState.text, floorPlanCoords);

            setHasChanges(true);
            setLabelPlacementState(false, null);
            setIsLabelPlacementMode(false);
            window.dispatchEvent(new CustomEvent("labelPlaced"));

            e.stopPropagation();
            e.preventDefault();
          }
        }
      }

      if (doorState.isPlacing && doorState.doorPath) {
        if (svgRef.current) {
          const rect = svgRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          const floorPlanCoords = reverseTransformCoordinates(x, y);

          const isValid = isValidPlacementPosition(
            floorPlanCoords,
            floorPlanData,
            roomRotations,
            10.0
          );

          if (!isValid) {
            showPlacementError("Doors must be placed on walls only.");
            e.stopPropagation();
            e.preventDefault();
            return;
          }

          const placementResult = getSnappedPlacementResult(
            floorPlanCoords,
            floorPlanData,
            roomRotations,
            3.0
          );

          if (!hasChanges) {
            captureOriginalState();
          }
          captureStateBeforeChange();

          const doorId = `door-${Date.now()}`;
          const newDoor = {
            id: doorId,
            doorPath: doorState.doorPath,
            position: placementResult.position,
            rotation: placementResult.rotation,
            scale: 1,
            width: 1.5,
            flipHorizontal: false,
            flipVertical: false,
          };

          setFloorPlanData((prevData) => {
            const doors = prevData.doors || [];
            setDoorWidths((prev) => ({ ...prev, [doorId]: 1.5 }));

            const newData = {
              ...prevData,
              doors: [...doors, newDoor],
            };

            setTimeout(() => {
              updateContextFloorPlanData(newData);
            }, 0);

            return newData;
          });

          setHasChanges(true);
          setDoorPlacementState(false, null);
          window.dispatchEvent(new CustomEvent("doorPlaced"));

          e.stopPropagation();
          e.preventDefault();
        }
      }

      if (windowState.isPlacing && windowState.windowPath) {
        if (svgRef.current) {
          const rect = svgRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          const floorPlanCoords = reverseTransformCoordinates(x, y);

          const isValid = isValidPlacementPosition(
            floorPlanCoords,
            floorPlanData,
            roomRotations,
            10.0
          );

          if (!isValid) {
            showPlacementError("Windows must be placed on walls only.");
            e.stopPropagation();
            e.preventDefault();
            return;
          }

          const placementResult = getSnappedPlacementResult(
            floorPlanCoords,
            floorPlanData,
            roomRotations,
            3.0
          );

          if (!hasChanges) {
            captureOriginalState();
          }
          captureStateBeforeChange();

          const windowId = `window-${Date.now()}`;
          const newWindow = {
            id: windowId,
            windowPath: windowState.windowPath,
            position: placementResult.position,
            rotation: placementResult.rotation,
            scale: 1,
            width: 2,
            flipHorizontal: false,
            flipVertical: false,
          };

          setFloorPlanData((prevData) => {
            const windows = prevData.windows || [];
            setWindowWidths((prev) => ({ ...prev, [windowId]: 1 }));

            const newData = {
              ...prevData,
              windows: [...windows, newWindow],
            };

            setTimeout(() => {
              updateContextFloorPlanData(newData);
            }, 0);

            return newData;
          });

          setHasChanges(true);
          setWindowPlacementState(false, null);
          window.dispatchEvent(new CustomEvent("windowPlaced"));

          e.stopPropagation();
          e.preventDefault();
        }
      }
    };

    const handleFloorPlanDragOver = (e: DragEvent) => {
      if (
        e.dataTransfer?.types.includes("application/x-floor-plan-object") ||
        e.dataTransfer?.types.includes("application/x-floor-plan-door") ||
        e.dataTransfer?.types.includes("application/x-floor-plan-window")
      ) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const handleFloorPlanDrop = (e: DragEvent) => {
      const objectPath = e.dataTransfer?.getData(
        "application/x-floor-plan-object"
      );
      if (objectPath && svgRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        addObject(objectPath, floorPlanCoords);
        setHasChanges(true);

        window.dispatchEvent(new CustomEvent("labelPlaced"));
        return;
      }

      const doorPath = e.dataTransfer?.getData("application/x-floor-plan-door");
      if (doorPath && svgRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        const isValid = isValidPlacementPosition(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!isValid) {
          showPlacementError("Doors must be placed on walls only.");
          return;
        }

        const placementResult = getSnappedPlacementResult(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        const doorId = `door-${Date.now()}`;
        const newDoor = {
          id: doorId,
          doorPath: doorPath,
          position: placementResult.position,
          rotation: placementResult.rotation,
          scale: 1,
          width: 1.5,
          flipHorizontal: false,
          flipVertical: false,
        };

        setFloorPlanData((prevData) => {
          const doors = prevData.doors || [];
          setDoorWidths((prev) => ({ ...prev, [doorId]: 1.5 }));

          const newData = {
            ...prevData,
            doors: [...doors, newDoor],
          };

          setTimeout(() => {
            updateContextFloorPlanData(newData);
          }, 0);

          return newData;
        });

        setHasChanges(true);
        window.dispatchEvent(new CustomEvent("doorPlaced"));
        return;
      }

      const windowPath = e.dataTransfer?.getData(
        "application/x-floor-plan-window"
      );
      if (windowPath && svgRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);
        const isValid = isValidPlacementPosition(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!isValid) {
          showPlacementError("Windows must be placed on walls only.");
          return;
        }

        const placementResult = getSnappedPlacementResult(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        const windowId = `window-${Date.now()}`;
        const newWindow = {
          id: windowId,
          windowPath: windowPath,
          position: placementResult.position,
          rotation: placementResult.rotation,
          scale: 1,
          width: 2,
          flipHorizontal: false,
          flipVertical: false,
        };

        setFloorPlanData((prevData) => {
          const windows = prevData.windows || [];
          setWindowWidths((prev) => ({ ...prev, [windowId]: 2 }));

          const newData = {
            ...prevData,
            windows: [...windows, newWindow],
          };

          setTimeout(() => {
            updateContextFloorPlanData(newData);
          }, 0);

          return newData;
        });

        setHasChanges(true);
        window.dispatchEvent(new CustomEvent("windowPlaced"));
        return;
      }
    };

    const showPlacementError = (message: string) => {
      const errorDiv = document.createElement("div");
      errorDiv.textContent = message;
      errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #f44336;
      color: white;
      padding: 10px 20px;
      border-radius: 6px;
      font-weight: 500;
      font-size: 14px;
      z-index: 10000;
      box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
      border: 1px solid #d32f2f;
      min-width: 350px;
      white-space: nowrap;
      text-align: center;
      animation: slideDown 0.3s ease-out;
    `;

      if (!document.getElementById("error-animation-styles")) {
        const styleSheet = document.createElement("style");
        styleSheet.id = "error-animation-styles";
        styleSheet.textContent = `
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `;
        document.head.appendChild(styleSheet);
      }

      document.body.appendChild(errorDiv);

      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.style.animation = "slideDown 0.3s ease-out reverse";
          setTimeout(() => {
            if (errorDiv.parentNode) {
              errorDiv.parentNode.removeChild(errorDiv);
            }
          }, 300);
        }
      }, 2000);
    };

    const handleTouchObjectDrop = (e: CustomEvent) => {
      const { objectPath, clientX, clientY } = e.detail;

      if (objectPath && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        addObject(objectPath, floorPlanCoords);
        setHasChanges(true);

        window.dispatchEvent(new CustomEvent("labelPlaced"));
      }
    };

    const handleTouchDoorDrop = (e: CustomEvent) => {
      const { itemPath, clientX, clientY } = e.detail;

      if (itemPath && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        const isValid = isValidPlacementPosition(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!isValid) {
          showPlacementError("Doors must be placed on walls only.");
          return;
        }

        const placementResult = getSnappedPlacementResult(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        const doorId = `door-${Date.now()}`;
        const newDoor = {
          id: doorId,
          doorPath: itemPath,
          position: placementResult.position,
          rotation: placementResult.rotation,
          scale: 1,
          width: 1.5,
          flipHorizontal: false,
          flipVertical: false,
        };

        setFloorPlanData((prevData) => {
          const doors = prevData.doors || [];
          setDoorWidths((prev) => ({ ...prev, [doorId]: 1.5 }));

          const newData = {
            ...prevData,
            doors: [...doors, newDoor],
          };

          setTimeout(() => {
            updateContextFloorPlanData(newData);
          }, 0);

          return newData;
        });

        setHasChanges(true);
        window.dispatchEvent(new CustomEvent("doorPlaced"));
      }
    };

    const handleTouchWindowDrop = (e: CustomEvent) => {
      const { itemPath, clientX, clientY } = e.detail;

      if (itemPath && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        const isValid = isValidPlacementPosition(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!isValid) {
          showPlacementError("Windows must be placed on walls only.");
          return;
        }

        const placementResult = getSnappedPlacementResult(
          floorPlanCoords,
          floorPlanData,
          roomRotations,
          20.0
        );

        if (!hasChanges) {
          captureOriginalState();
        }
        captureStateBeforeChange();

        const windowId = `window-${Date.now()}`;
        const newWindow = {
          id: windowId,
          windowPath: itemPath,
          position: placementResult.position,
          rotation: placementResult.rotation,
          scale: 1,
          width: 2,
          flipHorizontal: false,
          flipVertical: false,
        };

        setFloorPlanData((prevData) => {
          const windows = prevData.windows || [];
          setWindowWidths((prev) => ({ ...prev, [windowId]: 2 }));

          const newData = {
            ...prevData,
            windows: [...windows, newWindow],
          };

          setTimeout(() => {
            updateContextFloorPlanData(newData);
          }, 0);

          return newData;
        });

        setHasChanges(true);
        window.dispatchEvent(new CustomEvent("windowPlaced"));
      }
    };

    document.addEventListener("click", handleFloorPlanClick, { capture: true });
    document.addEventListener("dragover", handleFloorPlanDragOver, {
      capture: true,
    });
    document.addEventListener("drop", handleFloorPlanDrop, { capture: true });
    document.addEventListener(
      "touchObjectDrop",
      handleTouchObjectDrop as EventListener
    );
    document.addEventListener(
      "touchDoorDrop",
      handleTouchDoorDrop as EventListener
    );
    document.addEventListener(
      "touchWindowDrop",
      handleTouchWindowDrop as EventListener
    );

    return () => {
      document.removeEventListener("click", handleFloorPlanClick, {
        capture: true,
      });
      document.removeEventListener("dragover", handleFloorPlanDragOver, {
        capture: true,
      });
      document.removeEventListener("drop", handleFloorPlanDrop, {
        capture: true,
      });
      document.removeEventListener(
        "touchObjectDrop",
        handleTouchObjectDrop as EventListener
      );
      document.removeEventListener(
        "touchDoorDrop",
        handleTouchDoorDrop as EventListener
      );
      document.removeEventListener(
        "touchWindowDrop",
        handleTouchWindowDrop as EventListener
      );
    };
  }, [
    reverseTransformCoordinates,
    addLabel,
    addObject,
    addDoor,
    addWindow,
    setHasChanges,
    floorPlanData,
    roomRotations,
    hasChanges,
    captureOriginalState,
    captureStateBeforeChange,
    setDoorWidths,
    setWindowWidths,
    setContextFloorPlanData,
  ]);

  useEffect(() => {
    const handleCornerPlacement = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest(".back-button")) {
        return;
      }

      const cornerState = getCornerPlacementState();

      if (cornerState.isPlacing) {
        e.stopPropagation();
        e.preventDefault();

        if (svgRef.current) {
          const rect = svgRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          const floorPlanCoords = reverseTransformCoordinates(x, y);

          const clickedRoom = findRoomAtPosition(
            floorPlanCoords,
            floorPlanData,
            roomRotations
          );

          if (clickedRoom) {
            const success = addCornerToRoom(
              clickedRoom.id,
              floorPlanCoords,
              floorPlanData,
              setFloorPlanData,
              setHasChanges,
              captureStateBeforeChange,
              hasChanges,
              captureOriginalState,
              calculateRoomArea,
              calculateRoomDimensions
            );

            if (success) {
              const successDiv = document.createElement("div");
              successDiv.textContent = "Corner added successfully!";
              successDiv.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #4CAF50;
    color: white;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    font-size: 14px;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  `;

              document.body.appendChild(successDiv);

              setTimeout(() => {
                if (successDiv.parentNode) {
                  successDiv.parentNode.removeChild(successDiv);
                }
              }, 2000);

              setSelectedRoomIds([clickedRoom.id]);

              checkAndUpdateOverlaps();

                setTimeout(() => {
                  setFloorPlanData((currentData) => {
                    updateContextFloorPlanData(currentData);
                    return currentData;
                  });
                }, 50);
              setCornerPlacementState(false);

              setTimeout(() => {
                const backButton = document.querySelector(
                  ".back-button"
                ) as HTMLButtonElement;
                if (backButton) {
                  backButton.click();
                }
              }, 100);
            }
          } else {
            const errorDiv = document.createElement("div");
            errorDiv.textContent =
              "Cannot add corner here. Click on a room wall to add a corner";
            errorDiv.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #f44336;
    color: white;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    font-size: 14px;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
  `;

            document.body.appendChild(errorDiv);

            setTimeout(() => {
              if (errorDiv.parentNode) {
                errorDiv.parentNode.removeChild(errorDiv);
              }
            }, 2000);
          }
        }
      }
    };

    document.addEventListener("click", handleCornerPlacement, {
      capture: true,
    });

    return () => {
      document.removeEventListener("click", handleCornerPlacement, {
        capture: true,
      });
    };
  }, [
    reverseTransformCoordinates,
    floorPlanData,
    roomRotations,
    setFloorPlanData,
    setHasChanges,
    captureStateBeforeChange,
    hasChanges,
    captureOriginalState,
    checkAndUpdateOverlaps,
    setContextFloorPlanData,
  ]);

  useEffect(() => {
    if (activeBuildTool !== "drawCorner") {
      setCornerPlacementState(false);
    }
  }, [activeBuildTool]);

  useEffect(() => {
    const handleDimensionClick = (e: CustomEvent) => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.detail.clientX - rect.left;
        const y = e.detail.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        if (e.detail.pointsCount === 0) {
          window.dimensionFirstPoint = {
            x: floorPlanCoords.x,
            z: floorPlanCoords.z,
          };
        } else if (window.dimensionFirstPoint) {
          const firstPoint = window.dimensionFirstPoint;
          const secondPoint = { x: floorPlanCoords.x, z: floorPlanCoords.z };

          if (!hasChanges) {
            captureOriginalState();
          }
          captureStateBeforeChange();

          addDimensionLine(firstPoint, secondPoint);
          setHasChanges(true);

          delete window.dimensionFirstPoint;
        }
      }
    };

    window.addEventListener(
      "dimensionClick",
      handleDimensionClick as EventListener
    );
    return () => {
      window.removeEventListener(
        "dimensionClick",
        handleDimensionClick as EventListener
      );
    };
  }, [
    reverseTransformCoordinates,
    addDimensionLine,
    setHasChanges,
    captureStateBeforeChange,
    captureOriginalState,
    hasChanges,
  ]);

  const renderPreviewDimensionLine = () => {
    if (!isDimensionDrawing || !dimensionStartPoint || !dimensionCurrentPoint) {
      return null;
    }

    const startPos = transformCoordinates(dimensionStartPoint);
    const endPos = transformCoordinates(dimensionCurrentPoint);

    const distance = Math.sqrt(
      Math.pow(dimensionCurrentPoint.x - dimensionStartPoint.x, 2) +
      Math.pow(dimensionCurrentPoint.z - dimensionStartPoint.z, 2)
    );

    const dx = endPos.x - startPos.x;
    const dy = endPos.y - startPos.y;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

    const offsetMidX = (startPos.x + endPos.x) / 2;
    const offsetMidY = (startPos.y + endPos.y) / 2;

    return (
      <g className="dimension-preview" style={{ opacity: 0.7 }}>
        <line
          x1={startPos.x}
          y1={startPos.y}
          x2={endPos.x}
          y2={endPos.y}
          stroke="#2196F3"
          strokeWidth="0.8"
          markerStart="url(#dimensionArrowStart)"
          markerEnd="url(#dimensionArrowEnd)"
        />

        <text
          x={offsetMidX}
          y={
            offsetMidY +
            (Math.abs(angle) < 45 || Math.abs(angle) > 135 ? 15 : -10)
          }
          fill="#2196F3"
          fontSize="9"
          fontWeight="500"
          textAnchor="middle"
          transform={`rotate(${angle > 90 || angle < -90 ? angle + 180 : angle
            }, ${offsetMidX}, ${offsetMidY - 8})`}
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          {formatImperialLength(coordToInches(distance / 10), unitSystem)}
        </text>
      </g>
    );
  };

  const renderPreviewWall = () => {
    if (!isWallDrawing || !wallStartPoint || !wallCurrentPoint) {
      return null;
    }

    const startPos = transformCoordinates(wallStartPoint);
    const endPos = transformCoordinates(wallCurrentPoint);

    const distance = Math.sqrt(
      Math.pow(wallCurrentPoint.x - wallStartPoint.x, 2) +
      Math.pow(wallCurrentPoint.z - wallStartPoint.z, 2)
    );

    // Only show preview if distance is meaningful
    if (distance < 0.1) {
      return null;
    }

    // Check if start point is snapped to an existing wall node
    const SNAP_DISTANCE = 15;
    const existingWalls = floorPlanData.rooms.filter(
      room => room.room_type === "Wall" && room.floor_polygon.length === 2
    );
    
    let isSnappedToNode = false;
    for (const wall of existingWalls) {
      for (const point of wall.floor_polygon) {
        const dist = Math.sqrt(
          Math.pow(point.x - wallStartPoint.x, 2) +
          Math.pow(point.z - wallStartPoint.z, 2)
        );
        if (dist < 0.1) { // Very close match means it's snapped
          isSnappedToNode = true;
          break;
        }
      }
      if (isSnappedToNode) break;
    }

    // Check if we're in fixed-length mode
    const isFixedLength = predefinedWallLength !== null && predefinedWallLength > 0;

    // Calculate perpendicular offset for wall width visualization
    let dx = wallCurrentPoint.x - wallStartPoint.x;
    const dz = wallCurrentPoint.z - wallStartPoint.z;
    const length = Math.sqrt(dx * dx + dz * dz);
    const perpX = (-dz / length) * (drawingWallWidth / 2);
    const perpZ = (dx / length) * (drawingWallWidth / 2);

    const wallPolygon = [
      { x: wallStartPoint.x + perpX, z: wallStartPoint.z + perpZ },
      { x: wallCurrentPoint.x + perpX, z: wallCurrentPoint.z + perpZ },
      { x: wallCurrentPoint.x - perpX, z: wallCurrentPoint.z - perpZ },
      { x: wallStartPoint.x - perpX, z: wallStartPoint.z - perpZ },
    ];

    const transformedPolygon = wallPolygon.map(transformCoordinates);
    const polygonPoints = transformedPolygon
      .map((p) => `${p.x},${p.y}`)
      .join(" ");

    const midX = (startPos.x + endPos.x) / 2;
    const midY = (startPos.y + endPos.y) / 2;

    // Calculate angle from horizontal in screen coordinates
    // In SVG: positive X is right, positive Y is down
    // We want angle measured from horizontal (positive X-axis) counterclockwise
    dx = endPos.x - startPos.x;
    const dy = endPos.y - startPos.y;
    
    let angleRadians = Math.atan2(-dy, dx); // Negate dy because SVG Y increases downward
    let angleDegrees = (angleRadians * 180) / Math.PI;
    
    // Normalize to 0-180 range (always show the acute/obtuse angle, not reflex)
    if (angleDegrees < 0) {
      angleDegrees = -angleDegrees; // Make positive
    }
    if (angleDegrees > 180) {
      angleDegrees = 360 - angleDegrees;
    }

    // For displaying the arc, we need the actual angle for drawing
    const displayAngleRadians = Math.atan2(-dy, dx);

    return (
      <g className="wall-preview" style={{ opacity: 1 }}>
        <polygon
          points={polygonPoints}
          fill="black"
          stroke="#ff6b00"
          strokeWidth={1}
          strokeDasharray={isFixedLength ? "10,5" : "8,4"}
        />

        {/* Start point marker */}
        <circle 
          cx={startPos.x} 
          cy={startPos.y} 
          r={isFixedLength ? 6 : 4}
          fill={isSnappedToNode ? "#4CAF50" : "white"}
          stroke={isSnappedToNode ? "#2E7D32" : "black"}
          strokeWidth={isSnappedToNode ? 2 : 1}
        />
        
        {/* Indicator ring for snapped start point */}
        {isSnappedToNode && (
          <circle 
            cx={startPos.x} 
            cy={startPos.y} 
            r={8}
            fill="none"
            stroke="#4CAF50"
            strokeWidth={1.5}
            strokeDasharray="2,2"
          />
        )}

        {/* End point marker */}
        {(() => {
          // Check if endpoint is snapped to an existing wall node
          const SNAP_CHECK_DISTANCE = 0.1;
          let isEndSnappedToNode = false;
          
          for (const wall of existingWalls) {
            for (const point of wall.floor_polygon) {
              const dist = Math.sqrt(
                Math.pow(point.x - wallCurrentPoint.x, 2) +
                Math.pow(point.z - wallCurrentPoint.z, 2)
              );
              if (dist < SNAP_CHECK_DISTANCE) {
                isEndSnappedToNode = true;
                break;
              }
            }
            if (isEndSnappedToNode) break;
          }
          
          return (
            <>
              <circle 
                cx={endPos.x} 
                cy={endPos.y} 
                r={isFixedLength ? 6 : 4}
                fill={isEndSnappedToNode ? "#4CAF50" : "white"}
                stroke={isEndSnappedToNode ? "#2E7D32" : "black"}
                strokeWidth={isEndSnappedToNode ? 2 : 1}
              />
              
              {/* Indicator ring for snapped endpoint */}
              {isEndSnappedToNode && (
                <circle 
                  cx={endPos.x} 
                  cy={endPos.y} 
                  r={8}
                  fill="none"
                  stroke="#4CAF50"
                  strokeWidth={1.5}
                  strokeDasharray="2,2"
                />
              )}
            </>
          );
        })()}

        {/* Wall measurement */}
        <rect
          x={midX - 30}
          y={midY - 10}
          width={60}
          height={20}
          fill="rgba(255, 255, 255, 0.9)"
          stroke="#ff6b00"
          strokeWidth="0.5"
          rx={3}
        />
        <text
          x={midX}
          y={midY + 4}
          fill="#ff6b00"
          fontSize="10"
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          {formatImperialLength(coordToInches(distance / 10), unitSystem)}
        </text>
        
        {/* Show temp length input if user is typing */}

        {/* Horizontal reference line (dotted) */}
        <line
          x1={startPos.x}
          y1={startPos.y}
          x2={startPos.x + 40}
          y2={startPos.y}
          stroke="#4CAF50"
          strokeWidth="1"
          strokeDasharray="3,3"
          strokeOpacity="0.6"
        />

        {/* Arc showing angle from horizontal */}
        <path
          d={(() => {
            const arcRadius = 20;
            const startArcX = startPos.x + arcRadius;
            const startArcY = startPos.y;
            const endArcX = startPos.x + arcRadius * Math.cos(displayAngleRadians);
            const endArcY = startPos.y - arcRadius * Math.sin(displayAngleRadians);
            
            // Determine sweep direction based on angle
            const sweepFlag = displayAngleRadians >= 0 ? 0 : 1;
            
            return `M ${startArcX} ${startArcY} A ${arcRadius} ${arcRadius} 0 0 ${sweepFlag} ${endArcX} ${endArcY}`;
          })()}
          fill="none"
          stroke="#4CAF50"
          strokeWidth="1.5"
          strokeOpacity="0.7"
        />

        {/* Angle text - positioned outside the arc */}
        <text
          x={startPos.x + 30}
          y={startPos.y - (dy < 0 ? 15 : -15)}
          fill="#4CAF50"
          fontSize="11"
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            textShadow: "0 0 3px white, 0 0 3px white, 0 0 3px white"
          }}
        >
          {angleDegrees.toFixed(1)}°
        </text>
      </g>
    );
  };

  const renderAlignmentGuides = () => {
    const isDrawing = isWallDrawing;
    const isMoving = dragState.active && dragState.roomId && !dragState.isResizing && !dragState.isEdgeResizing;
    const isResizing = dragState.active && dragState.roomId && dragState.isResizing && dragState.vertexIndex !== null;
    
    if ((!isDrawing && !isMoving && !isResizing) || (!alignmentGuides.horizontal && !alignmentGuides.vertical)) {
      return null;
    }

    const guides = [];
    
    // Horizontal alignment guide (constant Z - horizontal line in 2D view)
    if (alignmentGuides.horizontal !== null) {
      const y = transformCoordinates({ x: 0, z: alignmentGuides.horizontal }).y;
      guides.push(
        <line
          key="horizontal-guide"
          x1={0}
          y1={y}
          x2={contentWidth * scale}
          y2={y}
          stroke="#00D9FF"
          strokeWidth={1.5}
          strokeDasharray="8,4"
          opacity={0.7}
          pointerEvents="none"
        />
      );
    }
    
    // Vertical alignment guide (constant X - vertical line in 2D view)
    if (alignmentGuides.vertical !== null) {
      const x = transformCoordinates({ x: alignmentGuides.vertical, z: 0 }).x;
      guides.push(
        <line
          key="vertical-guide"
          x1={x}
          y1={0}
          x2={x}
          y2={contentHeight * scale}
          stroke="#00D9FF"
          strokeWidth={1.5}
          strokeDasharray="8,4"
          opacity={0.7}
          pointerEvents="none"
        />
      );
    }
    
    return <g className="alignment-guides">{guides}</g>;
  };

  const renderWallAngle = () => {
    // Check if we're dragging or resizing a wall
    const isDraggingWall = dragState.active && 
                          dragState.roomId && 
                          !dragState.isResizing && 
                          !dragState.isEdgeResizing;
    
    const isResizingWall = dragState.active && 
                           dragState.roomId && 
                           dragState.isResizing &&
                           dragState.vertexIndex !== null;
    
    if (!isDraggingWall && !isResizingWall) {
      return null;
    }

    const room = floorPlanData.rooms.find(r => r.id === dragState.roomId);
    if (!room || room.room_type !== "Wall" || room.floor_polygon.length !== 2) {
      return null;
    }

    // Calculate current wall position in screen coordinates
    const startPos = transformCoordinates(room.floor_polygon[0]);
    const endPos = transformCoordinates(room.floor_polygon[1]);

    // Calculate angle from horizontal in screen coordinates
    const dx = endPos.x - startPos.x;
    const dy = endPos.y - startPos.y;
    
    let angleRadians = Math.atan2(-dy, dx); // Negate dy because SVG Y increases downward
    let angleDegrees = (angleRadians * 180) / Math.PI;
    
    // Normalize to 0-180 range
    if (angleDegrees < 0) {
      angleDegrees = -angleDegrees;
    }
    if (angleDegrees > 180) {
      angleDegrees = 360 - angleDegrees;
    }

    // Position for the angle display - near the start point
    const displayAngleRadians = Math.atan2(-dy, dx);

    return (
      <g className="wall-angle-indicator">
        {/* Horizontal reference line (dotted) */}
        <line
          x1={startPos.x}
          y1={startPos.y}
          x2={startPos.x + 40}
          y2={startPos.y}
          stroke="#4CAF50"
          strokeWidth="1"
          strokeDasharray="3,3"
          strokeOpacity="0.6"
        />

        {/* Arc showing angle from horizontal */}
        <path
          d={(() => {
            const arcRadius = 20;
            const startArcX = startPos.x + arcRadius;
            const startArcY = startPos.y;
            const endArcX = startPos.x + arcRadius * Math.cos(displayAngleRadians);
            const endArcY = startPos.y - arcRadius * Math.sin(displayAngleRadians);
            
            const sweepFlag = displayAngleRadians >= 0 ? 0 : 1;
            
            return `M ${startArcX} ${startArcY} A ${arcRadius} ${arcRadius} 0 0 ${sweepFlag} ${endArcX} ${endArcY}`;
          })()}
          fill="none"
          stroke="#4CAF50"
          strokeWidth="1.5"
          strokeOpacity="0.7"
        />

        {/* Angle text */}
        <text
          x={startPos.x + 30}
          y={startPos.y - (dy < 0 ? 15 : -15)}
          fill="#4CAF50"
          fontSize="10"
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            pointerEvents: "none",
            textShadow: "0 0 3px white, 0 0 3px white, 0 0 3px white"
          }}
        >
          {angleDegrees.toFixed(1)}°
        </text>
      </g>
    );
  };

  const calculateAngle = (
    centerX: number,
    centerY: number,
    mouseX: number,
    mouseY: number
  ) => {
    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;
    return Math.atan2(deltaY, deltaX) * (180 / Math.PI);
  };

  const handleRotationStart = useCallback(
    (
      e: React.MouseEvent,
      objectId: string,
      centerX: number,
      centerY: number
    ) => {
      e.stopPropagation();
      e.preventDefault();

      if (!hasChanges) {
        captureOriginalState();
      }
      captureStateBeforeChange();

      const svgElement = svgRef.current;
      if (!svgElement) return;

      const rect = svgElement.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left) / parentScale;
      const mouseY = (e.clientY - rect.top) / parentScale;

      const startAngle = calculateAngle(centerX, centerY, mouseX, mouseY);
      const currentRotation = objectRotations[objectId] || 0;

      setIsRotatingObject(true);
      setRotatingObjectId(objectId);
      setRotationStartAngle(startAngle);
      setInitialObjectRotation(currentRotation);
      setHasChanges(true);
    },
    [
      hasChanges,
      captureOriginalState,
      captureStateBeforeChange,
      objectRotations,
      svgRef,
      parentScale,
    ]
  );

  const handleRotationTouchStart = useCallback(
    (
      e: React.TouchEvent,
      objectId: string,
      centerX: number,
      centerY: number
    ) => {
      e.stopPropagation();
      e.preventDefault();

      if (!hasChanges) {
        captureOriginalState();
      }
      captureStateBeforeChange();

      if (e.touches.length !== 1) return;

      const svgElement = svgRef.current;
      if (!svgElement) return;

      const rect = svgElement.getBoundingClientRect();
      const touch = e.touches[0];
      const touchX = (touch.clientX - rect.left) / parentScale;
      const touchY = (touch.clientY - rect.top) / parentScale;

      const startAngle = calculateAngle(centerX, centerY, touchX, touchY);
      const currentRotation = objectRotations[objectId] || 0;

      setIsRotatingObject(true);
      setRotatingObjectId(objectId);
      setRotationStartAngle(startAngle);
      setInitialObjectRotation(currentRotation);
      setHasChanges(true);

      document.body.setAttribute("data-room-touch-interaction", "true");
    },
    [
      hasChanges,
      captureOriginalState,
      captureStateBeforeChange,
      objectRotations,
      svgRef,
      parentScale,
    ]
  );

  const renderWallHighlights = () => {
    const doorState = getDoorPlacementState();
    const windowState = getWindowPlacementState();

    if (!doorState.isPlacing && !windowState.isPlacing) {
      return null;
    }

    const wallSegments = getAllWallSegments(floorPlanData, roomRotations);

    return (
      <g className="wall-highlights">
        {wallSegments.map((wall, index) => {
          const startPos = transformCoordinates(wall.start);
          const endPos = transformCoordinates(wall.end);

          return (
            <line
              key={`wall-highlight-${index}`}
              x1={startPos.x}
              y1={startPos.y}
              x2={endPos.x}
              y2={endPos.y}
              stroke="#4CAF50"
              strokeWidth="3"
              strokeOpacity="0.6"
              strokeDasharray="5,5"
              pointerEvents="none"
              style={{
                animation: "wall-pulse 2s infinite",
              }}
            />
          );
        })}
        <style>{`
        @keyframes wall-pulse {
          0%, 100% { stroke-opacity: 0.3; }
          50% { stroke-opacity: 0.8; }
        }
      `}</style>
      </g>
    );
  };

  useEffect(() => {
    const handleRotationMove = (e: MouseEvent) => {
      if (!isRotatingObject || !rotatingObjectId) return;

      const object = floorPlanData.objects?.find(
        (obj) => obj.id === rotatingObjectId
      );
      if (!object) return;

      const position = transformCoordinates(object.position);

      const svgElement = svgRef.current;
      if (!svgElement) return;

      const rect = svgElement.getBoundingClientRect();

      const mouseX = (e.clientX - rect.left) / parentScale;
      const mouseY = (e.clientY - rect.top) / parentScale;

      const currentAngle = calculateAngle(
        position.x,
        position.y,
        mouseX,
        mouseY
      );

      let angleDiff = currentAngle - rotationStartAngle;

      if (angleDiff > 180) {
        angleDiff -= 360;
      } else if (angleDiff < -180) {
        angleDiff += 360;
      }

      const sensitivity = 3.0;
      const adjustedAngleDiff = angleDiff * sensitivity;

      let newRotation = initialObjectRotation + adjustedAngleDiff;

      while (newRotation < 0) newRotation += 360;
      while (newRotation >= 360) newRotation -= 360;

      setObjectRotations((prev) => ({
        ...prev,
        [rotatingObjectId]: newRotation,
      }));

      setFloorPlanData((prevData) => {
        const updatedObjects = (prevData.objects || []).map((obj) => {
          if (obj.id === rotatingObjectId) {
            return { ...obj, rotation: newRotation };
          }
          return obj;
        });

        const updatedData = {
          ...prevData,
          objects: updatedObjects,
        };

        return updatedData;
      });
    };

    const handleRotationTouchMove = (e: TouchEvent) => {
      if (!isRotatingObject || !rotatingObjectId) return;

      e.preventDefault();

      if (e.touches.length !== 1) return;

      const object = floorPlanData.objects?.find(
        (obj) => obj.id === rotatingObjectId
      );
      if (!object) return;

      const position = transformCoordinates(object.position);

      const svgElement = svgRef.current;
      if (!svgElement) return;

      const rect = svgElement.getBoundingClientRect();
      const touch = e.touches[0];

      const touchX = (touch.clientX - rect.left) / parentScale;
      const touchY = (touch.clientY - rect.top) / parentScale;

      const currentAngle = calculateAngle(
        position.x,
        position.y,
        touchX,
        touchY
      );

      let angleDiff = currentAngle - rotationStartAngle;

      if (angleDiff > 180) {
        angleDiff -= 360;
      } else if (angleDiff < -180) {
        angleDiff += 360;
      }

      const sensitivity = 3.0;
      const adjustedAngleDiff = angleDiff * sensitivity;

      let newRotation = initialObjectRotation + adjustedAngleDiff;

      while (newRotation < 0) newRotation += 360;
      while (newRotation >= 360) newRotation -= 360;

      setObjectRotations((prev) => ({
        ...prev,
        [rotatingObjectId]: newRotation,
      }));

      setFloorPlanData((prevData) => {
        const updatedObjects = (prevData.objects || []).map((obj) => {
          if (obj.id === rotatingObjectId) {
            return { ...obj, rotation: newRotation };
          }
          return obj;
        });

        const updatedData = {
          ...prevData,
          objects: updatedObjects,
        };

        return updatedData;
      });
    };

    const handleRotationEnd = () => {
      if (isRotatingObject && rotatingObjectId) {
        const object = floorPlanData.objects?.find(
          (obj) => obj.id === rotatingObjectId
        );
        if (object) {
          const finalRotation = objectRotations[rotatingObjectId] || 0;
          setFloorPlanData((prevData) => {
            const updatedObjects = (prevData.objects || []).map((obj) => {
              if (obj.id === rotatingObjectId) {
                return { ...obj, rotation: finalRotation };
              }
              return obj;
            });

            const updatedData = {
              ...prevData,
              objects: updatedObjects,
            };

            setTimeout(() => {
              updateContextFloorPlanData(updatedData);
            }, 0);

            return updatedData;
          });
        }
      }

      document.body.removeAttribute("data-room-touch-interaction");

      setIsRotatingObject(false);
      setRotatingObjectId(null);
      setRotationStartAngle(0);
      setInitialObjectRotation(0);
    };

    if (isRotatingObject) {
      document.addEventListener("mousemove", handleRotationMove);
      document.addEventListener("mouseup", handleRotationEnd);
      document.addEventListener("mouseleave", handleRotationEnd);
      document.addEventListener("touchmove", handleRotationTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleRotationEnd);
      document.addEventListener("touchcancel", handleRotationEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleRotationMove);
      document.removeEventListener("mouseup", handleRotationEnd);
      document.removeEventListener("mouseleave", handleRotationEnd);

      document.removeEventListener("touchmove", handleRotationTouchMove);
      document.removeEventListener("touchend", handleRotationEnd);
      document.removeEventListener("touchcancel", handleRotationEnd);
    };
  }, [
    isRotatingObject,
    rotatingObjectId,
    rotationStartAngle,
    initialObjectRotation,
    floorPlanData.objects,
    transformCoordinates,
    objectRotations,
    setContextFloorPlanData,
    svgRef,
    parentScale,
  ]);

  useEffect(() => {
    const handleDimensionMouseDown = (e: CustomEvent) => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.detail.clientX - rect.left;
        const y = e.detail.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        setIsDimensionDrawing(true);
        setDimensionStartPoint({ x: floorPlanCoords.x, z: floorPlanCoords.z });
        setDimensionCurrentPoint({
          x: floorPlanCoords.x,
          z: floorPlanCoords.z,
        });

        window.dispatchEvent(new CustomEvent("dimensionDragStart"));
      }
    };

    const handleDimensionMouseMove = (e: CustomEvent) => {
      if (isDimensionDrawing && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.detail.clientX - rect.left;
        const y = e.detail.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);
        setDimensionCurrentPoint({
          x: floorPlanCoords.x,
          z: floorPlanCoords.z,
        });
      }
    };

    const handleDimensionMouseUp = (e: CustomEvent) => {
      if (isDimensionDrawing && dimensionStartPoint && dimensionCurrentPoint) {
        const distance = Math.sqrt(
          Math.pow(dimensionCurrentPoint.x - dimensionStartPoint.x, 2) +
          Math.pow(dimensionCurrentPoint.z - dimensionStartPoint.z, 2)
        );

        if (distance > 0.1) {
          if (!hasChanges) {
            captureOriginalState();
          }
          captureStateBeforeChange();

          addDimensionLine(dimensionStartPoint, dimensionCurrentPoint);
          setHasChanges(true);
        }

        setIsDimensionDrawing(false);
        setDimensionStartPoint(null);
        setDimensionCurrentPoint(null);

        window.dispatchEvent(new CustomEvent("dimensionDragEnd"));
        window.dispatchEvent(new CustomEvent("resetDimensionTool"));
      }
    };

    window.addEventListener(
      "dimensionMouseDown",
      handleDimensionMouseDown as EventListener
    );
    window.addEventListener(
      "dimensionMouseMove",
      handleDimensionMouseMove as EventListener
    );
    window.addEventListener(
      "dimensionMouseUp",
      handleDimensionMouseUp as EventListener
    );

    return () => {
      window.removeEventListener(
        "dimensionMouseDown",
        handleDimensionMouseDown as EventListener
      );
      window.removeEventListener(
        "dimensionMouseMove",
        handleDimensionMouseMove as EventListener
      );
      window.removeEventListener(
        "dimensionMouseUp",
        handleDimensionMouseUp as EventListener
      );
    };
  }, [
    isDimensionDrawing,
    dimensionStartPoint,
    dimensionCurrentPoint,
    reverseTransformCoordinates,
    addDimensionLine,
    setHasChanges,
    captureStateBeforeChange,
    captureOriginalState,
    hasChanges,
  ]);

  useEffect(() => {
    const handleDragEnd = (e: DragEvent) => {
      setDragState({
        active: false,
        roomId: null,
        roomIds: [],
        vertexIndex: null,
        edgeIndices: null,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        isResizing: false,
        isEdgeResizing: false,
        isGroupOperation: false,
      });
      e.preventDefault();
      e.stopPropagation();

      document.body.style.pointerEvents = "none";
      setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 100);
    };

    document.addEventListener("dragend", handleDragEnd);
    document.addEventListener("drop", handleDragEnd);

    return () => {
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("drop", handleDragEnd);
    };
  }, []);

  const lastSyncedFloorPlanDataRef = useRef<FloorPlanData | null>(null);

  useEffect(() => {
    if (floorPlanData && !dragState.active) {
      // Only update context if data has actually changed AND it's different from what we last synced
      const currentString = JSON.stringify(floorPlanData);
      const contextString = JSON.stringify(contextFloorPlanData);
      const lastSyncedString = lastSyncedFloorPlanDataRef.current ? JSON.stringify(lastSyncedFloorPlanDataRef.current) : "";

      if (currentString !== contextString && currentString !== lastSyncedString) {
        lastSyncedFloorPlanDataRef.current = floorPlanData;
        updateContextFloorPlanData(floorPlanData);
      }
    }
  }, [floorPlanData, setContextFloorPlanData, dragState.active, contextFloorPlanData, updateContextFloorPlanData]);

  const isContextInitialized = useRef(false);
  const prevRoomTypesRef = useRef<string[]>([]);

  useEffect(() => {
    if (!contextFloorPlanData) return;
    const contextRoomTypes = contextFloorPlanData.rooms.map(
      (r) => `${r.id}-${r.room_type}`
    );

    const isInitialLoad = !isContextInitialized.current;
    const hasLabelChange =
      floorPlanData?.labels?.length !== contextFloorPlanData?.labels?.length;
    const hasObjectChange =
      floorPlanData?.objects?.length !== contextFloorPlanData?.objects?.length;
    const hasDoorChange =
      floorPlanData?.doors?.length !== contextFloorPlanData?.doors?.length;
    const hasWindowChange =
      floorPlanData?.windows?.length !== contextFloorPlanData?.windows?.length;
    const hasDimensionChange =
      floorPlanData?.dimensionLines?.length !==
      contextFloorPlanData?.dimensionLines?.length;
    const hasRoomTypeChange =
      JSON.stringify(contextRoomTypes) !==
      JSON.stringify(prevRoomTypesRef.current);

    if (
      isInitialLoad ||
      hasLabelChange ||
      hasObjectChange ||
      hasDoorChange ||
      hasWindowChange ||
      hasDimensionChange ||
      hasRoomTypeChange
    ) {
      // Check if this update is just echoing back what we just sent
      const contextString = JSON.stringify(contextFloorPlanData);
      const lastSyncedString = lastSyncedFloorPlanDataRef.current ? JSON.stringify(lastSyncedFloorPlanDataRef.current) : "";
      // Also compare against current local state - if local is NEWER (e.g. has more corners), don't overwrite
      const localString = JSON.stringify(floorPlanData);

      if (contextString !== lastSyncedString && contextString !== localString) {
        prevRoomTypesRef.current = contextRoomTypes;
        isContextInitialized.current = true;
        setFloorPlanData(contextFloorPlanData);
        lastSyncedFloorPlanDataRef.current = contextFloorPlanData;
      } else {
        // Still update the ref so we don't keep re-checking
        prevRoomTypesRef.current = contextRoomTypes;
        isContextInitialized.current = true;
      }
    }
  }, [contextFloorPlanData, floorPlanData]);

  useEffect(() => {
    if (contextSelectedRoomIds && contextSelectedRoomIds.length > 0) {
      setSelectedRoomIds(contextSelectedRoomIds);
    }
  }, [contextSelectedRoomIds]);


  useEffect(() => {
    // Only update context if the values are actually different
    if (JSON.stringify(contextSelectedRoomIds) !== JSON.stringify(selectedRoomIds)) {
      setContextSelectedRoomIds(selectedRoomIds);
    }
  }, [selectedRoomIds]);


  useEffect(() => {
    const handleFloorPlanReset = (event: CustomEvent) => {
      if (event.detail) {
        setFloorPlanData(event.detail);
        checkAndUpdateOverlaps();
      }
    };

    window.addEventListener(
      "floorPlanReset",
      handleFloorPlanReset as EventListener
    );

    return () => {
      window.removeEventListener(
        "floorPlanReset",
        handleFloorPlanReset as EventListener
      );
    };
  }, [checkAndUpdateOverlaps]);

  useEffect(() => {
    const labelState = getLabelPlacementState();
    setIsLabelPlacementMode(labelState.isPlacing);
  }, [getLabelPlacementState().isPlacing]);

  useEffect(() => {
    if (contextFloorPlanData && floorPlanData) {
      const contextLabelsLength = contextFloorPlanData.labels?.length || 0;
      const currentLabelsLength = floorPlanData.labels?.length || 0;
      const contextObjectsLength = contextFloorPlanData.objects?.length || 0;
      const currentObjectsLength = floorPlanData.objects?.length || 0;
      const contextDoorsLength = contextFloorPlanData.doors?.length || 0;
      const currentDoorsLength = floorPlanData.doors?.length || 0;
      const contextWindowsLength = contextFloorPlanData.windows?.length || 0;
      const currentWindowsLength = floorPlanData.windows?.length || 0;
      const contextDimensionsLength =
        contextFloorPlanData.dimensionLines?.length || 0;
      const currentDimensionsLength = floorPlanData.dimensionLines?.length || 0;

      if (
        contextLabelsLength !== currentLabelsLength ||
        contextObjectsLength !== currentObjectsLength ||
        contextDoorsLength !== currentDoorsLength ||
        contextWindowsLength !== currentWindowsLength ||
        contextDimensionsLength !== currentDimensionsLength
      ) {
        setHasChanges(true);
      }
    }
  }, [
    floorPlanData?.labels,
    floorPlanData?.objects,
    floorPlanData?.doors,
    floorPlanData?.windows,
    floorPlanData?.dimensionLines,
    contextFloorPlanData?.labels,
    contextFloorPlanData?.objects,
    contextFloorPlanData?.doors,
    contextFloorPlanData?.windows,
    contextFloorPlanData?.dimensionLines,
  ]);

  /*   useEffect(() => {
      const handleTemporaryBoundsUpdate = (event: CustomEvent) => {
        if (!event.detail) return;
  
        const tempBounds = event.detail;
  
        const paddedBounds = {
          minX: tempBounds.minX - padding,
          maxX: tempBounds.maxX + padding,
          minZ: tempBounds.minZ - padding,
          maxZ: tempBounds.maxZ + padding,
        };
  
        const newContentWidth = paddedBounds.maxX - paddedBounds.minX;
        const newContentHeight = paddedBounds.maxZ - paddedBounds.minZ;
  
        if (floorPlanRef.current) {
          floorPlanRef.current.style.width = `${newContentWidth * scale}px`;
          floorPlanRef.current.style.height = `${newContentHeight * scale}px`;
        }
      };
  
      window.addEventListener(
        "temporaryBoundsUpdate",
        handleTemporaryBoundsUpdate as EventListener
      );
  
      return () => {
        window.removeEventListener(
          "temporaryBoundsUpdate",
          handleTemporaryBoundsUpdate as EventListener
        );
      };
    }, [padding, scale]); */

  const positionWallMeasurements = (
    transformedPoints: { x: number; y: number }[],
    room: Room
  ) => {
    if (transformedPoints.length < 3) return null;

    const result = [];

    for (let i = 0; i < transformedPoints.length; i++) {
      const nextIndex = (i + 1) % transformedPoints.length;
      const point1 = transformedPoints[i];
      const point2 = transformedPoints[nextIndex];

      const dx = point2.x - point1.x;
      const dy = point2.y - point1.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length < 20) continue;

      const midpoint = {
        x: (point1.x + point2.x) / 2,
        y: (point1.y + point2.y) / 2,
      };

      const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

      const wallLength = Math.sqrt(
        Math.pow(room.floor_polygon[(i + 1) % room.floor_polygon.length].x - room.floor_polygon[i].x, 2) +
        Math.pow(room.floor_polygon[(i + 1) % room.floor_polygon.length].z - room.floor_polygon[i].z, 2)
      ) / 10;
      const centroid = {
        x: transformedPoints.reduce((sum, p) => sum + p.x, 0) / transformedPoints.length,
        y: transformedPoints.reduce((sum, p) => sum + p.y, 0) / transformedPoints.length,
      };

      const dx2 = centroid.x - midpoint.x;
      const dy2 = centroid.y - midpoint.y;
      const dist = Math.sqrt(dx2 * dx2 + dy2 * dy2);

      const dirX = dx2 / dist;
      const dirY = dy2 / dist;

      const offsetDistance = 7;

      let labelAngle = angle;
      if (labelAngle > 90 || labelAngle < -90) {
        labelAngle += 180;
      }

      result.push({
        position: {
          x: midpoint.x + dirX * offsetDistance,
          y: midpoint.y + dirY * offsetDistance,
        },
        angle: labelAngle,
        value: wallLength.toString(),
        isWidth: false,
      });
    }

    return result;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingWallWidth || !draggingWallId) return;

      const deltaX = e.movementX;
      const sensitivity = 0.1;
      const widthChange = deltaX * sensitivity;

      const currentWidth = wallWidths[draggingWallId] || 4;
      const newWidth = Math.max(1, Math.min(50, currentWidth + widthChange));

      updateWallWidth(draggingWallId, Math.round(newWidth));
    };

    const handleMouseUp = () => {
      if (isDraggingWallWidth) {
        setIsDraggingWallWidth(false);
        setDraggingWallId(null);
        setInitialWallWidth(0);
        setHasChanges(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingWallWidth || !draggingWallId || e.touches.length !== 1)
        return;

      e.preventDefault();
    };

    const handleTouchEnd = () => {
      handleMouseUp();
    };

    if (isDraggingWallWidth) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [
    isDraggingWallWidth,
    draggingWallId,
    initialWallWidth,
    updateWallWidth,
    wallWidths,
  ]);

  const detectAndConvertWallsToRooms = (
    floorPlanData: FloorPlanData,
    setFloorPlanData: React.Dispatch<React.SetStateAction<FloorPlanData>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    calculateRoomArea: (polygon: Point[]) => number,
    calculateRoomDimensions: (polygon: Point[]) => {
      width: number;
      height: number;
    },
    generateUniqueId: (prefix: string) => string
  ) => {
    const wallRooms = floorPlanData.rooms.filter(
      (room: Room) => room.room_type === "Wall"
    );

    if (wallRooms.length < 3) return;

    const connectedWallGroups = findConnectedWallGroups(wallRooms);

    connectedWallGroups.forEach((wallGroup: Room[]) => {
      if (wallGroup.length >= 3) {
        const closedPolygon = attemptToFormClosedPolygon(wallGroup);

        if (closedPolygon) {
          convertWallsToRoom(
            closedPolygon,
            wallGroup,
            floorPlanData,
            setFloorPlanData,
            setHasChanges,
            calculateRoomArea,
            calculateRoomDimensions,
            generateUniqueId
          );
        }
      }
    });
  };

  const findConnectedWallGroups = (wallRooms: Room[]): Room[][] => {
    const visited = new Set<number>();
    const groups: Room[][] = [];
    const tolerance = 20.0;

    wallRooms.forEach((wall: Room, index: number) => {
      if (visited.has(index)) return;

      const group: Room[] = [];
      const stack = [index];

      while (stack.length > 0) {
        const currentIndex = stack.pop();
        if (currentIndex === undefined || visited.has(currentIndex)) continue;

        visited.add(currentIndex);
        const currentWall = wallRooms[currentIndex];
        group.push(currentWall);

        wallRooms.forEach((otherWall: Room, otherIndex: number) => {
          if (visited.has(otherIndex) || currentIndex === otherIndex) return;

          if (areWallsConnected(currentWall, otherWall, tolerance)) {
            stack.push(otherIndex);
          }
        });
      }

      if (group.length > 0) {
        groups.push(group);
      }
    });

    return groups;
  };

  const isPointsClose = (
    point1: Point,
    point2: Point,
    tolerance: number
  ): boolean => {
    const dx = point1.x - point2.x;
    const dz = point1.z - point2.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    return distance <= tolerance;
  };

  const attemptToFormClosedPolygon = (
    wallGroup: Room[]
  ): { polygon: Point[]; walls: string[] } | null => {
    if (wallGroup.length < 3) return null;

    const tolerance = 20.0;

    for (let startIndex = 0; startIndex < wallGroup.length; startIndex++) {
      const result = tryFormPolygonFromStart(wallGroup, startIndex, tolerance);
      if (result) {
        return result;
      }
    }
    return null;
  };

  const calculateTotalArea = (rooms: Room[], roomRotations: { [key: string]: number }) => {
    const boundaries = rooms.filter(room => room.isBoundary);
    const regularRooms = rooms.filter(room =>
      room.room_type !== "Wall" &&
      room.room_type !== "Reference" &&
      !room.isBoundary
    );

    if (boundaries.length === 0) {
      return regularRooms.reduce((sum, room) => sum + room.area, 0);
    }

    if (boundaries.length > 1) {
      const boundariesOutsideOthers: Room[] = [];

      boundaries.forEach(boundary => {
        const boundaryCenter = {
          x: boundary.floor_polygon.reduce((sum, p) => sum + p.x, 0) / boundary.floor_polygon.length,
          z: boundary.floor_polygon.reduce((sum, p) => sum + p.z, 0) / boundary.floor_polygon.length,
        };

        const isInsideAnotherBoundary = boundaries.some(otherBoundary =>
          otherBoundary.id !== boundary.id &&
          isPointInsidePolygon(boundaryCenter, otherBoundary.floor_polygon)
        );

        if (!isInsideAnotherBoundary) {
          boundariesOutsideOthers.push(boundary);
        }
      });

      const boundariesArea = boundariesOutsideOthers.reduce((sum, boundary) => sum + boundary.area, 0);

      const roomsOutsideAllBoundaries: Room[] = [];

      regularRooms.forEach(room => {
        const roomRotation = roomRotations[room.id] || 0;
        let roomPolygon = room.floor_polygon;

        if (roomRotation !== 0) {
          const centroid = {
            x: roomPolygon.reduce((sum, p) => sum + p.x, 0) / roomPolygon.length,
            z: roomPolygon.reduce((sum, p) => sum + p.z, 0) / roomPolygon.length,
          };
          const radians = (roomRotation * Math.PI) / 180;
          const cos = Math.cos(radians);
          const sin = Math.sin(radians);

          roomPolygon = roomPolygon.map(point => {
            const dx = point.x - centroid.x;
            const dz = point.z - centroid.z;
            return {
              x: centroid.x + dx * cos - dz * sin,
              z: centroid.z + dx * sin + dz * cos,
            };
          });
        }

        const roomCenter = {
          x: roomPolygon.reduce((sum, p) => sum + p.x, 0) / roomPolygon.length,
          z: roomPolygon.reduce((sum, p) => sum + p.z, 0) / roomPolygon.length,
        };

        const isInsideAnyBoundary = boundaries.some(boundary =>
          isPointInsidePolygon(roomCenter, boundary.floor_polygon)
        );

        if (!isInsideAnyBoundary) {
          roomsOutsideAllBoundaries.push(room);
        }
      });

      const outsideRoomsArea = roomsOutsideAllBoundaries.reduce((sum: number, room: Room) => sum + room.area, 0);
      return boundariesArea + outsideRoomsArea;
    }

    const boundary = boundaries[0];
    const roomsOutsideBoundary: Room[] = [];

    regularRooms.forEach(room => {
      const roomRotation = roomRotations[room.id] || 0;
      let roomPolygon = room.floor_polygon;

      if (roomRotation !== 0) {
        const centroid = {
          x: roomPolygon.reduce((sum, p) => sum + p.x, 0) / roomPolygon.length,
          z: roomPolygon.reduce((sum, p) => sum + p.z, 0) / roomPolygon.length,
        };
        const radians = (roomRotation * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);

        roomPolygon = roomPolygon.map(point => {
          const dx = point.x - centroid.x;
          const dz = point.z - centroid.z;
          return {
            x: centroid.x + dx * cos - dz * sin,
            z: centroid.z + dx * sin + dz * cos,
          };
        });
      }

      const roomCenter = {
        x: roomPolygon.reduce((sum, p) => sum + p.x, 0) / roomPolygon.length,
        z: roomPolygon.reduce((sum, p) => sum + p.z, 0) / roomPolygon.length,
      };

      if (!isPointInsidePolygon(roomCenter, boundary.floor_polygon)) {
        roomsOutsideBoundary.push(room);
      }
    });

    if (roomsOutsideBoundary.length > 0) {
      return boundary.area + roomsOutsideBoundary.reduce((sum: number, room: Room) => sum + room.area, 0);
    } else {
      return boundary.area;
    }
  };

  const tryFormPolygonFromStart = (
    wallGroup: Room[],
    startIndex: number,
    tolerance: number
  ): { polygon: Point[]; walls: string[] } | null => {
    const polygon: Point[] = [];
    const usedWalls = new Set<string>();

    let currentWall = wallGroup[startIndex];
    usedWalls.add(currentWall.id);

    const directions = [
      {
        start: currentWall.floor_polygon[0],
        end: currentWall.floor_polygon[1],
      },
      {
        start: currentWall.floor_polygon[1],
        end: currentWall.floor_polygon[0],
      },
    ];

    for (const direction of directions) {
      const tempPolygon: Point[] = [
        { ...direction.start },
        { ...direction.end },
      ];
      const tempUsedWalls = new Set([currentWall.id]);
      let tempCurrentEndPoint = { ...direction.end };

      let attempts = 0;
      const maxAttempts = wallGroup.length * 5;

      while (tempUsedWalls.size < wallGroup.length && attempts < maxAttempts) {
        attempts++;

        let bestConnection = findBestConnection(
          wallGroup,
          tempCurrentEndPoint,
          tempUsedWalls,
          tolerance
        );
        if (bestConnection) {
          const { wall, connectPoint, nextPoint } = bestConnection;
          tempPolygon.push({ ...nextPoint });
          tempUsedWalls.add(wall.id);
          tempCurrentEndPoint = { ...nextPoint };
        } else {
          break;
        }
      }

      if (tempUsedWalls.size >= 3) {
        const firstPoint = tempPolygon[0];
        const distanceToClose = Math.sqrt(
          Math.pow(tempCurrentEndPoint.x - firstPoint.x, 2) +
          Math.pow(tempCurrentEndPoint.z - firstPoint.z, 2)
        );

        if (distanceToClose <= tolerance) {
          if (tempPolygon.length > 3) {
            tempPolygon.pop();
          }

          return {
            polygon: tempPolygon,
            walls: Array.from(tempUsedWalls),
          };
        }
      }
    }

    return null;
  };

  const findBestConnection = (
    wallGroup: Room[],
    fromPoint: Point,
    usedWalls: Set<string>,
    tolerance: number
  ): { wall: Room; connectPoint: Point; nextPoint: Point } | null => {
    let bestConnection: {
      wall: Room;
      connectPoint: Point;
      nextPoint: Point;
      distance: number;
    } | null = null;

    for (const wall of wallGroup) {
      if (usedWalls.has(wall.id)) continue;

      const wallStart = wall.floor_polygon[0];
      const wallEnd = wall.floor_polygon[1];

      const connections = [
        {
          connectPoint: wallStart,
          nextPoint: wallEnd,
          distance: getDistance(fromPoint, wallStart),
        },
        {
          connectPoint: wallEnd,
          nextPoint: wallStart,
          distance: getDistance(fromPoint, wallEnd),
        },
      ];

      for (const conn of connections) {
        if (conn.distance <= tolerance) {
          if (!bestConnection || conn.distance < bestConnection.distance) {
            bestConnection = { wall, ...conn };
          }
        }
      }
    }

    if (bestConnection && bestConnection.distance <= tolerance) {
      return {
        wall: bestConnection.wall,
        connectPoint: bestConnection.connectPoint,
        nextPoint: bestConnection.nextPoint,
      };
    }

    return null;
  };

  const forceConnectWalls = (
    wallGroup: Room[],
    tolerance: number
  ): { polygon: Point[]; walls: string[] } | null => {
    const allPoints: Point[] = [];

    wallGroup.forEach((wall) => {
      allPoints.push({ ...wall.floor_polygon[0] });
      allPoints.push({ ...wall.floor_polygon[1] });
    });

    const uniquePoints: Point[] = [];
    const pointTolerance = 5.0;

    for (const point of allPoints) {
      const isDuplicate = uniquePoints.some(
        (existing) => getDistance(point, existing) <= pointTolerance
      );

      if (!isDuplicate) {
        uniquePoints.push(point);
      }
    }

    if (uniquePoints.length >= 3) {
      const center = {
        x: uniquePoints.reduce((sum, p) => sum + p.x, 0) / uniquePoints.length,
        z: uniquePoints.reduce((sum, p) => sum + p.z, 0) / uniquePoints.length,
      };

      uniquePoints.sort((a, b) => {
        const angleA = Math.atan2(a.z - center.z, a.x - center.x);
        const angleB = Math.atan2(b.z - center.z, b.x - center.x);
        return angleA - angleB;
      });

      return {
        polygon: uniquePoints,
        walls: wallGroup.map((w) => w.id),
      };
    }

    return null;
  };

  const getDistance = (point1: Point, point2: Point): number => {
    return Math.sqrt(
      Math.pow(point1.x - point2.x, 2) + Math.pow(point1.z - point2.z, 2)
    );
  };

  const areWallsConnected = (
    wall1: Room,
    wall2: Room,
    tolerance: number
  ): boolean => {
    const wall1Start = wall1.floor_polygon[0];
    const wall1End = wall1.floor_polygon[1];
    const wall2Start = wall2.floor_polygon[0];
    const wall2End = wall2.floor_polygon[1];

    const connected =
      getDistance(wall1Start, wall2Start) <= tolerance ||
      getDistance(wall1Start, wall2End) <= tolerance ||
      getDistance(wall1End, wall2Start) <= tolerance ||
      getDistance(wall1End, wall2End) <= tolerance;

    return connected;
  };

  const convertWallsToRoom = (
    closedPolygon: { polygon: Point[]; walls: string[] },
    wallGroup: Room[],
    floorPlanData: FloorPlanData,
    setFloorPlanData: React.Dispatch<React.SetStateAction<FloorPlanData>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    calculateRoomArea: (polygon: Point[]) => number,
    calculateRoomDimensions: (polygon: Point[]) => {
      width: number;
      height: number;
    },
    generateUniqueId: (prefix: string) => string
  ) => {
    const polygon = closedPolygon.polygon;
    const usedWallIds = closedPolygon.walls;

    const area = calculateRoomArea(polygon);
    const dimensions = calculateRoomDimensions(polygon);

    const newRoom: Room = {
      id: generateUniqueId("room"),
      room_type: "",
      area: area,
      height: dimensions.height,
      width: dimensions.width,
      floor_polygon: polygon,
      is_regular: 1,
    };

    setFloorPlanData((prevData: FloorPlanData) => {
      const updatedRooms = prevData.rooms.filter(
        (room: Room) =>
          !(room.room_type === "Wall" && usedWallIds.includes(room.id))
      );

      updatedRooms.push(newRoom);

      const totalArea = calculateTotalArea(updatedRooms, roomRotations);

      const actualRoomCount = updatedRooms.filter(
        (room: Room) => room.room_type !== "Wall" && room.room_type !== "Reference" && room.room_type !== "Boundary"
      ).length;

      let updatedRoomTypes = prevData.room_types ? [...prevData.room_types] : [];
      if (!updatedRoomTypes.includes("SecondRoom")) {
        updatedRoomTypes.push("SecondRoom");
      }

      const updatedData: FloorPlanData = {
        ...prevData,
        rooms: updatedRooms,
        room_count: actualRoomCount,
        room_types: updatedRoomTypes,
        total_area: parseFloat(totalArea.toFixed(2)),
      };

      setTimeout(() => {
        updateContextFloorPlanData(updatedData);
      }, 0);

      return updatedData;
    });

    setHasChanges(true);
  };

  const findRoomNamePosition = (
    transformedPoints: { x: number; y: number }[],
    centroid: { x: number; y: number },
    roomType: string
  ) => {
    if (transformedPoints.length < 3) return centroid;

    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    transformedPoints.forEach((point) => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });

    const width = maxX - minX;
    const height = maxY - minY;

    const roomNamePadding = 8;

    const isSmallRoom = width < 60 || height < 60;
    const isNarrowRoom = width < height * 0.5 || height < width * 0.5;
    const isWideRoom = width > height * 1.5;

    if (roomType === "SecondRoom" || roomType === "Kitchen") {
      return {
        x: centroid.x,
        y: centroid.y - height * 0.08,
      };
    }

    if (isSmallRoom || isNarrowRoom || isWideRoom) {
      let bestPosition = { ...centroid };

      if (width < height * 0.5) {
        bestPosition.y = centroid.y - height * 0.08;
      }

      if (height < width * 0.5) {
        bestPosition.y = centroid.y - height * 0.08;
      }

      if (width < 40 && height < 40) {
        bestPosition = {
          x: (minX + maxX) / 2,
          y: (minY + maxY) / 2 - roomNamePadding / 3,
        };
      }

      return bestPosition;
    }

    return {
      x: centroid.x,
      y: centroid.y - roomNamePadding / 2,
    };
  };

  const handleDragEnd = useCallback(() => {
    checkAndUpdateOverlaps();
    checkWallModificationRef.current();
  }, [checkAndUpdateOverlaps]);

  const eventHandlers = useEventHandlers(
    dragState,
    svgRef as React.RefObject<SVGSVGElement>,
    scale,
    reverseTransformCoordinates,
    calculateRoomDimensions,
    calculateRoomArea,
    setFloorPlanData,
    setDragState,
    handleDragEnd
  );

  useEffect(() => {
    const updateLeft = () => {
      if (window.innerWidth > 850) {
        setLeftPosition("23%");
      } else {
        setLeftPosition("17.5%");
      }
    };

    updateLeft();
    window.addEventListener("resize", updateLeft);

    return () => window.removeEventListener("resize", updateLeft);
  }, []);

  const getOverlappingRoomNamesHelper = () => {
    return getOverlappingRoomNames(overlappingRooms, getRoomType);
  };

  const rotateAllSelectedRooms = (direction: "left" | "right") => {
    if (selectedRoomIds.length <= 1) return;

    captureStateBeforeChange();
    const newRotations = { ...roomRotations };

    selectedRoomIds.forEach((roomId) => {
      const currentRotation = roomRotations[roomId] || 0;
      const rotationAmount = direction === "right" ? 90 : -90;
      newRotations[roomId] = (currentRotation + rotationAmount) % 360;
    });

    setRoomRotations(newRotations);
    setHasChanges(true);
    checkAndUpdateOverlaps();
  };

  const doesWallCrossRoom = (
    wallStart: Point,
    wallEnd: Point,
    roomPolygon: Point[]
  ): boolean => {
    const touchTolerance = 5.0;
    const touchedEdges = new Set();

    for (let i = 0; i < roomPolygon.length; i++) {
      const edgeStart = roomPolygon[i];
      const edgeEnd = roomPolygon[(i + 1) % roomPolygon.length];

      let edgeTouched = false;

      const startDistToEdge = distancePointToLineSegment(
        wallStart,
        edgeStart,
        edgeEnd
      );
      const endDistToEdge = distancePointToLineSegment(
        wallEnd,
        edgeStart,
        edgeEnd
      );

      if (
        startDistToEdge <= touchTolerance ||
        endDistToEdge <= touchTolerance
      ) {
        edgeTouched = true;
      }

      const intersection = getLineIntersectionPoint(
        wallStart,
        wallEnd,
        edgeStart,
        edgeEnd
      );
      if (
        intersection &&
        isPointOnLineSegment(intersection, edgeStart, edgeEnd)
      ) {
        edgeTouched = true;
      }

      if (edgeTouched) {
        touchedEdges.add(i);
      }
    }
    return touchedEdges.size >= 2;
  };

  const distancePointToLineSegment = (
    point: Point,
    segStart: Point,
    segEnd: Point
  ): number => {
    const A = point.x - segStart.x;
    const B = point.z - segStart.z;
    const C = segEnd.x - segStart.x;
    const D = segEnd.z - segStart.z;

    const dot = A * C + B * D;
    const lenSquared = C * C + D * D;

    if (lenSquared === 0) {
      return Math.sqrt(A * A + B * B);
    }

    let param = dot / lenSquared;
    param = Math.max(0, Math.min(1, param));

    const closestX = segStart.x + param * C;
    const closestZ = segStart.z + param * D;

    return Math.sqrt(
      Math.pow(closestX - point.x, 2) + Math.pow(closestZ - point.z, 2)
    );
  };
  const isPointOnLineSegment = (
    point: Point,
    segStart: Point,
    segEnd: Point
  ): boolean => {
    const tolerance = 0.01;

    const minX = Math.min(segStart.x, segEnd.x) - tolerance;
    const maxX = Math.max(segStart.x, segEnd.x) + tolerance;
    const minZ = Math.min(segStart.z, segEnd.z) - tolerance;
    const maxZ = Math.max(segStart.z, segEnd.z) + tolerance;

    return (
      point.x >= minX && point.x <= maxX && point.z >= minZ && point.z <= maxZ
    );
  };

  const isPointInsidePolygon = (point: Point, polygon: Point[]): boolean => {
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const zi = polygon[i].z;
      const xj = polygon[j].x;
      const zj = polygon[j].z;

      if (
        zi > point.z !== zj > point.z &&
        point.x < ((xj - xi) * (point.z - zi)) / (zj - zi) + xi
      ) {
        inside = !inside;
      }
    }

    return inside;
  };

  const doLinesIntersect = (
    p1: Point,
    q1: Point,
    p2: Point,
    q2: Point
  ): boolean => {
    const orientation = (p: Point, q: Point, r: Point): number => {
      const val = (q.z - p.z) * (r.x - q.x) - (q.x - p.x) * (r.z - q.z);
      if (Math.abs(val) < 0.001) return 0;
      return val > 0 ? 1 : 2;
    };

    const onSegment = (p: Point, q: Point, r: Point): boolean => {
      return (
        q.x <= Math.max(p.x, r.x) + 0.001 &&
        q.x >= Math.min(p.x, r.x) - 0.001 &&
        q.z <= Math.max(p.z, r.z) + 0.001 &&
        q.z >= Math.min(p.z, r.z) - 0.001
      );
    };

    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);

    if (o1 !== o2 && o3 !== o4) return true;

    if (o1 === 0 && onSegment(p1, p2, q1)) return true;
    if (o2 === 0 && onSegment(p1, q2, q1)) return true;
    if (o3 === 0 && onSegment(p2, p1, q2)) return true;
    if (o4 === 0 && onSegment(p2, q1, q2)) return true;

    return false;
  };

  const getLineIntersectionPoint = (
    p1: Point,
    q1: Point,
    p2: Point,
    q2: Point
  ): Point | null => {
    const denom = (p1.x - q1.x) * (p2.z - q2.z) - (p1.z - q1.z) * (p2.x - q2.x);

    if (Math.abs(denom) < 0.001) return null;

    const t =
      ((p1.x - p2.x) * (p2.z - q2.z) - (p1.z - p2.z) * (p2.x - q2.x)) / denom;
    const u =
      -((p1.x - q1.x) * (p1.z - p2.z) - (p1.z - q1.z) * (p1.x - p2.x)) / denom;

    if (t >= -0.001 && t <= 1.001 && u >= -0.001 && u <= 1.001) {
      return {
        x: p1.x + t * (q1.x - p1.x),
        z: p1.z + t * (q1.z - p1.z),
      };
    }

    return null;
  };

  const divideRoomPolygon = (
    roomPolygon: Point[],
    wallStart: Point,
    wallEnd: Point,
    calculateRoomArea: (polygon: Point[]) => number,
    calculateRoomDimensions: (polygon: Point[]) => {
      width: number;
      height: number;
    },
    generateUniqueId: (prefix: string) => string
  ): Room[] | null => {
    const wallDx = wallEnd.x - wallStart.x;
    const wallDz = wallEnd.z - wallStart.z;
    const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);

    if (wallLength === 0) return null;

    const wallDirX = wallDx / wallLength;
    const wallDirZ = wallDz / wallLength;

    const intersections: Array<{
      point: Point;
      edgeIndex: number;
      distance: number;
    }> = [];

    const extendedStart = {
      x: wallStart.x - wallDirX * 1000,
      z: wallStart.z - wallDirZ * 1000,
    };
    const extendedEnd = {
      x: wallStart.x + wallDirX * 1000,
      z: wallStart.z + wallDirZ * 1000,
    };

    for (let i = 0; i < roomPolygon.length; i++) {
      const edgeStart = roomPolygon[i];
      const edgeEnd = roomPolygon[(i + 1) % roomPolygon.length];

      const intersection = getLineIntersectionPoint(
        extendedStart,
        extendedEnd,
        edgeStart,
        edgeEnd
      );
      if (
        intersection &&
        isPointOnLineSegment(intersection, edgeStart, edgeEnd)
      ) {
        const distanceX = intersection.x - wallStart.x;
        const distanceZ = intersection.z - wallStart.z;
        const distance = distanceX * wallDirX + distanceZ * wallDirZ;

        intersections.push({
          point: intersection,
          edgeIndex: i,
          distance: distance,
        });
      }
    }

    if (intersections.length < 2) {
      return null;
    }

    intersections.sort((a, b) => a.distance - b.distance);

    let entryIntersection = intersections[0];
    let exitIntersection = intersections[1];

    if (intersections.length > 2) {
      for (let i = 0; i < intersections.length - 1; i++) {
        const int1 = intersections[i];
        const int2 = intersections[i + 1];

        if (int1.distance <= 0 && int2.distance >= 0) {
          entryIntersection = int1;
          exitIntersection = int2;
          break;
        }
      }
    }

    const snappedWallStart = entryIntersection.point;
    const snappedWallEnd = exitIntersection.point;

    const polygon1: Point[] = [];

    polygon1.push({ ...snappedWallStart });

    let currentIndex = (entryIntersection.edgeIndex + 1) % roomPolygon.length;
    while (
      currentIndex !==
      (exitIntersection.edgeIndex + 1) % roomPolygon.length
    ) {
      polygon1.push({ ...roomPolygon[currentIndex] });
      currentIndex = (currentIndex + 1) % roomPolygon.length;
    }

    polygon1.push({ ...snappedWallEnd });

    const polygon2: Point[] = [];

    polygon2.push({ ...snappedWallStart });

    currentIndex = entryIntersection.edgeIndex;
    while (currentIndex !== exitIntersection.edgeIndex) {
      polygon2.push({ ...roomPolygon[currentIndex] });
      currentIndex =
        (currentIndex - 1 + roomPolygon.length) % roomPolygon.length;
    }

    polygon2.push({ ...snappedWallEnd });

    const validRooms: Room[] = [];

    [polygon1, polygon2].forEach((polygon, index) => {
      if (polygon.length >= 3) {
        const area = calculateRoomArea(polygon);

        if (area > 0.1) {
          const dimensions = calculateRoomDimensions(polygon);

          validRooms.push({
            id: generateUniqueId("room"),
            room_type: "",
            area: area,
            height: dimensions.height,
            width: dimensions.width,
            floor_polygon: polygon,
            is_regular: 1,
          });
        }
      }
    });

    return validRooms.length === 2 ? validRooms : null;
  };

  const isPointOnRoomBoundary = (
    point: Point,
    roomPolygon: Point[],
    tolerance: number
  ): boolean => {
    // Check if point is close to any vertex
    for (const vertex of roomPolygon) {
      const dist = Math.sqrt(
        Math.pow(point.x - vertex.x, 2) + 
        Math.pow(point.z - vertex.z, 2)
      );
      if (dist < tolerance) {
        return true;
      }
    }
    
    // Check if point is on any edge
    for (let i = 0; i < roomPolygon.length; i++) {
      const edgeStart = roomPolygon[i];
      const edgeEnd = roomPolygon[(i + 1) % roomPolygon.length];
      
      if (isPointOnLineSegment(point, edgeStart, edgeEnd)) {
        return true;
      }
    }
    
    return false;
  };

  const divideRoomByWall = (
    newWall: Room,
    floorPlanData: FloorPlanData,
    setFloorPlanData: React.Dispatch<React.SetStateAction<FloorPlanData>>,
    calculateRoomArea: (polygon: Point[]) => number,
    calculateRoomDimensions: (polygon: Point[]) => {
      width: number;
      height: number;
    },
    generateUniqueId: (prefix: string) => string,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    roomRotations: { [key: string]: number },
    setContextFloorPlanData?: React.Dispatch<
      React.SetStateAction<FloorPlanData>
    >
  ): boolean => {
    const wallStart = newWall.floor_polygon[0];
    const wallEnd = newWall.floor_polygon[1];

    const candidateRooms = floorPlanData.rooms.filter((room: Room) => {
      if (room.room_type === "Wall" || room.floor_polygon.length < 3)
        return false;

      const roomRotation = roomRotations[room.id] || 0;
      if (roomRotation !== 0) return false;
      
      // Check if wall start or end is already on the room boundary (snapped to corner/edge)
      const tolerance = 2.0;
      const isStartOnBoundary = isPointOnRoomBoundary(wallStart, room.floor_polygon, tolerance);
      const isEndOnBoundary = isPointOnRoomBoundary(wallEnd, room.floor_polygon, tolerance);
      
      // If both endpoints are on the boundary, the wall is not dividing the room
      // If one endpoint is on boundary and wall goes outward, it's not dividing
      if (isStartOnBoundary || isEndOnBoundary) {
        return false;
      }
      
      const intersectionCount = countWallRoomIntersections(
        wallStart,
        wallEnd,
        room.floor_polygon
      );
      return intersectionCount >= 2;
    });

    if (candidateRooms.length === 0) return false;

    let roomWasDivided = false;

    const roomToDivide = candidateRooms[0];

    const result = divideRoomWithSnappedWall(
      roomToDivide.floor_polygon,
      wallStart,
      wallEnd,
      calculateRoomArea,
      calculateRoomDimensions,
      generateUniqueId
    );

    if (result && result.dividedRooms.length === 2) {
      setFloorPlanData((prevData: FloorPlanData) => {
        const updatedRooms = prevData.rooms.filter(
          (r: Room) => r.id !== roomToDivide.id && r.id !== newWall.id
        );

        result.dividedRooms.forEach((dividedRoom: Room) => {
          updatedRooms.push(dividedRoom);
        });

        const totalArea = calculateTotalArea(updatedRooms, roomRotations);

        const actualRoomCount = updatedRooms.filter(
          (r: Room) => r.room_type !== "Wall" && r.room_type !== "Reference" && r.room_type !== "Boundary"
        ).length;

        const updatedData = {
          ...prevData,
          rooms: updatedRooms,
          room_count: actualRoomCount,
          total_area: parseFloat(totalArea.toFixed(2)),
        };

        setTimeout(() => updateContextFloorPlanData(updatedData), 0);

        return updatedData;
      });

      setHasChanges(true);
      roomWasDivided = true;
    }

    return roomWasDivided;
  };
// Add these helper functions before handleWallCreated in Generated.tsx

const extractAllWallSegments = (rooms: Room[]): Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}> => {
  const segments: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}> = [];
  
  rooms.forEach(room => {
    if (room.room_type === "Wall") {
      // Standalone wall
      segments.push({
        id: room.id,
        start: room.floor_polygon[0],
        end: room.floor_polygon[1],
        sourceRoomId: room.id,
        isRoomWall: false
      });
    } else if (room.room_type !== "Reference" && room.room_type !== "Boundary" && room.floor_polygon.length >= 3) {
      // Regular room - extract each edge as a wall segment
      for (let i = 0; i < room.floor_polygon.length; i++) {
        const nextIndex = (i + 1) % room.floor_polygon.length;
        segments.push({
          id: `${room.id}-edge-${i}`,
          start: room.floor_polygon[i],
          end: room.floor_polygon[nextIndex],
          sourceRoomId: room.id,
          isRoomWall: true
        });
      }
    }
  });
  
  console.log(`[Room Detection] Extracted ${segments.length} total segments`);
  return segments;
};

const findAllClosedAreasWithNewWall = (
  newWallSegment: {id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean},
  allSegments: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}>,
  calculateRoomArea: (polygon: Point[]) => number
): Array<{ polygon: Point[]; segmentIds: string[]; usedRoomIds: Set<string>; area: number; corners: number; roomWallSegmentCount: number }> => {
  console.log(`[Room Detection] Starting BFS search with new wall, ${allSegments.length} segments available`);
  const startTime = performance.now();
  
  const tolerance = 20.0;
  const foundPolygons: Array<{ polygon: Point[]; segmentIds: string[]; usedRoomIds: Set<string>; area: number; corners: number; roomWallSegmentCount: number }> = [];
  
  const dist = (p1: Point, p2: Point) => 
    Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.z - p2.z, 2));

  // Pre-filter segments that are within reasonable distance of the new wall
  const maxSearchDistance = 1000;
  const newWallCenter = {
    x: (newWallSegment.start.x + newWallSegment.end.x) / 2,
    z: (newWallSegment.start.z + newWallSegment.end.z) / 2
  };
  
  const nearbySegments = allSegments.filter(seg => {
    const segCenter = {
      x: (seg.start.x + seg.end.x) / 2,
      z: (seg.start.z + seg.end.z) / 2
    };
    const distance = dist(
      { x: newWallCenter.x, z: newWallCenter.z },
      { x: segCenter.x, z: segCenter.z }
    );
    return distance <= maxSearchDistance;
  });
  
  console.log(`[Room Detection] Filtered to ${nearbySegments.length} nearby segments`);

  // Try both directions from the new wall
  const directions = [
    { start: newWallSegment.start, end: newWallSegment.end },
    { start: newWallSegment.end, end: newWallSegment.start },
  ];

  for (let dirIdx = 0; dirIdx < directions.length; dirIdx++) {
    const direction = directions[dirIdx];
    console.log(`[Room Detection] Trying direction ${dirIdx + 1}/2 using BFS`);
    
    const pathsExplored = explorePathsBFS(
      direction.start,
      direction.end,
      newWallSegment,
      nearbySegments,
      allSegments,
      tolerance,
      foundPolygons,
      calculateRoomArea
    );
    
    console.log(`[Room Detection] Direction ${dirIdx + 1} explored ${pathsExplored} paths, found ${foundPolygons.length} polygons so far`);
    
    // Stop if we found good polygons
    if (foundPolygons.length >= 5) {
      console.log(`[Room Detection] Found enough polygons (${foundPolygons.length}), stopping search`);
      break;
    }
  }

  const endTime = performance.now();
  console.log(`[Room Detection] BFS search completed in ${(endTime - startTime).toFixed(2)}ms`);
  console.log(`[Room Detection] Found ${foundPolygons.length} unique closed polygons`);

  return foundPolygons;
};

interface PathState {
  polygon: Point[];
  usedSegmentIds: Set<string>;
  usedRoomIds: Set<string>;
  currentEndPoint: Point;
  depth: number;
}

const explorePathsBFS = (
  startPoint: Point,
  firstEndPoint: Point,
  newWallSegment: {id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean},
  nearbySegments: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}>,
  allSegments: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}>,
  tolerance: number,
  foundPolygons: Array<{ polygon: Point[]; segmentIds: string[]; usedRoomIds: Set<string>; area: number; corners: number; roomWallSegmentCount: number }>,
  calculateRoomArea: (polygon: Point[]) => number
): number => {
  const maxDepth = 12;
  const maxPolygonsToFind = 5;
  const maxPathsToExplore = 5000; // Safety limit
  
  const dist = (p1: Point, p2: Point) => 
    Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.z - p2.z, 2));

  // BFS queue: each item represents a potential path
  const queue: PathState[] = [{
    polygon: [{ ...startPoint }, { ...firstEndPoint }],
    usedSegmentIds: new Set([newWallSegment.id]),
    usedRoomIds: new Set([newWallSegment.sourceRoomId]),
    currentEndPoint: { ...firstEndPoint },
    depth: 1
  }];

  let pathsExplored = 0;
  const visitedStates = new Set<string>(); // Prevent revisiting same state

  while (queue.length > 0 && pathsExplored < maxPathsToExplore && foundPolygons.length < maxPolygonsToFind) {
    const currentState = queue.shift()!;
    pathsExplored++;

    // Create a state signature to avoid revisiting
    const stateSignature = Array.from(currentState.usedSegmentIds).sort().join(',');
    if (visitedStates.has(stateSignature)) {
      continue;
    }
    visitedStates.add(stateSignature);

    // Check if we can close the loop back to start
    const distanceToStart = dist(currentState.currentEndPoint, startPoint);
    
    if (distanceToStart <= tolerance && currentState.usedSegmentIds.size >= 3) {
      // Successfully closed the polygon!
      const finalPolygon = [...currentState.polygon];
      if (finalPolygon.length > 3 && dist(finalPolygon[finalPolygon.length - 1], finalPolygon[0]) < tolerance) {
        finalPolygon.pop(); // Remove duplicate last point
      }

      // Check if this polygon is unique
      const isDuplicate = foundPolygons.some(existing => {
        if (existing.polygon.length !== finalPolygon.length) return false;
        
        const checkPoints = Math.min(3, finalPolygon.length);
        for (let i = 0; i < checkPoints; i++) {
          const dx = existing.polygon[i].x - finalPolygon[i].x;
          const dz = existing.polygon[i].z - finalPolygon[i].z;
          if (Math.sqrt(dx * dx + dz * dz) > 5.0) {
            return false;
          }
        }
        return true;
      });

      if (!isDuplicate) {
        const area = calculateRoomArea(finalPolygon);
        
        // Count how many segments are from existing room walls
        const roomWallSegmentCount = Array.from(currentState.usedSegmentIds).filter(segId => {
          const segment = allSegments.find(s => s.id === segId);
          return segment && segment.isRoomWall;
        }).length;
        
        console.log(`[Room Detection BFS] Found closed polygon at depth ${currentState.depth}: ${finalPolygon.length} corners, area: ${area.toFixed(2)}, room walls: ${roomWallSegmentCount}`);
        
        foundPolygons.push({
          polygon: finalPolygon,
          segmentIds: Array.from(currentState.usedSegmentIds),
          usedRoomIds: new Set(currentState.usedRoomIds),
          area: area,
          corners: finalPolygon.length,
          roomWallSegmentCount: roomWallSegmentCount
        });
      }
      
      // Continue exploring to find other polygons (don't return immediately)
      continue;
    }

    // Don't explore too deep
    if (currentState.depth >= maxDepth) {
      continue;
    }

    // Find all possible next segments
    const possibleConnections: Array<{
      segment: {id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean};
      connectPoint: Point;
      nextPoint: Point;
      distance: number;
    }> = [];

    for (const segment of nearbySegments) {
      if (currentState.usedSegmentIds.has(segment.id)) continue;

      const connections = [
        {
          connectPoint: segment.start,
          nextPoint: segment.end,
          distance: dist(currentState.currentEndPoint, segment.start),
        },
        {
          connectPoint: segment.end,
          nextPoint: segment.start,
          distance: dist(currentState.currentEndPoint, segment.end),
        },
      ];

      for (const conn of connections) {
        if (conn.distance <= tolerance) {
          possibleConnections.push({ segment, ...conn });
        }
      }
    }

    // Sort by distance (prefer closer connections)
    possibleConnections.sort((a, b) => a.distance - b.distance);

    // Limit branching based on depth and current queue size
    let maxBranches;
    if (currentState.depth < 4) {
      maxBranches = 5; // Early: explore more
    } else if (currentState.depth < 8) {
      maxBranches = 3; // Mid: be selective
    } else {
      maxBranches = 2; // Deep: very selective
    }

    // Also limit based on queue size to prevent explosion
    if (queue.length > 100) {
      maxBranches = Math.min(maxBranches, 2);
    }
    if (queue.length > 200) {
      maxBranches = 1;
    }

    const branchesToExplore = Math.min(possibleConnections.length, maxBranches);

    // Add new states to the queue (BFS: add to end of queue)
    for (let i = 0; i < branchesToExplore; i++) {
      const connection = possibleConnections[i];
      
      const newUsedIds = new Set(currentState.usedSegmentIds);
      newUsedIds.add(connection.segment.id);
      
      const newUsedRooms = new Set(currentState.usedRoomIds);
      newUsedRooms.add(connection.segment.sourceRoomId);

      queue.push({
        polygon: [...currentState.polygon, { ...connection.nextPoint }],
        usedSegmentIds: newUsedIds,
        usedRoomIds: newUsedRooms,
        currentEndPoint: { ...connection.nextPoint },
        depth: currentState.depth + 1
      });
    }
  }

  console.log(`[Room Detection BFS] Explored ${pathsExplored} paths, queue had max size ~${visitedStates.size}`);
  return pathsExplored;
};

const explorePaths = (
  currentPolygon: Point[],
  usedSegmentIds: Set<string>,
  usedRoomIds: Set<string>,
  currentEndPoint: Point,
  startPoint: Point,
  allSegments: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}>,
  tolerance: number,
  foundPolygons: Array<{ polygon: Point[]; segmentIds: string[]; usedRoomIds: Set<string>; area: number; corners: number; roomWallSegmentCount: number }>,
  calculateRoomArea: (polygon: Point[]) => number,
  allSegmentsForCounting: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}>,
  depth: number = 0
): number => {
  // Safety limits to prevent infinite recursion and exponential explosion
  const maxDepth = 12; // Reduced from 20 - most rooms should close within 12 segments
  const maxPolygonsFound = 10; // Stop after finding 10 valid polygons
  
  if (depth > maxDepth) {
    console.log(`[Room Detection] Max depth ${maxDepth} reached, stopping this branch`);
    return 0;
  }
  
  if (foundPolygons.length >= maxPolygonsFound) {
    console.log(`[Room Detection] Max polygons ${maxPolygonsFound} found, stopping search`);
    return 0;
  }

  let pathsExplored = 1; // Count this path

  const dist = (p1: Point, p2: Point) => 
    Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.z - p2.z, 2));

  // Check if we can close the loop back to start
  const distanceToStart = dist(currentEndPoint, startPoint);
  if (distanceToStart <= tolerance && usedSegmentIds.size >= 3) {
    // Successfully closed the polygon!
    const finalPolygon = [...currentPolygon];
    if (finalPolygon.length > 3) {
      finalPolygon.pop(); // Remove duplicate last point
    }

    // Check if this polygon is unique
    const isDuplicate = foundPolygons.some(existing => {
      if (existing.polygon.length !== finalPolygon.length) return false;
      
      // Quick check: compare first few points only for performance
      const checkPoints = Math.min(3, finalPolygon.length);
      let exactMatch = true;
      for (let i = 0; i < checkPoints; i++) {
        const dx = existing.polygon[i].x - finalPolygon[i].x;
        const dz = existing.polygon[i].z - finalPolygon[i].z;
        if (Math.sqrt(dx * dx + dz * dz) > 5.0) {
          exactMatch = false;
          break;
        }
      }
      return exactMatch;
    });

    if (!isDuplicate) {
      const area = calculateRoomArea(finalPolygon);
      
      // Count how many segments are from existing room walls
      const roomWallSegmentCount = Array.from(usedSegmentIds).filter(segId => {
        const segment = allSegmentsForCounting.find(s => s.id === segId);
        return segment && segment.isRoomWall;
      }).length;
      
      console.log(`[Room Detection] Found closed polygon with ${finalPolygon.length} corners, area: ${area.toFixed(2)}, room wall segments: ${roomWallSegmentCount}`);
      
      foundPolygons.push({
        polygon: finalPolygon,
        segmentIds: Array.from(usedSegmentIds),
        usedRoomIds: new Set(usedRoomIds),
        area: area,
        corners: finalPolygon.length,
        roomWallSegmentCount: roomWallSegmentCount
      });
    }
    
    // Don't explore further from a closed polygon
    return pathsExplored;
  }

  // Find all possible next segments
  const possibleConnections: Array<{
    segment: {id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean};
    connectPoint: Point;
    nextPoint: Point;
    distance: number;
  }> = [];

  for (const segment of allSegments) {
    if (usedSegmentIds.has(segment.id)) continue;

    const connections = [
      {
        connectPoint: segment.start,
        nextPoint: segment.end,
        distance: dist(currentEndPoint, segment.start),
      },
      {
        connectPoint: segment.end,
        nextPoint: segment.start,
        distance: dist(currentEndPoint, segment.end),
      },
    ];

    for (const conn of connections) {
      if (conn.distance <= tolerance) {
        possibleConnections.push({ segment, ...conn });
      }
    }
  }

  if (possibleConnections.length === 0) {
    // Dead end
    return pathsExplored;
  }

  // Sort by distance to prefer closer connections
  possibleConnections.sort((a, b) => a.distance - b.distance);

  // CRITICAL: Limit branching factor aggressively
  // For shallow depths, allow more exploration. For deeper levels, be very selective
  let maxBranches;
  if (depth < 4) {
    maxBranches = 3; // Early on, explore top 3 connections
  } else if (depth < 8) {
    maxBranches = 2; // Mid-depth, only top 2
  } else {
    maxBranches = 1; // Deep levels, only the best connection
  }
  
  const branchesToExplore = Math.min(possibleConnections.length, maxBranches);

  // Explore each possible connection
  for (let i = 0; i < branchesToExplore; i++) {
    if (foundPolygons.length >= maxPolygonsFound) {
      break; // Stop if we found enough
    }
    
    const connection = possibleConnections[i];
    
    const newPolygon = [...currentPolygon, { ...connection.nextPoint }];
    const newUsedIds = new Set(usedSegmentIds);
    newUsedIds.add(connection.segment.id);
    const newUsedRooms = new Set(usedRoomIds);
    newUsedRooms.add(connection.segment.sourceRoomId);

    const subPaths = explorePaths(
      newPolygon,
      newUsedIds,
      newUsedRooms,
      connection.nextPoint,
      startPoint,
      allSegments,
      tolerance,
      foundPolygons,
      calculateRoomArea,
      allSegmentsForCounting,
      depth + 1
    );
    
    pathsExplored += subPaths;
  }

  return pathsExplored;
};

const selectBestPolygon = (
  polygons: Array<{ polygon: Point[]; segmentIds: string[]; usedRoomIds: Set<string>; area: number; corners: number; roomWallSegmentCount: number }>
): { polygon: Point[]; segmentIds: string[]; usedRoomIds: Set<string> } | null => {
  if (polygons.length === 0) {
    console.log(`[Room Detection] No valid polygons to select from`);
    return null;
  }

  console.log(`[Room Detection] Selecting best from ${polygons.length} polygons:`);
  polygons.forEach((p, idx) => {
    console.log(`  ${idx + 1}. Corners: ${p.corners}, Area: ${p.area.toFixed(2)}, Room wall segments: ${p.roomWallSegmentCount}`);
  });

  // Sort by: 1) fewest room wall segments, 2) fewest corners, 3) smallest area
  polygons.sort((a, b) => {
    // MOST IMPORTANT: Prefer polygons that use fewer existing room wall segments
    if (a.roomWallSegmentCount !== b.roomWallSegmentCount) {
      return a.roomWallSegmentCount - b.roomWallSegmentCount; // Fewer room walls first
    }
    
    // SECONDARY: Prefer simpler shapes (fewer corners)
    if (a.corners !== b.corners) {
      return a.corners - b.corners; // Fewest corners first
    }
    
    // TERTIARY: Prefer smaller areas
    return a.area - b.area; // Smallest area first
  });

  const best = polygons[0];
  console.log(`[Room Detection] Selected polygon with ${best.corners} corners, area ${best.area.toFixed(2)}, using ${best.roomWallSegmentCount} room wall segments`);
  
  return {
    polygon: best.polygon,
    segmentIds: best.segmentIds,
    usedRoomIds: best.usedRoomIds
  };
};
// Replace the cleanPolygonOverlappingCorners function with this corrected version

const cleanPolygonOverlappingCorners = (
  polygon: Point[],
  segmentIds: string[],
  allSegments: Array<{id: string, start: Point, end: Point, sourceRoomId: string, isRoomWall: boolean}>
): { polygon: Point[]; segmentIds: string[] } | null => {
  console.log(`[Polygon Cleanup] Checking polygon with ${polygon.length} corners for overlapping vertices`);
  
  const cornerTolerance = 20.0; // Same tolerance used for connecting walls
  
  const dist = (p1: Point, p2: Point) => 
    Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.z - p2.z, 2));

  // Find pairs of corners that are overlapping (but not adjacent)
  const overlappingPairs: Array<{idx1: number, idx2: number, distance: number}> = [];
  
  for (let i = 0; i < polygon.length; i++) {
    for (let j = i + 2; j < polygon.length; j++) {
      // Skip if they are adjacent or the last and first (already connected)
      if (j === polygon.length - 1 && i === 0) continue;
      
      const distance = dist(polygon[i], polygon[j]);
      if (distance <= cornerTolerance) {
        overlappingPairs.push({ idx1: i, idx2: j, distance });
        console.log(`[Polygon Cleanup] Found overlapping corners: index ${i} and ${j}, distance: ${distance.toFixed(2)}`);
      }
    }
  }

  if (overlappingPairs.length === 0) {
    console.log(`[Polygon Cleanup] No overlapping corners found, polygon is valid`);
    return { polygon, segmentIds };
  }

  // Sort by distance (closest pairs first)
  overlappingPairs.sort((a, b) => a.distance - b.distance);

  // Process the closest overlapping pair
  const pair = overlappingPairs[0];
  const { idx1, idx2 } = pair;
  
  console.log(`[Polygon Cleanup] Processing overlap between corners ${idx1} and ${idx2}`);

  // Calculate the length of the two paths between these corners
  // Path 1: from idx1 to idx2 (going forward)
  const path1Length = idx2 - idx1;
  
  // Path 2: from idx2 to idx1 (going around)
  const path2Length = polygon.length - path1Length;
  
  console.log(`[Polygon Cleanup] Path 1 (${idx1}→${idx2}): ${path1Length} segments`);
  console.log(`[Polygon Cleanup] Path 2 (${idx2}→${idx1} around): ${path2Length} segments`);

  // Merge the two overlapping corners into a single averaged point
  const mergedPoint = {
    x: (polygon[idx1].x + polygon[idx2].x) / 2,
    z: (polygon[idx1].z + polygon[idx2].z) / 2
  };
  
  console.log(`[Polygon Cleanup] Merged corner at (${mergedPoint.x.toFixed(2)}, ${mergedPoint.z.toFixed(2)})`);

  // Keep the path with MORE segments (longer path), remove the shorter one
  let newPolygon: Point[];
  let newSegmentIds: string[];
  
  if (path1Length < path2Length) {
    // Path 1 is shorter, remove it and keep path 2
    console.log(`[Polygon Cleanup] Removing shorter path 1, keeping path 2`);
    
    // New polygon: merged point, then from idx2+1 to end, then from start to idx1-1
    newPolygon = [
      mergedPoint,
      ...polygon.slice(idx2 + 1),
      ...polygon.slice(0, idx1)
    ];
    
    // Remove segment IDs for the removed path
    // Keep segments from idx2 onward and from 0 to idx1-1
    newSegmentIds = [
      ...segmentIds.slice(idx2),
      ...segmentIds.slice(0, idx1)
    ];
    
  } else {
    // Path 2 is shorter (or equal), remove it and keep path 1
    console.log(`[Polygon Cleanup] Removing shorter path 2, keeping path 1`);
    
    // New polygon: from idx1 to idx2-1, then merged point
    newPolygon = [
      ...polygon.slice(idx1, idx2),
      mergedPoint
    ];
    
    // Keep segment IDs for path 1
    newSegmentIds = segmentIds.slice(idx1, idx2);
  }

  console.log(`[Polygon Cleanup] Cleaned polygon: ${polygon.length} → ${newPolygon.length} corners`);
  console.log(`[Polygon Cleanup] Segments: ${segmentIds.length} → ${newSegmentIds.length}`);

  // Validate the new polygon
  if (newPolygon.length < 3) {
    console.log(`[Polygon Cleanup] Cleaned polygon has less than 3 corners, invalid`);
    return null;
  }

  // Check if there are still overlapping corners (recursive cleanup)
  return cleanPolygonOverlappingCorners(newPolygon, newSegmentIds, allSegments);
};
// Add this function near the extractAllWallSegments function in Generated.tsx

const addCornersForWallEndpoints = (
  newWall: Room,
  currentData: FloorPlanData,
  calculateRoomArea: (polygon: Point[]) => number,
  calculateRoomDimensions: (polygon: Point[]) => { width: number; height: number }
): FloorPlanData => {
  console.log('[Corner Detection] Checking if wall endpoints land on room edges...');
  
  const wallStart = newWall.floor_polygon[0];
  const wallEnd = newWall.floor_polygon[1];
  const edgeTolerance = 15.0; // Distance from edge to consider "on edge"
  const cornerTolerance = 20.0; // Distance from existing corner to skip
  
  let updatedData = currentData;
  const wallEndpoints = [
    { point: wallStart, name: 'start' },
    { point: wallEnd, name: 'end' }
  ];

  // Helper function to get closest point on line segment
  const getClosestPointOnLineSegment = (
    point: Point,
    lineStart: Point,
    lineEnd: Point
  ): { x: number; z: number; param: number; distance: number } => {
    const dx = lineEnd.x - lineStart.x;
    const dz = lineEnd.z - lineStart.z;
    const lengthSquared = dx * dx + dz * dz;

    if (lengthSquared === 0) {
      const dist = Math.sqrt(
        Math.pow(point.x - lineStart.x, 2) + Math.pow(point.z - lineStart.z, 2)
      );
      return { x: lineStart.x, z: lineStart.z, param: 0, distance: dist };
    }

    const t = Math.max(
      0,
      Math.min(
        1,
        ((point.x - lineStart.x) * dx + (point.z - lineStart.z) * dz) / lengthSquared
      )
    );

    const closestX = lineStart.x + t * dx;
    const closestZ = lineStart.z + t * dz;
    const dist = Math.sqrt(
      Math.pow(point.x - closestX, 2) + Math.pow(point.z - closestZ, 2)
    );

    return { x: closestX, z: closestZ, param: t, distance: dist };
  };

  // Check each wall endpoint
  for (const endpoint of wallEndpoints) {
    console.log(`[Corner Detection] Checking ${endpoint.name} endpoint at (${endpoint.point.x.toFixed(2)}, ${endpoint.point.z.toFixed(2)})`);
    
    let cornerAddedThisEndpoint = false;
    
    // Check against all room edges
    for (const room of updatedData.rooms) {
      if (room.room_type === "Wall" || room.room_type === "Reference" || room.room_type === "Boundary") {
        continue;
      }

      if (room.floor_polygon.length < 3) continue;

      for (let i = 0; i < room.floor_polygon.length; i++) {
        const edgeStart = room.floor_polygon[i];
        const edgeEnd = room.floor_polygon[(i + 1) % room.floor_polygon.length];

        const result = getClosestPointOnLineSegment(endpoint.point, edgeStart, edgeEnd);

        // Check if the endpoint is close to this edge
        if (result.distance <= edgeTolerance) {
          // Check if it's already close to an existing corner (don't add duplicate)
          const distToStart = Math.sqrt(
            Math.pow(endpoint.point.x - edgeStart.x, 2) +
            Math.pow(endpoint.point.z - edgeStart.z, 2)
          );
          const distToEnd = Math.sqrt(
            Math.pow(endpoint.point.x - edgeEnd.x, 2) +
            Math.pow(endpoint.point.z - edgeEnd.z, 2)
          );

          if (distToStart < cornerTolerance || distToEnd < cornerTolerance) {
            console.log(`[Corner Detection] Endpoint is already close to an existing corner, skipping`);
            continue;
          }

          // The endpoint is on the edge but not near a corner - add a corner!
          console.log(`[Corner Detection] Adding corner to room ${room.id} at edge ${i}`);
          console.log(`[Corner Detection] Corner position: (${result.x.toFixed(2)}, ${result.z.toFixed(2)})`);

          // Update the data directly instead of using setFloorPlanData
          const updatedRooms = updatedData.rooms.map((r) => {
            if (r.id === room.id) {
              const newPolygon = [...r.floor_polygon];
              // Insert the new corner at the closest point on the edge
              newPolygon.splice(i + 1, 0, { x: result.x, z: result.z });

              const newArea = calculateRoomArea(newPolygon);
              const newDimensions = calculateRoomDimensions(newPolygon);

              console.log(`[Corner Detection] Updated room polygon from ${r.floor_polygon.length} to ${newPolygon.length} corners`);

              return {
                ...r,
                floor_polygon: newPolygon,
                area: newArea,
                width: newDimensions.width,
                height: newDimensions.height,
              };
            }
            return r;
          });

          const totalArea = updatedRooms.reduce((sum, room) => {
            if (room.room_type !== "Wall" && room.room_type !== "Reference" && room.room_type !== "Boundary") {
              return sum + room.area;
            }
            return sum;
          }, 0);

          updatedData = {
            ...updatedData,
            rooms: updatedRooms,
            total_area: parseFloat(totalArea.toFixed(2)),
          };

          cornerAddedThisEndpoint = true;
          console.log(`[Corner Detection] Corner added successfully!`);
          
          // Only add one corner per endpoint (don't check other rooms for this endpoint)
          break;
        }
      }
      
      if (cornerAddedThisEndpoint) break; // If we added a corner for this endpoint, move to next endpoint
    }
  }

  console.log('[Corner Detection] Final room count:', updatedData.rooms.length);
  return updatedData;
};

  const countWallRoomIntersections = (
    wallStart: Point,
    wallEnd: Point,
    roomPolygon: Point[]
  ): number => {
    let intersectionCount = 0;
    const tolerance = 2.0;

    const wallDx = wallEnd.x - wallStart.x;
    const wallDz = wallEnd.z - wallStart.z;
    const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);

    if (wallLength === 0) return 0;

    const wallDirX = wallDx / wallLength;
    const wallDirZ = wallDz / wallLength;

    const extendedStart = {
      x: wallStart.x - wallDirX * tolerance,
      z: wallStart.z - wallDirZ * tolerance,
    };
    const extendedEnd = {
      x: wallEnd.x + wallDirX * tolerance,
      z: wallEnd.z + wallDirZ * tolerance,
    };

    for (let i = 0; i < roomPolygon.length; i++) {
      const edgeStart = roomPolygon[i];
      const edgeEnd = roomPolygon[(i + 1) % roomPolygon.length];

      const intersection = getLineIntersectionPoint(
        extendedStart,
        extendedEnd,
        edgeStart,
        edgeEnd
      );

      if (
        intersection &&
        isPointOnLineSegment(intersection, edgeStart, edgeEnd)
      ) {
        intersectionCount++;
      }
    }

    return intersectionCount;
  };

  const divideRoomWithSnappedWall = (
    roomPolygon: Point[],
    wallStart: Point,
    wallEnd: Point,
    calculateRoomArea: (polygon: Point[]) => number,
    calculateRoomDimensions: (polygon: Point[]) => {
      width: number;
      height: number;
    },
    generateUniqueId: (prefix: string) => string
  ): {
    snappedWallStart: Point;
    snappedWallEnd: Point;
    dividedRooms: Room[];
  } | null => {
    const wallDx = wallEnd.x - wallStart.x;
    const wallDz = wallEnd.z - wallStart.z;
    const wallLength = Math.sqrt(wallDx * wallDx + wallDz * wallDz);

    if (wallLength === 0) return null;

    const wallDirX = wallDx / wallLength;
    const wallDirZ = wallDz / wallLength;
    const intersections: Array<{
      point: Point;
      edgeIndex: number;
      distance: number;
    }> = [];

    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    roomPolygon.forEach((point) => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minZ = Math.min(minZ, point.z);
      maxZ = Math.max(maxZ, point.z);
    });

    const roomDiagonal = Math.sqrt(
      Math.pow(maxX - minX, 2) + Math.pow(maxZ - minZ, 2)
    );
    const extensionLength = roomDiagonal * 2;

    const extendedStart = {
      x: wallStart.x - wallDirX * extensionLength,
      z: wallStart.z - wallDirZ * extensionLength,
    };
    const extendedEnd = {
      x: wallStart.x + wallDirX * extensionLength,
      z: wallStart.z + wallDirZ * extensionLength,
    };

    const wallCenterX = (wallStart.x + wallEnd.x) / 2;
    const wallCenterZ = (wallStart.z + wallEnd.z) / 2;

    const useWallCenter = wallLength < 10;
    const referenceX = useWallCenter ? wallCenterX : wallStart.x;
    const referenceZ = useWallCenter ? wallCenterZ : wallStart.z;

    for (let i = 0; i < roomPolygon.length; i++) {
      const edgeStart = roomPolygon[i];
      const edgeEnd = roomPolygon[(i + 1) % roomPolygon.length];

      const intersection = getLineIntersectionPoint(
        extendedStart,
        extendedEnd,
        edgeStart,
        edgeEnd
      );
      if (
        intersection &&
        isPointOnLineSegment(intersection, edgeStart, edgeEnd)
      ) {
        const distanceX = intersection.x - referenceX;
        const distanceZ = intersection.z - referenceZ;
        const distance = distanceX * wallDirX + distanceZ * wallDirZ;

        intersections.push({ point: intersection, edgeIndex: i, distance });
      }
    }

    if (intersections.length < 2) return null;

    intersections.sort((a, b) => a.distance - b.distance);

    let entryIntersection: any;
    let exitIntersection: any;

    if (intersections.length === 2) {
      entryIntersection = intersections[0];
      exitIntersection = intersections[1];
    } else if (intersections.length > 2) {
      const wallCenterX = (wallStart.x + wallEnd.x) / 2;
      const wallCenterZ = (wallStart.z + wallEnd.z) / 2;

      let closestToCenter = intersections[0];
      let minDistanceToCenter = Infinity;

      intersections.forEach((intersection) => {
        const distToCenter = Math.sqrt(
          Math.pow(intersection.point.x - wallCenterX, 2) +
          Math.pow(intersection.point.z - wallCenterZ, 2)
        );
        if (distToCenter < minDistanceToCenter) {
          minDistanceToCenter = distToCenter;
          closestToCenter = intersection;
        }
      });

      const centerDistance = closestToCenter.distance;

      let beforeCenter = null;
      let afterCenter = null;

      for (const intersection of intersections) {
        if (intersection.distance < centerDistance) {
          if (!beforeCenter || intersection.distance > beforeCenter.distance) {
            beforeCenter = intersection;
          }
        } else if (intersection.distance > centerDistance) {
          if (!afterCenter || intersection.distance < afterCenter.distance) {
            afterCenter = intersection;
          }
        }
      }

      entryIntersection = beforeCenter || intersections[0];
      exitIntersection = afterCenter || intersections[intersections.length - 1];

      if (entryIntersection === exitIntersection) {
        entryIntersection = intersections[0];
        exitIntersection = intersections[intersections.length - 1];
      }
    } else {
      entryIntersection = intersections[0];
      exitIntersection = intersections[intersections.length - 1];
    }

    const polygon1: Point[] = [];
    const polygon2: Point[] = [];

    polygon1.push({ ...entryIntersection.point });
    let currentIndex = (entryIntersection.edgeIndex + 1) % roomPolygon.length;
    while (
      currentIndex !==
      (exitIntersection.edgeIndex + 1) % roomPolygon.length
    ) {
      polygon1.push({ ...roomPolygon[currentIndex] });
      currentIndex = (currentIndex + 1) % roomPolygon.length;
    }
    polygon1.push({ ...exitIntersection.point });

    polygon2.push({ ...entryIntersection.point });
    currentIndex = entryIntersection.edgeIndex;
    while (currentIndex !== exitIntersection.edgeIndex) {
      polygon2.push({ ...roomPolygon[currentIndex] });
      currentIndex =
        (currentIndex - 1 + roomPolygon.length) % roomPolygon.length;
    }
    polygon2.push({ ...exitIntersection.point });

    const dividedRooms: Room[] = [];
    [polygon1, polygon2].forEach((polygon) => {
      if (polygon.length >= 3) {
        const area = calculateRoomArea(polygon);
        if (area > 0.1) {
          const dimensions = calculateRoomDimensions(polygon);
          dividedRooms.push({
            id: generateUniqueId("room"),
            room_type: "",
            area,
            height: dimensions.height,
            width: dimensions.width,
            floor_polygon: polygon,
            is_regular: 1,
          });
        }
      }
    });

    return dividedRooms.length === 2
      ? {
        snappedWallStart: entryIntersection.point,
        snappedWallEnd: exitIntersection.point,
        dividedRooms,
      }
      : null;
  };

  const straightenWallIfNeeded = (
    start: Point,
    end: Point,
    toleranceAngle: number = 10
  ) => {
    const dx = end.x - start.x;
    const dz = end.z - start.z;

    const angle = Math.atan2(dz, dx) * (180 / Math.PI);
    const normalizedAngle = ((angle % 180) + 180) % 180;

    if (
      normalizedAngle <= toleranceAngle ||
      normalizedAngle >= 180 - toleranceAngle
    ) {
      return {
        start: start,
        end: { x: end.x, z: start.z },
      };
    }

    if (Math.abs(normalizedAngle - 90) <= toleranceAngle) {
      return {
        start: start,
        end: { x: start.x, z: end.z },
      };
    }

    if (Math.abs(normalizedAngle - 45) <= toleranceAngle) {
      const length = Math.sqrt(dx * dx + dz * dz);
      const diagLength = length / Math.sqrt(2);
      return {
        start: start,
        end: {
          x: start.x + (dx > 0 ? diagLength : -diagLength),
          z: start.z + (dz > 0 ? diagLength : -diagLength),
        },
      };
    }

    if (Math.abs(normalizedAngle - 135) <= toleranceAngle) {
      const length = Math.sqrt(dx * dx + dz * dz);
      const diagLength = length / Math.sqrt(2);
      return {
        start: start,
        end: {
          x: start.x + (dx > 0 ? diagLength : -diagLength),
          z: start.z + (dz > 0 ? diagLength : -diagLength),
        },
      };
    }

    return { start, end };
  };

  const findBestRoomToDivide = (
    wallStart: Point,
    wallEnd: Point,
    floorPlanData: FloorPlanData,
    roomRotations: { [key: string]: number }
  ): Room | null => {
    const candidateRooms = floorPlanData.rooms.filter((room: Room) => {
      if (room.room_type === "Wall" || room.floor_polygon.length < 3)
        return false;

      const roomRotation = roomRotations[room.id] || 0;
      if (roomRotation !== 0) return false;

      const intersectionCount = countWallRoomIntersections(
        wallStart,
        wallEnd,
        room.floor_polygon
      );

      return intersectionCount >= 2;
    });

    if (candidateRooms.length === 0) return null;

    if (candidateRooms.length > 1) {
      const wallMidpoint = {
        x: (wallStart.x + wallEnd.x) / 2,
        z: (wallStart.z + wallEnd.z) / 2,
      };

      let bestRoom = candidateRooms[0];
      let minDistanceToCenter = Infinity;

      for (const room of candidateRooms) {
        const roomCenter = {
          x:
            room.floor_polygon.reduce((sum: number, p: Point) => sum + p.x, 0) /
            room.floor_polygon.length,
          z:
            room.floor_polygon.reduce((sum: number, p: Point) => sum + p.z, 0) /
            room.floor_polygon.length,
        };

        const distance = Math.sqrt(
          Math.pow(wallMidpoint.x - roomCenter.x, 2) +
          Math.pow(wallMidpoint.z - roomCenter.z, 2)
        );

        if (distance < minDistanceToCenter) {
          minDistanceToCenter = distance;
          bestRoom = room;
        }
      }

      return bestRoom;
    }

    return candidateRooms[0];
  };

  const handleWallCreated = (wallPoints: Point[]) => {
    captureStateBeforeChange();

    const start = wallPoints[0];
    const end = wallPoints[wallPoints.length - 1];

    // Skip straightening if user has set a specific angle
    const straightened = predefinedWallAngle !== null 
      ? { start, end }  // Use exact points when angle is specified
      : straightenWallIfNeeded(start, end, 2);  // Apply snapping otherwise
    const straightenedStart = straightened.start;
    const straightenedEnd = straightened.end;

    const simpleLine = [straightenedStart, straightenedEnd];

    const newWall: Room = {
      id: generateUniqueId("wall"),
      room_type: "Wall",
      area: 0,
      height: 0,
      width: drawingWallWidth,
      floor_polygon: simpleLine,
    };

    const roomDivided = divideRoomByWall(
      newWall,
      floorPlanData,
      setFloorPlanData,
      calculateRoomArea,
      calculateRoomDimensions,
      generateUniqueId,
      setHasChanges,
      roomRotations,
      setContextFloorPlanData
    );

    if (!roomDivided) {
      setFloorPlanData((prevData) => {
        const updatedRooms = [...prevData.rooms, newWall];
        const actualRoomCount = updatedRooms.filter(
          (room) => room.room_type !== "Wall" && room.room_type !== "Reference" && room.room_type !== "Boundary"
        ).length;

        const updatedData = {
          ...prevData,
          rooms: updatedRooms,
          room_count: actualRoomCount,
          total_area: prevData.total_area,
        };

        return updatedData;
      });

      setHasChanges(true);
      
      // Check if wall endpoints land on room edges and add corners if needed
      setTimeout(() => {
        setFloorPlanData((currentData) => {
          const addedWall = currentData.rooms.find(r => r.id === newWall.id);
          if (addedWall) {
            console.log('[Corner Detection] Starting corner detection for new wall');
            const dataWithCorners = addCornersForWallEndpoints(
              addedWall,
              currentData,
              calculateRoomArea,
              calculateRoomDimensions
            );
            console.log('[Corner Detection] Returning updated data');
            return dataWithCorners;
          }
          return currentData;
        });
      }, 50);

      checkAndUpdateOverlaps();

      setTimeout(() => {
        console.log('[Room Detection] Starting room detection after wall placement');
        const detectionStartTime = performance.now();
        
        setFloorPlanData((currentData) => {
          // Find the newly added wall (it's the last wall in the rooms array)
          const newWallInData = currentData.rooms
            .filter(room => room.room_type === "Wall")
            .slice(-1)[0];
          
          if (!newWallInData) {
            console.log('[Room Detection] No new wall found, skipping');
            return currentData;
          }

          console.log(`[Room Detection] Processing new wall: ${newWallInData.id}`);
          console.log(`[Room Detection] Total rooms in data: ${currentData.rooms.length}`);

          // Extract all wall segments from existing data
          const allSegments = extractAllWallSegments(currentData.rooms);

          // Create segment for the new wall
          const newWallSegment = {
            id: newWallInData.id,
            start: newWallInData.floor_polygon[0],
            end: newWallInData.floor_polygon[1],
            sourceRoomId: newWallInData.id,
            isRoomWall: false
          };

          // Find ALL possible closed areas that include this new wall
          const allClosedPolygons = findAllClosedAreasWithNewWall(
            newWallSegment, 
            allSegments, 
            calculateRoomArea
          );

          // Select the best one (fewest corners, smallest area)
          const closedPolygon = selectBestPolygon(allClosedPolygons);

          if (closedPolygon) {
            console.log('[Room Detection] Cleaning polygon for overlapping corners...');
            
            // Clean the polygon to handle overlapping corners
            const cleanedResult = cleanPolygonOverlappingCorners(
              closedPolygon.polygon,
              closedPolygon.segmentIds,
              allSegments
            );

            if (!cleanedResult) {
              console.log('[Room Detection] Polygon cleanup resulted in invalid polygon, skipping room creation');
              console.log(`[Room Detection] Total time: ${(performance.now() - detectionStartTime).toFixed(2)}ms`);
              return currentData;
            }

            const polygon = cleanedResult.polygon;
            const usedSegmentIds = cleanedResult.segmentIds;

            console.log(`[Room Detection] Final polygon has ${polygon.length} corners after cleanup`);
            console.log('[Room Detection] Checking if polygon already exists as a room...');

            // Check if this polygon overlaps significantly with an existing room
            const isAlreadyRoom = currentData.rooms.some(room => {
              if (room.room_type === "Wall" || room.room_type === "Reference" || room.room_type === "Boundary") {
                return false;
              }
              
              // Check if polygons are similar (approximately same area and center)
              const existingArea = room.area;
              const newArea = calculateRoomArea(polygon);
              const areaDiff = Math.abs(existingArea - newArea);
              
              if (areaDiff < 1.0) {
                // Check center points
                const existingCenter = {
                  x: room.floor_polygon.reduce((sum, p) => sum + p.x, 0) / room.floor_polygon.length,
                  z: room.floor_polygon.reduce((sum, p) => sum + p.z, 0) / room.floor_polygon.length,
                };
                const newCenter = {
                  x: polygon.reduce((sum, p) => sum + p.x, 0) / polygon.length,
                  z: polygon.reduce((sum, p) => sum + p.z, 0) / polygon.length,
                };
                
                const centerDist = Math.sqrt(
                  Math.pow(existingCenter.x - newCenter.x, 2) + 
                  Math.pow(existingCenter.z - newCenter.z, 2)
                );
                
                if (centerDist < 10.0) {
                  console.log(`[Room Detection] Polygon matches existing room ${room.id}, skipping creation`);
                  return true; // This is likely the same room
                }
              }
              
              return false;
            });

            if (!isAlreadyRoom) {
              console.log('[Room Detection] Creating new room from closed polygon');
              
              const area = calculateRoomArea(polygon);
              const dimensions = calculateRoomDimensions(polygon);

              const newRoom: Room = {
                id: generateUniqueId("room"),
                room_type: "",
                area: area,
                height: dimensions.height,
                width: dimensions.width,
                floor_polygon: polygon,
                is_regular: 1,
              };

              // Only remove standalone walls that were used (not room edges)
              const standaloneWallsToRemove = usedSegmentIds.filter(id => {
                const segment = allSegments.find(s => s.id === id);
                return segment && !segment.isRoomWall;
              });

              console.log(`[Room Detection] Removing ${standaloneWallsToRemove.length} standalone walls that formed the room`);

              const filteredRooms = currentData.rooms.filter(
                (room: Room) =>
                  !(room.room_type === "Wall" && standaloneWallsToRemove.includes(room.id))
              );

              const updatedRooms = [...filteredRooms, newRoom];
              
              const actualRoomCount = updatedRooms.filter(
                (room) => room.room_type !== "Wall" && room.room_type !== "Reference" && room.room_type !== "Boundary"
              ).length;

              let updatedRoomTypes = currentData.room_types ? [...currentData.room_types] : [];
              if (!updatedRoomTypes.includes("SecondRoom")) {
                updatedRoomTypes.push("SecondRoom");
              }

              const totalArea = calculateTotalArea(updatedRooms, roomRotations);

              console.log(`[Room Detection] Room created successfully! ID: ${newRoom.id}, Area: ${area.toFixed(2)}`);
              console.log(`[Room Detection] Total time: ${(performance.now() - detectionStartTime).toFixed(2)}ms`);

              return {
                ...currentData,
                rooms: updatedRooms,
                room_count: actualRoomCount,
                room_types: updatedRoomTypes,
                total_area: parseFloat(totalArea.toFixed(2)),
              };
            } else {
              console.log('[Room Detection] Polygon already exists, no room created');
            }
          } else {
            console.log('[Room Detection] No closed polygon found');
          }

          console.log(`[Room Detection] Total time: ${(performance.now() - detectionStartTime).toFixed(2)}ms`);
          return currentData;
        });
      }, 150); // Increased delay to 150ms to allow corner addition to complete first
    }
  };

  const checkWallModificationAndFormRooms = () => {
    //////////////////////////////////////
    if (isModifyingWall) {
      return;
    }
    setTimeout(() => {
      setFloorPlanData((currentData) => {
        let hasChanges = false;
        let updatedData = { ...currentData };

        const wallRooms = currentData.rooms.filter(
          (room: Room) => room.room_type === "Wall"
        );

        wallRooms.forEach((wall) => {
          if (wall.floor_polygon.length === 2) {
            const wallStart = wall.floor_polygon[0];
            const wallEnd = wall.floor_polygon[1];

            const roomsToCheck = currentData.rooms.filter((room: Room) => {
              if (room.room_type === "Wall" || room.floor_polygon.length < 3)
                return false;
              const roomRotation = roomRotations[room.id] || 0;
              if (roomRotation !== 0) return false;

              const intersectionCount = countWallRoomIntersections(
                wallStart,
                wallEnd,
                room.floor_polygon
              );
              return intersectionCount >= 2;
            });

            if (roomsToCheck.length > 0) {
              const roomToDivide = roomsToCheck[0];

              const result = divideRoomWithSnappedWall(
                roomToDivide.floor_polygon,
                wallStart,
                wallEnd,
                calculateRoomArea,
                calculateRoomDimensions,
                generateUniqueId
              );

              if (result && result.dividedRooms.length === 2) {
                updatedData.rooms = updatedData.rooms.filter(
                  (r: Room) => r.id !== roomToDivide.id && r.id !== wall.id
                );

                updatedData.rooms.push(...result.dividedRooms);

                hasChanges = true;
              }
            }
          }
        });

        if (!hasChanges) {
          const remainingWallRooms = updatedData.rooms.filter(
            (room: Room) => room.room_type === "Wall"
          );

          if (remainingWallRooms.length >= 3) {
            const connectedGroups = findConnectedWallGroups(remainingWallRooms);

            connectedGroups.forEach((group) => {
              if (group.length >= 3) {
                const closedPolygon = attemptToFormClosedPolygon(group);
                if (closedPolygon) {
                  const polygon = closedPolygon.polygon;
                  const usedWallIds = closedPolygon.walls;
                  const area = calculateRoomArea(polygon);
                  const dimensions = calculateRoomDimensions(polygon);

                  const newRoom: Room = {
                    id: generateUniqueId("room"),
                    room_type: "",
                    area: area,
                    height: dimensions.height,
                    width: dimensions.width,
                    floor_polygon: polygon,
                    is_regular: 1,
                  };

                  const filteredRooms = updatedData.rooms.filter(
                    (room: Room) =>
                      !(
                        room.room_type === "Wall" &&
                        usedWallIds.includes(room.id)
                      )
                  );
                  filteredRooms.push(newRoom);

                  const totalArea = filteredRooms.reduce(
                    (sum: number, room: Room) => {
                      if (room.room_type !== "Wall") return sum + room.area;
                      return sum;
                    },
                    0
                  );

                  const actualRoomCount = filteredRooms.filter(
                    (room: Room) => room.room_type !== "Wall" && room.room_type !== "Reference" && room.room_type !== "Boundary"
                  ).length;

                  let updatedRoomTypes = updatedData.room_types ? [...updatedData.room_types] : [];
                  if (!updatedRoomTypes.includes("SecondRoom")) {
                    updatedRoomTypes.push("SecondRoom");
                  }

                  updatedData = {
                    ...updatedData,
                    rooms: filteredRooms,
                    room_count: actualRoomCount,
                    room_types: updatedRoomTypes,
                    total_area: parseFloat(totalArea.toFixed(2)),
                  };

                  hasChanges = true;
                }
              }
            });
          }
        }

        if (hasChanges) {
          setTimeout(() => {
            updateContextFloorPlanData(updatedData);
          }, 0);
          setHasChanges(true);
        }

        return updatedData;
      });
    }, 150);
  };

  checkWallModificationRef.current = checkWallModificationAndFormRooms;

  // Wall drawing event handlers (similar to dimension drawing)
  const [isWallDrawing, setIsWallDrawing] = useState(false);
  const [wallStartPoint, setWallStartPoint] = useState<Point | null>(null);
  const [wallCurrentPoint, setWallCurrentPoint] = useState<Point | null>(null);
  const [alignmentGuides, setAlignmentGuides] = useState<{
    horizontal: number | null;
    vertical: number | null;
  }>({ horizontal: null, vertical: null });
  const [movingWallId, setMovingWallId] = useState<string | null>(null);
  const [tempWallLength, setTempWallLength] = useState<string>('');
  const [editingWallId, setEditingWallId] = useState<string | null>(null);
  const [editingWallLength, setEditingWallLength] = useState<string>('');

  // Detect wall movement/resizing alignment for visual guides
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Check if we're dragging a wall (moving entire wall)
      const isDraggingWall = dragState.active && 
                            dragState.roomId && 
                            !dragState.isResizing && 
                            !dragState.isEdgeResizing;
      
      // Check if we're resizing a wall (moving endpoint)
      const isResizingWall = dragState.active && 
                             dragState.roomId && 
                             dragState.isResizing &&
                             dragState.vertexIndex !== null;
      
      if ((!isDraggingWall && !isResizingWall) || !svgRef.current) {
        if (alignmentGuides.horizontal !== null || alignmentGuides.vertical !== null) {
          setAlignmentGuides({ horizontal: null, vertical: null });
        }
        return;
      }

      const room = floorPlanData.rooms.find(r => r.id === dragState.roomId);
      if (!room || room.room_type !== "Wall" || room.floor_polygon.length !== 2) {
        return;
      }

      const SNAP_THRESHOLD = 15;
      let foundHorizontalAlign: number | null = null;
      let foundVerticalAlign: number | null = null;

      // Get all OTHER walls (not the one being moved)
      const existingWalls = floorPlanData.rooms.filter(
        r => r.room_type === "Wall" && 
             r.floor_polygon.length === 2 && 
             r.id !== dragState.roomId
      );

      // For resizing, we need to check the cursor position, not the current wall endpoints
      let pointsToCheck: Point[] = [];
      
      if (isResizingWall && dragState.vertexIndex !== null) {
        // Get current cursor position
        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const cursorPos = reverseTransformCoordinates(mouseX, mouseY);
        
        // Check the cursor position (where the vertex will be)
        pointsToCheck = [cursorPos];
      } else {
        // For moving, check current wall endpoints
        pointsToCheck = room.floor_polygon;
      }

      // Calculate current wall angle (or projected angle for resizing)
      let wallDx, wallDz;
      if (isResizingWall && dragState.vertexIndex !== null) {
        const otherIdx = dragState.vertexIndex === 0 ? 1 : 0;
        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const cursorPos = reverseTransformCoordinates(mouseX, mouseY);
        
        wallDx = cursorPos.x - room.floor_polygon[otherIdx].x;
        wallDz = cursorPos.z - room.floor_polygon[otherIdx].z;
      } else {
        wallDx = room.floor_polygon[1].x - room.floor_polygon[0].x;
        wallDz = room.floor_polygon[1].z - room.floor_polygon[0].z;
      }
      

      // Check alignment with other walls

      // Calculate current wall angle (for parallel detection)
      const currentWallAngle = Math.atan2(wallDz, wallDx);
      const ANGLE_TOLERANCE = 5 * Math.PI / 180; // 5 degrees tolerance for parallel detection

      // Check alignment with other standalone walls
      existingWalls.forEach(wall => {
        // Check points against both endpoints of existing walls
        pointsToCheck.forEach((point) => {
          wall.floor_polygon.forEach(otherPoint => {
            // Horizontal alignment
            if (Math.abs(point.z - otherPoint.z) < SNAP_THRESHOLD) {
              foundHorizontalAlign = otherPoint.z;
            }
            // Vertical alignment  
            if (Math.abs(point.x - otherPoint.x) < SNAP_THRESHOLD) {
              foundVerticalAlign = otherPoint.x;
            }
          });
        });
      });

      // Check alignment with room walls (edges between consecutive polygon points)
      const rooms = floorPlanData.rooms.filter(
        r => r.room_type !== "Wall" && 
             r.room_type !== "Reference" && 
             r.floor_polygon.length >= 3
      );
      
      rooms.forEach(roomObj => {
        // Check each edge of the room
        for (let i = 0; i < roomObj.floor_polygon.length; i++) {
          const edgeStart = roomObj.floor_polygon[i];
          const edgeEnd = roomObj.floor_polygon[(i + 1) % roomObj.floor_polygon.length];
          
          // Calculate edge angle
          const edgeDx = edgeEnd.x - edgeStart.x;
          const edgeDz = edgeEnd.z - edgeStart.z;
          const edgeAngle = Math.atan2(edgeDz, edgeDx);
          
          // Check if current wall is parallel to this room edge
          let angleDiff = Math.abs(currentWallAngle - edgeAngle);
          // Normalize angle difference to [0, PI]
          if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
          
          // Also check if walls are parallel but in opposite direction
          const isParallel = angleDiff < ANGLE_TOLERANCE || 
                            Math.abs(angleDiff - Math.PI) < ANGLE_TOLERANCE;
          
          if (isParallel) {
            // Check alignment for all points being moved/resized
            pointsToCheck.forEach((point) => {
              // Horizontal alignment (same Z coordinate)
              if (Math.abs(point.z - edgeStart.z) < SNAP_THRESHOLD) {
                foundHorizontalAlign = edgeStart.z;
              } else if (Math.abs(point.z - edgeEnd.z) < SNAP_THRESHOLD) {
                foundHorizontalAlign = edgeEnd.z;
              }
              
              // Vertical alignment (same X coordinate)
              if (Math.abs(point.x - edgeStart.x) < SNAP_THRESHOLD) {
                foundVerticalAlign = edgeStart.x;
              } else if (Math.abs(point.x - edgeEnd.x) < SNAP_THRESHOLD) {
                foundVerticalAlign = edgeEnd.x;
              }
            });
          }
        }
      });

      setAlignmentGuides({
        horizontal: foundHorizontalAlign,
        vertical: foundVerticalAlign,
      });
    };

    const handleGlobalMouseUp = () => {
      setAlignmentGuides({ horizontal: null, vertical: null });
      setMovingWallId(null);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [dragState, floorPlanData.rooms, reverseTransformCoordinates, alignmentGuides]);

  // Keyboard handler for wall length input during drag-to-draw
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle when in drag-to-draw mode (not fixed length) and actively drawing
      if (!isWallDrawing || predefinedWallLength !== null) {
        return;
      }

      // Handle numeric input (0-9) and decimal point
      if ((e.key >= '0' && e.key <= '9') || e.key === '.') {
        e.preventDefault();
        const newLength = tempWallLength + e.key;
        setTempWallLength(newLength);
        
        // Instantly update the wall endpoint
        const inputValue = parseFloat(newLength);
        if (!isNaN(inputValue) && inputValue > 0 && wallStartPoint && wallCurrentPoint) {
          const dx = wallCurrentPoint.x - wallStartPoint.x;
          const dz = wallCurrentPoint.z - wallStartPoint.z;
          const angle = Math.atan2(dz, dx);
          
          // Convert input based on unit system
          let length;
          if (unitSystem === 'metric') {
            // Input is in cm (multiples of 100cm), convert to coordinate units
            // inputValue represents centimeters
            // First convert cm to inches: cm / 2.54
            // Then apply the coordinate scaling: inches / 1.2
            length = (inputValue / 2.54) / 1.2;
          } else {
            // Input is in inches
            const split = newLength.split('.');
            length = ((split[0] ? parseFloat(split[0]) * 12 : 0) +(split[1] ? parseFloat(split[1]) : 0)) / 1.2;
          }
          
          const newEndPoint = {
            x: wallStartPoint.x + Math.cos(angle) * length,
            z: wallStartPoint.z + Math.sin(angle) * length,
          };
          
          setWallCurrentPoint(newEndPoint);
        }
      }
      // Handle backspace
      else if (e.key === 'Backspace') {
        e.preventDefault();
        const newLength = tempWallLength.slice(0, -1);
        setTempWallLength(newLength);
        
        // Instantly update the wall endpoint
        if (newLength !== '') {
          const inputValue = parseFloat(newLength);
          if (!isNaN(inputValue) && inputValue > 0 && wallStartPoint && wallCurrentPoint) {
            const dx = wallCurrentPoint.x - wallStartPoint.x;
            const dz = wallCurrentPoint.z - wallStartPoint.z;
            const angle = Math.atan2(dz, dx);
            
            // Convert input based on unit system
            let length;
            if (unitSystem === 'metric') {
              // Input is in cm, convert to coordinate units
              length = (inputValue / 2.54) / 1.2;
            } else {
              // Input is in inches
              const split = newLength.split('.');
              length = ((split[0] ? parseFloat(split[0]) * 12 : 0) +(split[1] ? parseFloat(split[1]) : 0)) / 1.2; 
            }
            
            const newEndPoint = {
              x: wallStartPoint.x + Math.cos(angle) * length,
              z: wallStartPoint.z + Math.sin(angle) * length,
            };
            
            setWallCurrentPoint(newEndPoint);
          }
        }
      }
      // Handle Enter - just kept for consistency, no need to update again
      else if (e.key === 'Enter' && tempWallLength !== '') {
        e.preventDefault();
        // Already updated on each keystroke, nothing more to do
      }
      // Handle Escape to clear input
      else if (e.key === 'Escape') {
        e.preventDefault();
        setTempWallLength('');
      }
    };

    if (isWallDrawing && predefinedWallLength === null) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isWallDrawing, predefinedWallLength, tempWallLength, wallStartPoint, wallCurrentPoint, unitSystem]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCustomRoomDrawing) {
        e.preventDefault();
        // Reset all wall drawing state
        setIsWallDrawing(false);
        setWallStartPoint(null);
        setWallCurrentPoint(null);
        setIsCustomRoomDrawing(false);
        setActiveBuildTool(null);
        setFirstWall(true);
      }
      if (e.key === 'Backspace' && isCustomRoomDrawing && isWallDrawing) {
        e.preventDefault();
        // Reset all wall drawing state
        setIsWallDrawing(false);
        setWallStartPoint(null);
        setWallCurrentPoint(null);
        setIsCustomRoomDrawing(false);
        setActiveBuildTool(null);
        setFirstWall(true);
      }
    };

    if (isCustomRoomDrawing) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCustomRoomDrawing, isWallDrawing, setActiveBuildTool, setIsCustomRoomDrawing]);

  // Keyboard handler for editing selected wall length
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle when a wall is selected
      if (selectedRoomIds.length !== 1) {
        return;
      }

      const selectedRoom = floorPlanData.rooms.find(r => r.id === selectedRoomIds[0]);
      if (!selectedRoom || selectedRoom.room_type !== 'Wall' || selectedRoom.floor_polygon.length !== 2) {
        return;
      }

      // Start editing mode if user types a number
      if ((e.key >= '0' && e.key <= '9') || e.key === '.') {
        e.preventDefault();
        const newLength = editingWallLength + e.key;
        setEditingWallLength(newLength);
        setEditingWallId(selectedRoom.id);
      }
      // Handle backspace
      else if (e.key === 'Backspace' && editingWallId === selectedRoom.id) {
        e.preventDefault();
        setEditingWallLength(prev => prev.slice(0, -1));
      }
      // Handle Enter to apply the length
      else if (e.key === 'Enter' && editingWallLength !== '' && editingWallId === selectedRoom.id) {
        e.preventDefault();
        const inputValue = parseFloat(editingWallLength);
        if (!isNaN(inputValue) && inputValue > 0) {
          // Apply the new length
          const start = selectedRoom.floor_polygon[0];
          const end = selectedRoom.floor_polygon[1];
          
          // Calculate current angle
          const dx = end.x - start.x;
          const dz = end.z - start.z;
          const angle = Math.atan2(dz, dx);
          
          // Convert input based on unit system to coordinate units
          let length;
          if (unitSystem === 'metric') {
            // Input is in cm, convert to coordinate units
            // cm -> inches -> coordinate units
            length = (inputValue / 2.54) / 1.2;
          } else {
            // Input is in inches, convert to coordinate units
            const split = editingWallLength.split('.');
            length = ((split[0] ? parseFloat(split[0]) * 12 : 0) +(split[1] ? parseFloat(split[1]) : 0)) / 1.2; 
          }
          
          // Create new endpoint at the specified length
          const newEnd = {
            x: start.x + Math.cos(angle) * length,
            z: start.z + Math.sin(angle) * length,
          };
          
          if (!hasChanges) {
            captureOriginalState();
          }
          captureStateBeforeChange();
          
          // Update the wall
          setFloorPlanData(prevData => {
            const updatedRooms = prevData.rooms.map(room => {
              if (room.id === selectedRoom.id) {
                return {
                  ...room,
                  floor_polygon: [start, newEnd]
                };
              }
              return room;
            });
            
            return {
              ...prevData,
              rooms: updatedRooms
            };
          });
          
          setHasChanges(true);
        }
        
        // Clear editing state
        setEditingWallLength('');
        setEditingWallId(null);
      }
      // Handle Escape to clear input
      else if (e.key === 'Escape' && editingWallId === selectedRoom.id) {
        e.preventDefault();
        setEditingWallLength('');
        setEditingWallId(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRoomIds, floorPlanData.rooms, editingWallLength, editingWallId, hasChanges, unitSystem]);

  // Clear editing state when selection changes
  useEffect(() => {
    setEditingWallLength('');
    setEditingWallId(null);
  }, [selectedRoomIds]);


  useEffect(() => {
    const handleWallMouseDown = (e: CustomEvent) => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.detail.clientX - rect.left;
        const y = e.detail.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);

        // Check for nearby wall nodes to snap to
        const SNAP_DISTANCE = 15; // Snap within 15 units
        let snappedStartPoint: Point | null = null;

        // Get all existing walls
        const existingWalls = floorPlanData.rooms.filter(
          room => room.room_type === "Wall" && room.floor_polygon.length === 2
        );

        // Check all wall endpoints for proximity
        for (const wall of existingWalls) {
          for (const point of wall.floor_polygon) {
            const distance = Math.sqrt(
              Math.pow(point.x - floorPlanCoords.x, 2) +
              Math.pow(point.z - floorPlanCoords.z, 2)
            );
            
            if (distance < SNAP_DISTANCE) {
              snappedStartPoint = { x: point.x, z: point.z };
              break;
            }
          }
          if (snappedStartPoint) break;
        }

        // If not snapped to wall node, check for nearby room corners to snap to
        if (!snappedStartPoint) {
          // Get all existing rooms (exclude walls and boundaries)
          const existingRooms = floorPlanData.rooms.filter(
            room => room.room_type !== "Wall" && room.room_type !== "Boundary"
          );
          
          // Check all room corners for proximity
          for (const room of existingRooms) {
            for (const corner of room.floor_polygon) {
              const distance = Math.sqrt(
                Math.pow(corner.x - floorPlanCoords.x, 2) +
                Math.pow(corner.z - floorPlanCoords.z, 2)
              );
              
              if (distance < SNAP_DISTANCE) {
                snappedStartPoint = { x: corner.x, z: corner.z };
                break;
              }
            }
            if (snappedStartPoint) break;
          }
        }

        let startPoint = { x: floorPlanCoords.x, z: floorPlanCoords.z };
        
        if (isCustomRoomDrawing && floorPlanData.rooms.length > 0 && !firstWall) {
          // Find the last drawn wall
          const walls = floorPlanData.rooms.filter(r => r.room_type === "Wall");
          if (walls.length > 0) {
            const lastWall = walls[walls.length - 1];
            if (lastWall.floor_polygon.length === 2) {
              // Use the endpoint of the last wall as the starting point
              startPoint = { x: lastWall.floor_polygon[1].x, z: lastWall.floor_polygon[1].z };
            }
          }
        } else if (snappedStartPoint) {
          startPoint = snappedStartPoint;
        }
        setFirstWall(false);

        // Check if we're in fixed-length mode
        if (predefinedWallLength !== null && predefinedWallLength > 0) {
          // Fixed-length mode: click-drag-release (like freehand)
          // Mouse down just sets the start point
          setIsWallDrawing(true);
          setWallStartPoint(startPoint);
          setWallCurrentPoint(startPoint);
        } else {
          // Freehand/drag-to-draw mode: start from snapped point if available
          setIsWallDrawing(true);
          setWallStartPoint(startPoint);
          setWallCurrentPoint(startPoint);
          setTempWallLength(''); // Clear any previous length input
        }
      }
    };

    const handleWallMouseMove = (e: CustomEvent) => {
      if (isWallDrawing && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.detail.clientX - rect.left;
        const y = e.detail.clientY - rect.top;

        const floorPlanCoords = reverseTransformCoordinates(x, y);
        
        // Find alignment with existing walls
        const SNAP_THRESHOLD = 15; // Snap within 15 units
        let snappedPoint = { ...floorPlanCoords };
        let foundHorizontalAlign: number | null = null;
        let foundVerticalAlign: number | null = null;
        
        // Get all existing walls
        const existingWalls = floorPlanData.rooms.filter(
          room => room.room_type === "Wall" && room.floor_polygon.length === 2
        );
        
        // First, check if endpoint is near any wall node for snapping
        let snappedToNode = false;
// In the handleWallMouseMove function, replace the alignment checking section (lines 5577-5603) with:

        // If not snapped to a node, check alignment with wall endpoints and room walls
        if (!snappedToNode && wallStartPoint) {
          // Calculate the angle of the current wall being drawn
          const currentWallDx = floorPlanCoords.x - wallStartPoint.x;
          const currentWallDz = floorPlanCoords.z - wallStartPoint.z;
          const currentWallAngle = Math.atan2(currentWallDz, currentWallDx);
          
          const ANGLE_TOLERANCE = 5 * Math.PI / 180; // 5 degrees tolerance for parallel detection
          
          // Check alignment with standalone walls
          existingWalls.forEach(wall => {
            const wallStart = wall.floor_polygon[0];
            const wallEnd = wall.floor_polygon[1];
            
            // Check horizontal alignment (same Z coordinate)
            if (Math.abs(floorPlanCoords.z - wallStart.z) < SNAP_THRESHOLD) {
              snappedPoint.z = wallStart.z;
              foundHorizontalAlign = wallStart.z;
            } else if (Math.abs(floorPlanCoords.z - wallEnd.z) < SNAP_THRESHOLD) {
              snappedPoint.z = wallEnd.z;
              foundHorizontalAlign = wallEnd.z;
            }
            
            // Check vertical alignment (same X coordinate)
            if (Math.abs(floorPlanCoords.x - wallStart.x) < SNAP_THRESHOLD) {
              snappedPoint.x = wallStart.x;
              foundVerticalAlign = wallStart.x;
            } else if (Math.abs(floorPlanCoords.x - wallEnd.x) < SNAP_THRESHOLD) {
              snappedPoint.x = wallEnd.x;
              foundVerticalAlign = wallEnd.x;
            }
          });
          
          // Check alignment with room walls (edges between consecutive polygon points)
          const rooms = floorPlanData.rooms.filter(
            room => room.room_type !== "Wall" && 
                    room.room_type !== "Reference" && 
                    room.floor_polygon.length >= 3
          );
          
          rooms.forEach(room => {
            // Check each edge of the room
            for (let i = 0; i < room.floor_polygon.length; i++) {
              const edgeStart = room.floor_polygon[i];
              const edgeEnd = room.floor_polygon[(i + 1) % room.floor_polygon.length];
              
              // Calculate edge angle
              const edgeDx = edgeEnd.x - edgeStart.x;
              const edgeDz = edgeEnd.z - edgeStart.z;
              const edgeAngle = Math.atan2(edgeDz, edgeDx);
              
              // Check if current wall is parallel to this room edge
              let angleDiff = Math.abs(currentWallAngle - edgeAngle);
              // Normalize angle difference to [0, PI]
              if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
              
              // Also check if walls are parallel but in opposite direction
              const isParallel = angleDiff < ANGLE_TOLERANCE || 
                                Math.abs(angleDiff - Math.PI) < ANGLE_TOLERANCE;
              
              if (isParallel) {
                // Check horizontal alignment (same Z coordinate)
                if (Math.abs(floorPlanCoords.z - edgeStart.z) < SNAP_THRESHOLD) {
                  snappedPoint.z = edgeStart.z;
                  foundHorizontalAlign = edgeStart.z;
                } else if (Math.abs(floorPlanCoords.z - edgeEnd.z) < SNAP_THRESHOLD) {
                  snappedPoint.z = edgeEnd.z;
                  foundHorizontalAlign = edgeEnd.z;
                }
                
                // Check vertical alignment (same X coordinate)
                if (Math.abs(floorPlanCoords.x - edgeStart.x) < SNAP_THRESHOLD) {
                  snappedPoint.x = edgeStart.x;
                  foundVerticalAlign = edgeStart.x;
                } else if (Math.abs(floorPlanCoords.x - edgeEnd.x) < SNAP_THRESHOLD) {
                  snappedPoint.x = edgeEnd.x;
                  foundVerticalAlign = edgeEnd.x;
                }
              }
            }
          });
        }
        // If not snapped to a node, check alignment with wall endpoints and detect parallel walls
        if (!snappedToNode) {
          existingWalls.forEach(wall => {
            const wallStart = wall.floor_polygon[0];
            const wallEnd = wall.floor_polygon[1];
            
            // Check if walls are parallel (similar angle)
            if (wallStartPoint) {
              // Check horizontal alignment (same Z coordinate)
              if (Math.abs(floorPlanCoords.z - wallStart.z) < SNAP_THRESHOLD) {
                snappedPoint.z = wallStart.z;
                foundHorizontalAlign = wallStart.z;
              } else if (Math.abs(floorPlanCoords.z - wallEnd.z) < SNAP_THRESHOLD) {
                snappedPoint.z = wallEnd.z;
                foundHorizontalAlign = wallEnd.z;
              }
              
              // Check vertical alignment (same X coordinate)
              if (Math.abs(floorPlanCoords.x - wallStart.x) < SNAP_THRESHOLD) {
                snappedPoint.x = wallStart.x;
                foundVerticalAlign = wallStart.x;
              } else if (Math.abs(floorPlanCoords.x - wallEnd.x) < SNAP_THRESHOLD) {
                snappedPoint.x = wallEnd.x;
                foundVerticalAlign = wallEnd.x;
              }
            }
          });
        }
        
        // Update alignment guides
        setAlignmentGuides({
          horizontal: foundHorizontalAlign,
          vertical: foundVerticalAlign,
        });
        
        // In fixed-length mode, constrain endpoint to predefined length
        if (predefinedWallLength !== null && predefinedWallLength > 0 && wallStartPoint) {
          let angle;
          
          // Use predefined angle if set, otherwise calculate from mouse position
          if (predefinedWallAngle !== null) {
            angle = -(predefinedWallAngle * Math.PI) / 180;
          } else {
            const dx = snappedPoint.x - wallStartPoint.x;
            const dz = snappedPoint.z - wallStartPoint.z;
            angle = Math.atan2(dz, dx);
          }
          
          const constrainedPoint = {
            x: wallStartPoint.x + Math.cos(angle) * predefinedWallLength,
            z: wallStartPoint.z + Math.sin(angle) * predefinedWallLength,
          };
          
          setWallCurrentPoint(constrainedPoint);
        } else if (tempWallLength !== '' && wallStartPoint) {
          // If user has typed a length, constrain to that length
          const inputValue = parseFloat(tempWallLength);
          if (!isNaN(inputValue) && inputValue > 0) {
            const dx = snappedPoint.x - wallStartPoint.x;
            const dz = snappedPoint.z - wallStartPoint.z;
            const angle = Math.atan2(dz, dx);

            let length = 0;
            if (unitSystem === 'metric') {
              // Input is in cm, convert to coordinate units
              length = (inputValue / 2.54) / 1.2;
            } else {
              // Input is in inches, convert to coordinate units
              const split = tempWallLength.split('.');
              length = ((split[0] ? parseFloat(split[0]) * 12 : 0) +(split[1] ? parseFloat(split[1]) : 0)) / 1.2;
            }
            
            const constrainedPoint = {
              x: wallStartPoint.x + Math.cos(angle) * length,
              z: wallStartPoint.z + Math.sin(angle) * length,
            };
            
            setWallCurrentPoint(constrainedPoint);
          } else {
            // Invalid length, use snapped point
            setWallCurrentPoint(snappedPoint);
          }
        } else {
          // Freehand mode - use snapped point
          setWallCurrentPoint(snappedPoint);
        }
      }
    };

    const handleWallMouseUp = (e: CustomEvent) => {
      if (isWallDrawing && wallStartPoint && wallCurrentPoint) {
        // Check if we're in fixed-length mode
        if (predefinedWallLength !== null && predefinedWallLength > 0) {
          // Fixed-length mode: create wall with predefined length
          let angle;
          
          // Use predefined angle if set, otherwise calculate from current point
          if (predefinedWallAngle !== null) {
            angle = -(predefinedWallAngle * Math.PI) / 180;
          } else {
            const dx = wallCurrentPoint.x - wallStartPoint.x;
            const dz = wallCurrentPoint.z - wallStartPoint.z;
            angle = Math.atan2(dz, dx);
          }
          
          const endPoint = {
            x: wallStartPoint.x + Math.cos(angle) * predefinedWallLength,
            z: wallStartPoint.z + Math.sin(angle) * predefinedWallLength,
          };

          if (!hasChanges) {
            captureOriginalState();
          }
          captureStateBeforeChange();

          handleWallCreated([wallStartPoint, endPoint]);
          setHasChanges(true);
        } else {
          // Freehand/drag-to-draw mode: create wall from start to current point
          const distance = Math.sqrt(
            Math.pow(wallCurrentPoint.x - wallStartPoint.x, 2) +
            Math.pow(wallCurrentPoint.z - wallStartPoint.z, 2)
          );

          if (distance > 0.1) {
            if (!hasChanges) {
              captureOriginalState();
            }
            captureStateBeforeChange();

            handleWallCreated([wallStartPoint, wallCurrentPoint]);
            setHasChanges(true);
          }
        }

        setIsWallDrawing(false);
        setWallStartPoint(null);
        setWallCurrentPoint(null);
        setTempWallLength(''); // Clear temp length input
        // Clear alignment guides
        setAlignmentGuides({ horizontal: null, vertical: null });
      }
    };

    window.addEventListener(
      "wallMouseDown",
      handleWallMouseDown as EventListener
    );
    window.addEventListener(
      "wallMouseMove",
      handleWallMouseMove as EventListener
    );
    window.addEventListener(
      "wallMouseUp",
      handleWallMouseUp as EventListener
    );

    return () => {
      window.removeEventListener(
        "wallMouseDown",
        handleWallMouseDown as EventListener
      );
      window.removeEventListener(
        "wallMouseMove",
        handleWallMouseMove as EventListener
      );
      window.removeEventListener(
        "wallMouseUp",
        handleWallMouseUp as EventListener
      );
    };
  }, [
    isWallDrawing,
    wallStartPoint,
    wallCurrentPoint,
    reverseTransformCoordinates,
    handleWallCreated,
    setHasChanges,
    captureStateBeforeChange,
    captureOriginalState,
    hasChanges,
    predefinedWallLength,
  ]);

  const handleRoomCreated = (roomPolygon: Point[]) => {
    captureStateBeforeChange();

    const dimensions = calculateRoomDimensions(roomPolygon);
    const area = calculateRoomArea(roomPolygon);

    const newRoom: Room = {
      id: generateUniqueId("room"),
      room_type: "",
      area: area,
      height: dimensions.height,
      width: dimensions.width,
      floor_polygon: roomPolygon,
      is_regular: 1,
    };

    setFloorPlanData((prevData) => {
      const updatedRooms = [...prevData.rooms, newRoom];
      const totalArea = calculateTotalArea(updatedRooms, roomRotations);

      const actualRoomCount = updatedRooms.filter(
        (room) => room.room_type !== "Wall" && room.room_type !== "Reference" && room.room_type !== "Boundary"
      ).length;

      let updatedRoomTypes = prevData.room_types ? [...prevData.room_types] : [];
      if (!updatedRoomTypes.includes("SecondRoom")) {
        updatedRoomTypes.push("SecondRoom");
      }

      return {
        ...prevData,
        rooms: updatedRooms,
        room_count: actualRoomCount,
        room_types: updatedRoomTypes,
        total_area: parseFloat(totalArea.toFixed(2)),
      };
    });

    setHasChanges(true);
    checkAndUpdateOverlaps();
  };

  const handleBoundaryCreated = (boundaryPolygon: Point[]) => {
    captureStateBeforeChange();

    const dimensions = calculateRoomDimensions(boundaryPolygon);
    const area = calculateRoomArea(boundaryPolygon);

    const newBoundary: Room = {
      id: generateUniqueId("boundary"),
      room_type: "Boundary",
      area: area,
      height: dimensions.height,
      width: dimensions.width,
      floor_polygon: boundaryPolygon,
      is_regular: 1,
      isBoundary: true,
    };

    setFloorPlanData((prevData) => {
      const updatedRooms = [...prevData.rooms, newBoundary];
      const nonBoundaryRooms = updatedRooms.filter(room =>
        !room.isBoundary &&
        room.room_type !== "Wall" &&
        room.room_type !== "Reference" &&
        room.room_type !== "Boundary"
      );
      const totalArea = calculateTotalArea(updatedRooms, roomRotations);

      return {
        ...prevData,
        rooms: updatedRooms,
        room_count: nonBoundaryRooms.length,
        total_area: parseFloat(totalArea.toFixed(2)),
      };
    });

    setHasChanges(true);
    checkAndUpdateOverlaps();
  };

  const renderAllWallSegments = () => {
    if (!showIndividualWalls) return null;

    const allWallSegments: Array<{
      wallId: string;
      roomId: string;
      start: { x: number; y: number };
      end: { x: number; y: number };
      isWallSelected: boolean;
      isRoomSelected: boolean;
      wallWidth: number;
      isExternal: boolean;
      segmentIndex: number;
      roomRotation: number;
      roomCentroid: { x: number; y: number };
    }> = [];

    floorPlanData.rooms.forEach((room) => {
      if (room.room_type === "Wall") return;

      const transformedPoints = room.floor_polygon.map(transformCoordinates);
      if (transformedPoints.length < 3) return;

      const isRoomSelected = selectedRoomIds.includes(room.id);
      const roomCentroid = {
        x:
          transformedPoints.reduce((sum, p) => sum + p.x, 0) /
          transformedPoints.length,
        y:
          transformedPoints.reduce((sum, p) => sum + p.y, 0) /
          transformedPoints.length,
      };
      const roomRotation = roomRotations[room.id] || 0;

      const rotatedPoints = transformedPoints.map((point) => {
        if (roomRotation === 0) return point;

        const radians = (roomRotation * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const dx = point.x - roomCentroid.x;
        const dy = point.y - roomCentroid.y;

        return {
          x: roomCentroid.x + dx * cos - dy * sin,
          y: roomCentroid.y + dx * sin + dy * cos,
        };
      });

      for (let i = 0; i < rotatedPoints.length; i++) {
        const start = rotatedPoints[i];
        const end = rotatedPoints[(i + 1) % rotatedPoints.length];
        const wallId = `${room.id}-wall-${i}`;
        const isWallSelected = selectedRoomIds.includes(wallId);
        const isExternal = isExternalWallSegment(
          room.id,
          i,
          floorPlanData,
          roomRotations
        );
        const baseWidth = wallWidths[wallId] || 4;
        const finalWidth = isExternal ? baseWidth + 3 : baseWidth;

        allWallSegments.push({
          wallId,
          roomId: room.id,
          start,
          end,
          isWallSelected,
          isRoomSelected,
          wallWidth: finalWidth,
          isExternal,
          segmentIndex: i,
          roomRotation,
          roomCentroid,
        });
      }
    });

    const wallsByRoom = new Map<string, typeof allWallSegments>();
    allWallSegments.forEach((wall) => {
      if (!wallsByRoom.has(wall.roomId)) {
        wallsByRoom.set(wall.roomId, []);
      }
      wallsByRoom.get(wall.roomId)!.push(wall);
    });

    const roomWallPolygons = new Map<string, React.ReactElement[]>();

    wallsByRoom.forEach((roomWalls, roomId) => {
      const sortedWalls = roomWalls.sort(
        (a, b) => a.segmentIndex - b.segmentIndex
      );

      const miteredCorners = calculateMiteredCorners(sortedWalls);

      const wallElements = sortedWalls.map((wall, index) => {
        const { wallId, isWallSelected, isRoomSelected, wallWidth } = wall;

        const corners = miteredCorners[index];
        if (!corners) return null;

        const polygonPoints = `${corners.topStart.x},${corners.topStart.y} ${corners.topEnd.x},${corners.topEnd.y} ${corners.bottomEnd.x},${corners.bottomEnd.y} ${corners.bottomStart.x},${corners.bottomStart.y}`;

        return (
          <g key={wallId}>
            {isWallSelected && (
              <polygon
                points={polygonPoints}
                fill="#2196F3"
                fillOpacity="0.3"
                style={{ pointerEvents: "none" }}
              />
            )}

            <polygon
              points={polygonPoints}
              fill="black"
              filter={isWallSelected ? "drop-shadow(0 0 6px #2196F3)" : "none"}
              style={{
                cursor: "pointer",
                pointerEvents: isRoomSelected ? "none" : "auto",
              }}
              onClick={(e) => {
                if (viewOnly) {
                  e.stopPropagation();
                  return;
                }
                e.stopPropagation();
                e.preventDefault();

                if (!isRoomSelected) {
                  const isMultiSelect = e.ctrlKey || e.metaKey;
                  if (isMultiSelect) {
                    setSelectedRoomIds((prev) =>
                      prev.includes(wallId)
                        ? prev.filter((id) => id !== wallId)
                        : [...prev, wallId]
                    );
                  } else {
                    setSelectedRoomIds([wallId]);
                  }
                }
              }}
            />
          </g>
        );
      });

      roomWallPolygons.set(
        roomId,
        wallElements.filter(Boolean) as React.ReactElement[]
      );
    });

    const allWalls: React.ReactElement[] = [];
    roomWallPolygons.forEach((walls, roomId) => {
      allWalls.push(...walls.map((wall, index) =>
        React.cloneElement(wall, { key: `${roomId}-wall-segment-${index}` })
      ));
    });

    return <g key="all-wall-segments">{allWalls}</g>;
  };

  // Helper function to calculate mitered corner for a single wall connection
  function calculateSingleWallMiter(
    wallStart: Point,
    wallEnd: Point,
    wallWidth: number,
    connectedWallStart: Point,
    connectedWallEnd: Point,
    connectedWallWidth: number,
    actualConnectionPoint: Point, // Which point of current wall is at the connection
    miterAtStart: boolean // Explicitly tell which end to miter
  ) {
    // Convert to screen coordinates for calculation
    const startScreen = transformCoordinates(wallStart);
    const endScreen = transformCoordinates(wallEnd);
    const connStartScreen = transformCoordinates(connectedWallStart);
    const connEndScreen = transformCoordinates(connectedWallEnd);
    const actualConnPointScreen = transformCoordinates(actualConnectionPoint);
    
    const screenDx = endScreen.x - startScreen.x;
    const screenDy = endScreen.y - startScreen.y;
    const screenLength = Math.sqrt(screenDx * screenDx + screenDy * screenDy);
    
    if (screenLength === 0) return null;
    
    // Calculate perpendicular for current wall
    const perpX = -screenDy / screenLength;
    const perpY = screenDx / screenLength;
    const halfWidth = wallWidth / 2;
    
    // Default corners
    let topStart = {
      x: startScreen.x + perpX * halfWidth,
      y: startScreen.y + perpY * halfWidth,
    };
    let topEnd = {
      x: endScreen.x + perpX * halfWidth,
      y: endScreen.y + perpY * halfWidth,
    };
    let bottomStart = {
      x: startScreen.x - perpX * halfWidth,
      y: startScreen.y - perpY * halfWidth,
    };
    let bottomEnd = {
      x: endScreen.x - perpX * halfWidth,
      y: endScreen.y - perpY * halfWidth,
    };
    
    // Determine which end of the connected wall is at the connection point
    const distToConnStart = Math.sqrt(
      Math.pow(actualConnPointScreen.x - connStartScreen.x, 2) +
      Math.pow(actualConnPointScreen.y - connStartScreen.y, 2)
    );
    const distToConnEnd = Math.sqrt(
      Math.pow(actualConnPointScreen.x - connEndScreen.x, 2) +
      Math.pow(actualConnPointScreen.y - connEndScreen.y, 2)
    );
    
    // Orient the connected wall so it points away from the connection
    // BUT also consider whether we're at start or end of current wall
    let orientedConnStart, orientedConnEnd;
    
    if (distToConnStart < distToConnEnd) {
      // Connection is at the start of connected wall
      if (miterAtStart) {
        // At start of current wall, reverse connected wall so it points away
        orientedConnStart = connEndScreen;
        orientedConnEnd = connStartScreen;
      } else {
        // At end of current wall, keep normal orientation
        orientedConnStart = connStartScreen;
        orientedConnEnd = connEndScreen;
      }
    } else {
      // Connection is at the end of connected wall
      if (miterAtStart) {
        // At start of current wall, keep normal orientation
        orientedConnStart = connStartScreen;
        orientedConnEnd = connEndScreen;
      } else {
        // At end of current wall, reverse it
        orientedConnStart = connEndScreen;
        orientedConnEnd = connStartScreen;
      }
    }
    
    const connDx = orientedConnEnd.x - orientedConnStart.x;
    const connDy = orientedConnEnd.y - orientedConnStart.y;
    const connLength = Math.sqrt(connDx * connDx + connDy * connDy);
    
    if (connLength > 0) {
      const connPerpX = -connDy / connLength;
      const connPerpY = connDx / connLength;
      const connHalfWidth = connectedWallWidth / 2;
      
      const maxExtension = Math.max(wallWidth, connectedWallWidth) * 5;
      
      if (miterAtStart) {
        // Calculate intersection for top edge at start
        const topIntersection = getLineIntersection(
          {
            x: orientedConnStart.x + connPerpX * connHalfWidth,
            y: orientedConnStart.y + connPerpY * connHalfWidth,
          },
          {
            x: orientedConnEnd.x + connPerpX * connHalfWidth,
            y: orientedConnEnd.y + connPerpY * connHalfWidth,
          },
          topStart,
          topEnd,
          maxExtension
        );
        
        // Calculate intersection for bottom edge at start
        const bottomIntersection = getLineIntersection(
          {
            x: orientedConnStart.x - connPerpX * connHalfWidth,
            y: orientedConnStart.y - connPerpY * connHalfWidth,
          },
          {
            x: orientedConnEnd.x - connPerpX * connHalfWidth,
            y: orientedConnEnd.y - connPerpY * connHalfWidth,
          },
          bottomStart,
          bottomEnd,
          maxExtension
        );
        
        if (topIntersection) topStart = topIntersection;
        if (bottomIntersection) bottomStart = bottomIntersection;
      } else {
        // Calculate intersection for top edge at end
        const topIntersection = getLineIntersection(
          topStart,
          topEnd,
          {
            x: orientedConnStart.x + connPerpX * connHalfWidth,
            y: orientedConnStart.y + connPerpY * connHalfWidth,
          },
          {
            x: orientedConnEnd.x + connPerpX * connHalfWidth,
            y: orientedConnEnd.y + connPerpY * connHalfWidth,
          },
          maxExtension
        );
        
        // Calculate intersection for bottom edge at end
        const bottomIntersection = getLineIntersection(
          bottomStart,
          bottomEnd,
          {
            x: orientedConnStart.x - connPerpX * connHalfWidth,
            y: orientedConnStart.y - connPerpY * connHalfWidth,
          },
          {
            x: orientedConnEnd.x - connPerpX * connHalfWidth,
            y: orientedConnEnd.y - connPerpY * connHalfWidth,
          },
          maxExtension
        );
        
        if (topIntersection) topEnd = topIntersection;
        if (bottomIntersection) bottomEnd = bottomIntersection;
      }
    }
    
    return {
      topStart,
      topEnd,
      bottomStart,
      bottomEnd,
    };
  }

  function calculateMiteredCorners(
    walls: Array<{
      start: { x: number; y: number };
      end: { x: number; y: number };
      wallWidth: number;
    }>
  ) {
    const corners = [];

    for (let i = 0; i < walls.length; i++) {
      const currentWall = walls[i];
      const prevWall = walls[i === 0 ? walls.length - 1 : i - 1];
      const nextWall = walls[(i + 1) % walls.length];

      const { start, end, wallWidth } = currentWall;

      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length === 0) {
        corners.push(null);
        continue;
      }

      const dirX = dx / length;
      const dirY = dy / length;
      const perpX = -dirY;
      const perpY = dirX;
      const halfWidth = wallWidth / 2;

      let topStart = {
        x: start.x + perpX * halfWidth,
        y: start.y + perpY * halfWidth,
      };
      let topEnd = {
        x: end.x + perpX * halfWidth,
        y: end.y + perpY * halfWidth,
      };
      let bottomStart = {
        x: start.x - perpX * halfWidth,
        y: start.y - perpY * halfWidth,
      };
      let bottomEnd = {
        x: end.x - perpX * halfWidth,
        y: end.y - perpY * halfWidth,
      };

      if (prevWall) {
        const prevDx = prevWall.end.x - prevWall.start.x;
        const prevDy = prevWall.end.y - prevWall.start.y;
        const prevLength = Math.sqrt(prevDx * prevDx + prevDy * prevDy);

        if (prevLength > 0) {
          const prevDirX = prevDx / prevLength;
          const prevDirY = prevDy / prevLength;
          const prevPerpX = -prevDirY;
          const prevPerpY = prevDirX;
          const prevHalfWidth = prevWall.wallWidth / 2;

          // Maximum extension is 5x the wall width to prevent extremely long corners
          const maxExtension = Math.max(wallWidth, prevWall.wallWidth) * 5;

          const topIntersection = getLineIntersection(
            {
              x: prevWall.start.x + prevPerpX * prevHalfWidth,
              y: prevWall.start.y + prevPerpY * prevHalfWidth,
            },
            {
              x: prevWall.end.x + prevPerpX * prevHalfWidth,
              y: prevWall.end.y + prevPerpY * prevHalfWidth,
            },
            topStart,
            topEnd,
            maxExtension
          );

          const bottomIntersection = getLineIntersection(
            {
              x: prevWall.start.x - prevPerpX * prevHalfWidth,
              y: prevWall.start.y - prevPerpY * prevHalfWidth,
            },
            {
              x: prevWall.end.x - prevPerpX * prevHalfWidth,
              y: prevWall.end.y - prevPerpY * prevHalfWidth,
            },
            bottomStart,
            bottomEnd,
            maxExtension
          );

          if (topIntersection) topStart = topIntersection;
          if (bottomIntersection) bottomStart = bottomIntersection;
        }
      }

      if (nextWall) {
        const nextDx = nextWall.end.x - nextWall.start.x;
        const nextDy = nextWall.end.y - nextWall.start.y;
        const nextLength = Math.sqrt(nextDx * nextDx + nextDy * nextDy);

        if (nextLength > 0) {
          const nextDirX = nextDx / nextLength;
          const nextDirY = nextDy / nextLength;
          const nextPerpX = -nextDirY;
          const nextPerpY = nextDirX;
          const nextHalfWidth = nextWall.wallWidth / 2;

          // Maximum extension is 5x the wall width to prevent extremely long corners
          const maxExtension = Math.max(wallWidth, nextWall.wallWidth) * 5;

          const topIntersection = getLineIntersection(
            topStart,
            topEnd,
            {
              x: nextWall.start.x + nextPerpX * nextHalfWidth,
              y: nextWall.start.y + nextPerpY * nextHalfWidth,
            },
            {
              x: nextWall.end.x + nextPerpX * nextHalfWidth,
              y: nextWall.end.y + nextPerpY * nextHalfWidth,
            },
            maxExtension
          );

          const bottomIntersection = getLineIntersection(
            bottomStart,
            bottomEnd,
            {
              x: nextWall.start.x - nextPerpX * nextHalfWidth,
              y: nextWall.start.y - nextPerpY * nextHalfWidth,
            },
            {
              x: nextWall.end.x - nextPerpX * nextHalfWidth,
              y: nextWall.end.y - nextPerpY * nextHalfWidth,
            },
            maxExtension
          );

          if (topIntersection) topEnd = topIntersection;
          if (bottomIntersection) bottomEnd = bottomIntersection;
        }
      }

      corners.push({
        topStart,
        topEnd,
        bottomStart,
        bottomEnd,
      });
    }

    return corners;
  }

function getLineIntersection(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number },
    p4: { x: number; y: number },
    maxExtension?: number
  ) {
    const denom = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);

    // Lines are parallel or nearly parallel
    if (Math.abs(denom) < 0.0001) return null;

    const t =
      ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / denom;

    const intersection = {
      x: p1.x + t * (p2.x - p1.x),
      y: p1.y + t * (p2.y - p1.y),
    };

    // If maxExtension is provided, check if the intersection is too far from p2
    // (which is typically the corner point we're extending from)
    if (maxExtension !== undefined) {
      const distanceFromCorner = Math.sqrt(
        Math.pow(intersection.x - p2.x, 2) + Math.pow(intersection.y - p2.y, 2)
      );
      
      // If intersection is too far, return null to fall back to simple corner
      if (distanceFromCorner > maxExtension) {
        return null;
      }
    }

    return intersection;
  }

  const renderRoomWalls = (room: any) => {
    return null;
  };

  const getRoomColor = (roomType: string, roomId?: string) => {
    if (roomType === "Wall") {
      return "black";
    }

    // Handle custom texture mode
    if (options.colorScheme === "custom") {
      // If room has a custom texture or color assigned
      if (roomId && options.customRoomTextures && options.customRoomTextures[roomId]) {
        const value = options.customRoomTextures[roomId];
        // Check if it's a color (starts with "color:")
        if (value.startsWith('color:')) {
          return value.substring(6); // Remove "color:" prefix
        }
        // Otherwise it's a texture
        return `url(#texture-${roomId})`;
      }
      // Default color for rooms without texture in custom mode
      return "#D0D0D0";
    }

    const baseColors = "#D0D0D0";

    switch (options.colorScheme) {
      case "monochrome":
        return "#B5DBFF";

      case "pastel":
        const lightenColor = (color: string) => {
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);

          const mixAmount = 0.4;
          const newR = Math.floor(r + (255 - r) * mixAmount);
          const newG = Math.floor(g + (255 - g) * mixAmount);
          const newB = Math.floor(b + (255 - b) * mixAmount);

          return `#${newR.toString(16).padStart(2, "0")}${newG
            .toString(16)
            .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
        };
        return lightenColor(baseColors);

      case "contrast":
        // Load existing maps from localStorage if present
        let contrastMap: { [key: string]: string } = JSON.parse(localStorage.getItem("contrastMap") || "{}");
        let moreColors: string[] = JSON.parse(localStorage.getItem("moreColors") || JSON.stringify([
          "#F7C8E0", "#C5E8E7", "#FFD6A5", "#BDE0FE", "#C8F7C5", "#EAD7F7", "#FFF5BA",
          "#FDC5F5", "#C9F5E9", "#F7D9C4", "#C4E3F7", "#F7EAC8", "#E2C5F7", "#BFFCC6",
          "#FFCBC1", "#C6F0FC", "#F5CFEA", "#FCE2CE", "#D5F6CE", "#D8C9FC", "#F0E4A8",
          "#C1E1DC", "#E8C6FF", "#FFD7D0", "#C6F7F3", "#FAE1B5", "#E4D1F9", "#BFE1B0",
          "#FCD4B2", "#D2E0FB", "#EFC6C4", "#B9F8F4", "#FDE6D9", "#E6F0BD", "#D3E8F8"
        ]));

        // Initialize default colors if they aren't already stored
        const defaultContrastMap: { [key: string]: string } = {
          LivingRoom: "#FFBDB9",
          Bathroom: "#A0D0F0",
          MasterRoom: "#FFDCC5",
          Kitchen: "#E2CCE2",
          SecondRoom: "#F9DD7D",
          ChildRoom: "#DFBDFF",
          DiningRoom: "#BADEBC",
          Balcony: "#C2E5E2",
          PoojaRoom: "#CDE6F9",
          Wall: "#333333",
        };

        // Merge defaults (without overwriting saved values)
        contrastMap = { ...defaultContrastMap, ...contrastMap };

        // Assign new color if needed
        if (!contrastMap[roomType]) {
          const color = moreColors.pop() || "#000000";
          contrastMap[roomType] = color;
          // Persist both mappings and remaining colors
          localStorage.setItem("contrastMap", JSON.stringify(contrastMap));
          localStorage.setItem("moreColors", JSON.stringify(moreColors));
        }

        return contrastMap[roomType] || "#E8E8E8";

      default:
        return baseColors;
    }
  };

  const sortedRooms = useMemo(() => {
    return [...floorPlanData.rooms]
      .filter(room => room.room_type !== "Reference")
      .sort((a, b) => {
        const aSelected = selectedRoomIds.includes(a.id);
        const bSelected = selectedRoomIds.includes(b.id);
        const aOverlapping = isRoomOverlapping(a.id, overlappingRooms);
        const bOverlapping = isRoomOverlapping(b.id, overlappingRooms);
        const aLastModified = a.id === lastModifiedRoomId;
        const bLastModified = b.id === lastModifiedRoomId;

        if (a.isBoundary && !b.isBoundary) return -1;
        if (!a.isBoundary && b.isBoundary) return 1;

        if (aLastModified && !bLastModified) return 1;
        if (!aLastModified && bLastModified) return -1;

        // CHANGED: Selected rooms render FIRST (at bottom), not last
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        if (aOverlapping && !bOverlapping) return 1;
        if (!aOverlapping && bOverlapping) return -1;

        if (a.room_type === "Wall" && b.room_type !== "Wall") return 1;
        if (a.room_type !== "Wall" && b.room_type === "Wall") return -1;

        return 0;
      });
  }, [
    floorPlanData.rooms,
    selectedRoomIds,
    overlappingRooms,
    lastModifiedRoomId,
  ]);

  const labelStyles = `
  .floor-plan-label.selected-label {
    cursor: move;
  }
  
  .floor-plan-label:not(.selected-label) {
    cursor: pointer;
  }
  
  .floor-plan-label.dragging {
    opacity: 0.7;
  }
`;

  return (
    <div className={`generated-container`} ref={parentRef}>
      {renderOverlapAlert({
        overlappingRooms,
        getOverlappingRoomNames: getOverlappingRoomNamesHelper,
        onIgnore: handleIgnoreOverlaps,
      })}

      <div>
        <style>{floorPlanStyles}</style>
        <style>{wallStyles}</style>
        <style>{labelStyles}</style>

        <div
          ref={floorPlanRef}
          className="floor-plan-container"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: isMobile
              ? "translate(-50%, -50%)"
              : "translate(-50%, -50%)",
            width: `${contentWidth * scale}px`,
            height: `${contentHeight * scale}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onMouseDown={(e) => {
            if (document.body.classList.contains("drag-completed")) {
              e.stopPropagation();
              e.preventDefault();
              return;
            }
          }}
        >
          {options.showMeasurements && floorPlanData.total_area > 0 && (
            <span
              style={{
                position: "absolute",
                top:
                  transformCoordinates({ x: contentBounds.minX, z: contentBounds.minZ }).y - 50,
                left: "50%",
                transform: "translateX(-50%)",
                textAlign: "center",
                marginBottom: "-25px",
                color: "#000",
                fontSize: "small",
                width: "100%",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                pointerEvents: "none",
              }}
              className="always-black-text"
            >
              <span
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  padding: "5px",
                }}
              >
                <b>Total Area:</b> {formatImperialArea(floorPlanData.total_area, unitSystem)}
                &nbsp;|&nbsp; <b>Total Rooms:</b> {floorPlanData.room_count}
              </span>
            </span>
          )}

          <svg
            width="100%"
            height="100%"
            ref={svgRef}
            style={{
              touchAction: isRoomDraggingLocked ? "auto" : "none",

              backgroundColor: "transparent",
              transition: "background-color 0.2s ease",
            }}
            onClick={(e) => {
              // Deselect room on any click (will be stopped by rooms/objects if they're clicked)
              if (selectedRoomId || selectedRoomIds.length > 0) {
                setSelectedRoomId(null);
                setSelectedRoomIds([]);
                if (setContextSelectedRoomIds) {
                  setContextSelectedRoomIds([]);
                }
              }
              // Ensure any interaction flags are cleared
              document.body.removeAttribute("data-room-touch-interaction");
            }}
            onTouchStart={(e) => {
              // On touch, if rooms are selected, clear selection to allow panning
              // This touch will bubble to Playground for panning
              if (selectedRoomId || selectedRoomIds.length > 0) {
                // Only clear if touching empty space (not on a room)
                const target = e.target as SVGElement;
                if (target.tagName === 'svg' || target.tagName === 'g') {
                  setSelectedRoomId(null);
                  setSelectedRoomIds([]);
                  if (setContextSelectedRoomIds) {
                    setContextSelectedRoomIds([]);
                  }
                  // Ensure any interaction flags are cleared
                  document.body.removeAttribute("data-room-touch-interaction");
                }
              }
            }}
          >
            <g
              transform={`rotate(${rotation}, ${(contentWidth * scale) / 2}, ${(contentHeight * scale) / 2
                })`}
            >
              <defs>
                <marker
                  id="arrow"
                  markerWidth="10"
                  markerHeight="10"
                  refX="5"
                  refY="5"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 0 0 L 10 5 L 0 10 Z" fill={"black"} />
                </marker>

                <marker
                  id="arrow1"
                  markerWidth="10"
                  markerHeight="10"
                  refX="5"
                  refY="5"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 10 0 L 0 5 L 10 10 Z" fill={"black"} />
                </marker>

                <marker
                  id="dimensionArrowStart"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 5 0 L 0 3 L 5 6 Z" fill="#000" />
                </marker>

                <marker
                  id="dimensionArrowEnd"
                  markerWidth="6"
                  markerHeight="6"
                  refX="1"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 1 0 L 6 3 L 1 6 Z" fill="#000" />
                </marker>

                {/* Texture patterns for custom mode */}
                {options.colorScheme === 'custom' && options.customRoomTextures && 
                  Object.entries(options.customRoomTextures)
                    .filter(([_, textureId]) => !textureId.startsWith('color:'))
                    .map(([roomId, textureId]) => (
                      <pattern
                        key={`texture-${roomId}`}
                        id={`texture-${roomId}`}
                        patternUnits="userSpaceOnUse"
                        width="160"
                        height="160"
                        patternTransform="scale(1)"
                      >
                        <image
                          href={`/textures/${textureId}.jpg`}
                          x="0"
                          y="0"
                          width="160"
                          height="160"
                          preserveAspectRatio="none"
                        />
                      </pattern>
                    ))
                }
              </defs>

              {/* 
            {coordinateSystem}
            */}


              {options.showMeasurements && floorPlanData.total_area > 0 && (
                <>
                  <line
                    x1={
                      transformCoordinates({
                        x: contentBounds.minX - 10,
                        z: contentBounds.minZ,
                      }).x
                    }
                    y1={
                      transformCoordinates({ x: contentBounds.minX, z: contentBounds.minZ })
                        .y + 2
                    }
                    x2={
                      transformCoordinates({
                        x: contentBounds.minX - 10,
                        z: contentBounds.maxZ,
                      }).x
                    }
                    y2={
                      transformCoordinates({ x: contentBounds.minX, z: contentBounds.maxZ })
                        .y - 2
                    }
                    stroke={"black"}
                    strokeWidth="1"
                    markerStart="url(#arrow1)"
                    markerEnd="url(#arrow)"
                  />
                  <text
                    style={{
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      MozUserSelect: "none",
                      msUserSelect: "none",
                    }}
                    x={
                      transformCoordinates({
                        x: contentBounds.minX - 10,
                        z: (contentBounds.minZ + contentBounds.maxZ) / 2,
                      }).x
                    }
                    y={
                      transformCoordinates({
                        x: contentBounds.minX,
                        z: (contentBounds.minZ + contentBounds.maxZ) / 2,
                      }).y
                    }
                    fontSize="11"
                    fill={"black"}
                    textAnchor="middle"
                    transform={`
        rotate(
          -90,
          ${transformCoordinates({
                      x: contentBounds.minX - 12,
                      z: (contentBounds.minZ + contentBounds.maxZ) / 2,
                    }).x
                      },
          ${transformCoordinates({
                        x: contentBounds.minX - 12,
                        z: (contentBounds.minZ + contentBounds.maxZ) / 2,
                      }).y
                      }
        )
      `}
                  >
                    {formatImperialLength(tlength / 10, unitSystem)}
                  </text>

                  <line
                    x1={
                      transformCoordinates({
                        x: contentBounds.minX,
                        z: contentBounds.maxZ + 10,
                      }).x + 3
                    }
                    y1={
                      transformCoordinates({
                        x: contentBounds.minX,
                        z: contentBounds.maxZ + 10,
                      }).y
                    }
                    x2={
                      transformCoordinates({
                        x: contentBounds.maxX,
                        z: contentBounds.maxZ + 10,
                      }).x - 2
                    }
                    y2={
                      transformCoordinates({
                        x: contentBounds.maxX,
                        z: contentBounds.maxZ + 10,
                      }).y
                    }
                    stroke="black"
                    strokeWidth="1"
                    markerStart="url(#arrow1)"
                    markerEnd="url(#arrow)"
                  />
                  <text
                    style={{
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      MozUserSelect: "none",
                      msUserSelect: "none",
                    }}
                    x={
                      transformCoordinates({
                        x: (contentBounds.minX + contentBounds.maxX) / 2,
                        z: contentBounds.maxZ + 20,
                      }).x
                    }
                    y={
                      transformCoordinates({
                        x: (contentBounds.minX + contentBounds.maxX) / 2,
                        z: contentBounds.maxZ + 16,
                      }).y
                    }
                    fontSize="11"
                    fill="black"
                    textAnchor="middle"
                  >
                    {formatImperialLength(twidth / 10, unitSystem)}
                  </text>
                </>
              )}

              {sortedRooms.map((room) => {
                const transformedPoints =
                  room.floor_polygon.map(transformCoordinates);

                if (
                  room.room_type === "Wall" &&
                  room.floor_polygon.length === 2
                ) {
                  const startPoint = transformedPoints[0];
                  const endPoint = transformedPoints[1];
                  const isSelected = selectedRoomIds.includes(room.id);
                  const midpoint = {
                    x: (startPoint.x + endPoint.x) / 2,
                    y: (startPoint.y + endPoint.y) / 2,
                  };

                  const wallWidth = wallWidths[room.id] || room.width || 4;

                  const dx = endPoint.x - startPoint.x;
                  const dy = endPoint.y - startPoint.y;
                  const length = Math.sqrt(dx * dx + dy * dy);

                  if (length === 0) return null;

                  const perpX = -dy / length;
                  const perpY = dx / length;
                  const halfWidth = wallWidth / 2;

                  let topStart = {
                    x: startPoint.x + perpX * halfWidth,
                    y: startPoint.y + perpY * halfWidth,
                  };
                  let topEnd = {
                    x: endPoint.x + perpX * halfWidth,
                    y: endPoint.y + perpY * halfWidth,
                  };
                  let bottomStart = {
                    x: startPoint.x - perpX * halfWidth,
                    y: startPoint.y - perpY * halfWidth,
                  };
                  let bottomEnd = {
                    x: endPoint.x - perpX * halfWidth,
                    y: endPoint.y - perpY * halfWidth,
                  };

                  // Check for wall connections at both start and end points
                  const SNAP_THRESHOLD = 0.1; // Very small threshold for exact matches
                  const existingWalls = floorPlanData.rooms.filter(
                    r => r.room_type === "Wall" && 
                         r.floor_polygon.length === 2 && 
                         r.id !== room.id
                  );
                  
                  let connectedAtStart: { start: Point; end: Point; width: number; connectionPoint: Point } | null = null;
                  let connectedAtEnd: { start: Point; end: Point; width: number; connectionPoint: Point } | null = null;
                  
                  for (const otherWall of existingWalls) {
                    const otherStart = otherWall.floor_polygon[0];
                    const otherEnd = otherWall.floor_polygon[1];
                    
                    // Check ONLY if current wall's START point (index 0) matches other wall's points
                    const distStartToOtherStart = Math.sqrt(
                      Math.pow(room.floor_polygon[0].x - otherStart.x, 2) +
                      Math.pow(room.floor_polygon[0].z - otherStart.z, 2)
                    );
                    const distStartToOtherEnd = Math.sqrt(
                      Math.pow(room.floor_polygon[0].x - otherEnd.x, 2) +
                      Math.pow(room.floor_polygon[0].z - otherEnd.z, 2)
                    );
                    
                    // Only set connectedAtStart if the START point matches
                    if (distStartToOtherStart < SNAP_THRESHOLD) {
                      connectedAtStart = {
                        start: otherStart,
                        end: otherEnd,
                        width: wallWidths[otherWall.id] || otherWall.width || 4,
                        connectionPoint: otherStart,
                      };
                    } else if (distStartToOtherEnd < SNAP_THRESHOLD) {
                      connectedAtStart = {
                        start: otherStart,
                        end: otherEnd,
                        width: wallWidths[otherWall.id] || otherWall.width || 4,
                        connectionPoint: otherEnd,
                      };
                    }
                    
                    // Check ONLY if current wall's END point (index 1) matches other wall's points
                    const distEndToOtherStart = Math.sqrt(
                      Math.pow(room.floor_polygon[1].x - otherStart.x, 2) +
                      Math.pow(room.floor_polygon[1].z - otherStart.z, 2)
                    );
                    const distEndToOtherEnd = Math.sqrt(
                      Math.pow(room.floor_polygon[1].x - otherEnd.x, 2) +
                      Math.pow(room.floor_polygon[1].z - otherEnd.z, 2)
                    );
                    
                    // Only set connectedAtEnd if the END point matches
                    if (distEndToOtherStart < SNAP_THRESHOLD) {
                      connectedAtEnd = {
                        start: otherStart,
                        end: otherEnd,
                        width: wallWidths[otherWall.id] || otherWall.width || 4,
                        connectionPoint: otherStart,
                      };
                    } else if (distEndToOtherEnd < SNAP_THRESHOLD) {
                      connectedAtEnd = {
                        start: otherStart,
                        end: otherEnd,
                        width: wallWidths[otherWall.id] || otherWall.width || 4,
                        connectionPoint: otherEnd,
                      };
                    }
                  }
                  
                  // Apply mitered corner at start if connected there
                  if (connectedAtStart) {
                    const miteredCorner = calculateSingleWallMiter(
                      room.floor_polygon[0],
                      room.floor_polygon[1],
                      wallWidth,
                      connectedAtStart.start,
                      connectedAtStart.end,
                      connectedAtStart.width,
                      room.floor_polygon[0], // Pass the actual start point
                      true // Miter at start
                    );
                    
                    if (miteredCorner) {
                      topStart = miteredCorner.topStart;
                      bottomStart = miteredCorner.bottomStart;
                    }
                  }
                  
                  // Apply mitered corner at end if connected there
                  if (connectedAtEnd) {
                    const miteredCorner = calculateSingleWallMiter(
                      room.floor_polygon[0],
                      room.floor_polygon[1],
                      wallWidth,
                      connectedAtEnd.start,
                      connectedAtEnd.end,
                      connectedAtEnd.width,
                      room.floor_polygon[1], // Pass the actual end point
                      false // Miter at end
                    );
                    
                    if (miteredCorner) {
                      topEnd = miteredCorner.topEnd;
                      bottomEnd = miteredCorner.bottomEnd;
                    }
                  }

                  const polygonPoints = `${topStart.x},${topStart.y} ${topEnd.x},${topEnd.y} ${bottomEnd.x},${bottomEnd.y} ${bottomStart.x},${bottomStart.y}`;

                  return (
                    <g
                      key={room.id}
                      transform={`rotate(${roomRotations[room.id] || 0}, ${midpoint.x
                        }, ${midpoint.y})`}
                    >
                      {isSelected && (
                        <polygon
                          points={polygonPoints}
                          fill="none"
                          stroke="#2196F3"
                          strokeWidth="3"
                          strokeOpacity="0.5"
                          style={{ pointerEvents: "none" }}
                        />
                      )}

                      <polygon
                        id={room.id}
                        className={`wall-polygon ${isSelected ? "selected-wall" : ""
                          }`}
                        points={polygonPoints}
                        fill="black"
                        stroke="none"
                        onClick={(e) => {
                          if (getLabelPlacementState().isPlacing) return;
                          if (getDoorPlacementState().isPlacing) return;
                          if (getWindowPlacementState().isPlacing) return;
                          if (
                            isDrawingActive ||
                            activeBuildTool === "drawWall" ||
                            activeBuildTool === "drawRoom" ||
                            activeBuildTool === "drawBoundry" ||
                            isLabelPlacementMode
                          )
                            return;

                          setSelectedRoomIds((prev) =>
                            prev.filter((id) => !id.includes("-wall-"))
                          );

                          handleRoomSelection(
                            room.id,
                            e,
                            selectedRoomIds,
                            setSelectedRoomIds,
                            handleRoomTypeUpdate,
                            openProjectPanel
                          );
                        }}
                        onMouseDown={
                          isSelected && !isMobile
                            ? (e) => {
                                // On desktop, allow dragging from the polygon itself
                                e.stopPropagation();
                                handleMouseDownWithHistory(
                                  e,
                                  room.id,
                                  svgRef,
                                  setDragState,
                                  setHasChanges,
                                  setSelectedRoomIds,
                                  selectedRoomIds
                                );
                              }
                            : !isSelected
                            ? (e) =>
                              handleMouseDownWithHistory(
                                e,
                                room.id,
                                svgRef,
                                setDragState,
                                setHasChanges,
                                setSelectedRoomIds,
                                selectedRoomIds
                              )
                            : undefined
                        }
                        onTouchStart={
                          isRoomDraggingLocked || isSelected
                            ? undefined
                            : (e) =>
                              handleTouchStartWithHistory(
                                e,
                                room.id,
                                svgRef,
                                setDragState,
                                setHasChanges,
                                setSelectedRoomIds,
                                selectedRoomIds
                              )
                        }
                      />

                      {isSelected && (
                        <>
                          {/* Endpoint resize handles */}
                          <circle
                            cx={startPoint.x}
                            cy={startPoint.y}
                            r={isMobile ? 8 : 6}
                            className="resize-handle"
                            onMouseDown={(e) =>
                              handleVertexMouseDownWithHistory(
                                e,
                                room.id,
                                0,
                                svgRef,
                                setDragState,
                                setSelectedRoomIds,
                                selectedRoomIds,
                                setHasChanges
                              )
                            }
                            onTouchStart={(e) =>
                              handleVertexTouchStartWithHistory(
                                e,
                                room.id,
                                0,
                                svgRef,
                                setDragState,
                                setSelectedRoomIds,
                                selectedRoomIds,
                                setHasChanges
                              )
                            }
                          />

                          <circle
                            cx={endPoint.x}
                            cy={endPoint.y}
                            r={isMobile ? 8 : 6}
                            className="resize-handle"
                            onMouseDown={(e) =>
                              handleVertexMouseDownWithHistory(
                                e,
                                room.id,
                                1,
                                svgRef,
                                setDragState,
                                setSelectedRoomIds,
                                selectedRoomIds,
                                setHasChanges
                              )
                            }
                            onTouchStart={(e) =>
                              handleVertexTouchStartWithHistory(
                                e,
                                room.id,
                                1,
                                svgRef,
                                setDragState,
                                setSelectedRoomIds,
                                selectedRoomIds,
                                setHasChanges
                              )
                            }
                          />
                          
                          {/* Center drag handle for moving the wall - only visible on mobile */}
                          {isMobile && (
                            <circle
                              cx={midpoint.x}
                              cy={midpoint.y}
                              r={10}
                              fill="#2196F3"
                              stroke="white"
                              strokeWidth={2}
                              cursor="move"
                              onTouchStart={(e) => {
                                e.stopPropagation();
                                if (e.touches.length === 1) {
                                  handleTouchStartWithHistory(
                                    e,
                                    room.id,
                                    svgRef,
                                    setDragState,
                                    setHasChanges,
                                    setSelectedRoomIds,
                                    selectedRoomIds
                                  );
                                }
                              }}
                            />
                          )}
                        </>
                      )}
                      {(() => {
                        const wallLength = Math.sqrt(
                          Math.pow(room.floor_polygon[1].x - room.floor_polygon[0].x, 2) +
                          Math.pow(room.floor_polygon[1].z - room.floor_polygon[0].z, 2)
                        ) / 10;

                        const startPos = transformCoordinates(room.floor_polygon[0]);
                        const endPos = transformCoordinates(room.floor_polygon[1]);

                        const midX = (startPos.x + endPos.x) / 2;
                        const midY = (startPos.y + endPos.y) / 2;

                        const dx = endPos.x - startPos.x;
                        const dy = endPos.y - startPos.y;
                        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

                        const offsetDistance = 8;
                        const isVertical = Math.abs(angle) > 45 && Math.abs(angle) < 135;

                        const labelX = midX + (isVertical ? offsetDistance : 0);
                        const labelY = midY + (isVertical ? 0 : -offsetDistance);

                        const isWallSelected = selectedRoomIds.includes(room.id) && selectedRoomIds.length === 1;
                        const isEditingThisWall = editingWallId === room.id && editingWallLength !== '';
                        
                        // Show current length in blue box when wall is selected
                        const displayLength = isEditingThisWall 
                          ? editingWallLength 
                          : formatImperialLength(coordToInches(wallLength), unitSystem);

                        return (
                          <>
                            <text
                              key={`wall-measurement-${room.id}`}
                              x={labelX}
                              y={labelY}
                              fontSize="8"
                              fontWeight="bold"
                              fill="black"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              transform={`rotate(${angle > 90 || angle < -90 ? angle + 180 : angle}, ${labelX}, ${labelY})`}
                              style={{
                                userSelect: "none",
                                WebkitUserSelect: "none",
                                MozUserSelect: "none",
                                msUserSelect: "none",
                              }}
                            >
                              {formatImperialLength(coordToInches(wallLength), unitSystem)}
                            </text>
                            
                            {/* Show blue input box when wall is selected */}
                            {isWallSelected && (
                              <g transform={`translate(${midX}, ${midY - 25})`}>
                                <rect
                                  x={-27}
                                  y={-11}
                                  width={50}
                                  height={22}
                                  fill="rgba(33, 150, 243, 0.95)"
                                  stroke="#1976D2"
                                  strokeWidth="1.5"
                                  rx={4}
                                />
                                <text
                                  x={0}
                                  y={0}
                                  fill="white"
                                  fontSize="12"
                                  fontWeight="700"
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  style={{
                                    userSelect: "none",
                                    WebkitUserSelect: "none",
                                  }}
                                >
                                  {isEditingThisWall 
                                    ? `${editingWallLength}${unitSystem === 'metric' ? ' cm' : '"'}` 
                                    : displayLength}
                                </text>
                              </g>
                            )}
                          </>
                        );
                      })()}
                    </g>
                  );
                }

                const polygonPoints = transformedPoints
                  .map((p) => `${p.x},${p.y}`)
                  .join(" ");
                const centroid = calculateRoomCentroid(transformedPoints);
                const isOverlapping = isRoomOverlapping(
                  room.id,
                  overlappingRooms
                );
                const isSelected = selectedRoomIds.includes(room.id);
                const isPrimarySelection =
                  selectedRoomIds.length > 0 && selectedRoomIds[0] === room.id;
                const isWall = room.room_type === "Wall";

                return (
                  <g
                    key={room.id}
                    transform={`rotate(${roomRotations[room.id] || 0}, ${centroid.x
                      }, ${centroid.y})`}
                  >
                    <polygon
                      id={room.id}
                      key={`${room.id}-${room.room_type}`}
                      className={`room-polygon ${isSelected
                        ? isPrimarySelection
                          ? "primary-selection"
                          : "secondary-selection"
                        : ""
                        } ${isOverlapping ? "overlapping" : ""} ${isWall ? "wall-polygon" : ""
                        } ${isLabelPlacementMode ||
                          getDoorPlacementState().isPlacing ||
                          getWindowPlacementState().isPlacing
                          ? "disable-interaction"
                          : ""
                        }`}
                      points={polygonPoints}
                      fill={room.isBoundary ? "transparent" : getRoomColor(room.room_type, room.id)}
                      stroke={room.isBoundary ? "#ff6b00" : "none"}
                      strokeWidth={room.isBoundary ? "3" : "0"}
                      strokeDasharray={room.isBoundary ? "8,4" : "none"}
                      style={{
                        strokeWidth: room.isBoundary ? "3px" : "0px",
                        stroke: room.isBoundary ? "#ff6b00" : "none",
                      }}
                      onClick={(e) => {
                        if (viewOnly) {
                          e.stopPropagation();
                          e.preventDefault();
                          return;
                        }
                        if (getLabelPlacementState().isPlacing) {
                          return;
                        }
                        if (getDoorPlacementState().isPlacing) {
                          return;
                        }
                        if (getWindowPlacementState().isPlacing) {
                          return;
                        }
                        if (
                          isDrawingActive ||
                          activeBuildTool === "drawWall" ||
                          activeBuildTool === "drawRoom" || activeBuildTool === "drawBoundry"
                        ) {
                          return;
                        }

                        handleRoomSelection(
                          room.id,
                          e,
                          selectedRoomIds,
                          setSelectedRoomIds,
                          handleRoomTypeUpdate,
                          openProjectPanel
                        );
                      }}
                      onDragOver={(e) => {
                        if (!isWall) {
                          e.preventDefault();
                          e.currentTarget.classList.add("drop-target");
                        }
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove("drop-target");
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove("drop-target");

                        const roomType = e.dataTransfer.getData("roomType");

                        if (
                          roomType &&
                          room.room_type !== roomType &&
                          !isWall
                        ) {
                          if (!hasChanges) {
                            captureOriginalState();
                          }

                          handleRoomTypeUpdate(room.id, roomType);
                          setHasChanges(true);

                          const svgElement =
                            e.currentTarget as SVGPolygonElement;
                          svgElement.classList.add("room-updated");
                          setTimeout(() => {
                            svgElement.classList.remove("room-updated");
                          }, 300);
                        }
                      }}
                      onMouseDown={(e) =>
                        handleMouseDownWithHistory(
                          e,
                          room.id,
                          svgRef,
                          setDragState,
                          setHasChanges,
                          setSelectedRoomIds,
                          selectedRoomIds
                        )
                      }
                      onTouchStart={(e) =>
                        handleTouchStartWithHistory(
                          e,
                          room.id,
                          svgRef,
                          setDragState,
                          setHasChanges,
                          setSelectedRoomIds,
                          selectedRoomIds
                        )
                      }
                    />
                    {isSelected &&
                      !isWall &&
                      renderEdgeHandles(
                        room,
                        transformCoordinates,
                        handleEdgeMouseDownWithHistory,
                        handleEdgeTouchStartWithHistory,
                        svgRef,
                        setDragState,
                        setSelectedRoomIds,
                        selectedRoomIds,
                        setHasChanges,
                        isMobile,
                        floorPlanData
                      )}
                    {isSelected &&
                      !isWall &&
                      transformedPoints.map((point, index) => (
                        <circle
                          key={`handle-${room.id}-${index}`}
                          cx={point.x}
                          cy={point.y}
                          r={isMobile ? 8 : 6}
                          className="resize-handle"
                          onMouseDown={(e) =>
                            handleVertexMouseDownWithHistory(
                              e,
                              room.id,
                              index,
                              svgRef,
                              setDragState,
                              setSelectedRoomIds,
                              selectedRoomIds,
                              setHasChanges
                            )
                          }
                          onTouchStart={(e) =>
                            handleVertexTouchStartWithHistory(
                              e,
                              room.id,
                              index,
                              svgRef,
                              setDragState,
                              setSelectedRoomIds,
                              selectedRoomIds,
                              setHasChanges
                            )
                          }
                        />
                      ))}
                    {/* Render inner angles at each vertex */}
                    {isSelected &&
                      !isWall &&
                      !room.isBoundary &&
                      (() => {
                        // Calculate angles for the original polygon (before transformation)
                        const angles = calculateRoomAngles(room.floor_polygon);
                        
                        return room.floor_polygon.map((point, index) => {
                          const prevIndex = (index - 1 + room.floor_polygon.length) % room.floor_polygon.length;
                          const nextIndex = (index + 1) % room.floor_polygon.length;
                          
                          // Transform vertex and adjacent points to screen coordinates
                          const transformedVertex = transformCoordinates(room.floor_polygon[index]);
                          const transformedPrev = transformCoordinates(room.floor_polygon[prevIndex]);
                          const transformedNext = transformCoordinates(room.floor_polygon[nextIndex]);
                          
                          // Calculate label position in original coordinates
                          const labelPos = calculateAngleLabelPosition(
                            room.floor_polygon[prevIndex],
                            room.floor_polygon[index],
                            room.floor_polygon[nextIndex],
                            15,
                            10
                          );
                          
                          // Transform the label position to screen coordinates
                          const transformedLabelPos = transformCoordinates(labelPos);
                          
                          // Calculate vectors for arc drawing
                          const v1x = transformedPrev.x - transformedVertex.x;
                          const v1y = transformedPrev.y - transformedVertex.y;
                          const v2x = transformedNext.x - transformedVertex.x;
                          const v2y = transformedNext.y - transformedVertex.y;
                          
                          // Calculate start and end angles for the arc
                          const startAngle = Math.atan2(v1y, v1x);
                          const endAngle = Math.atan2(v2y, v2x);
                          
                          // Normalize angle difference
                          let angleDiff = endAngle - startAngle;
                          if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
                          if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
                          
                          // Arc radius
                          const arcRadius = 12;
                          
                          // Calculate arc path
                          const startX = transformedVertex.x + Math.cos(startAngle) * arcRadius;
                          const startY = transformedVertex.y + Math.sin(startAngle) * arcRadius;
                          const endX = transformedVertex.x + Math.cos(endAngle) * arcRadius;
                          const endY = transformedVertex.y + Math.sin(endAngle) * arcRadius;
                          
                          const largeArcFlag = Math.abs(angleDiff) > Math.PI ? 1 : 0;
                          const sweepFlag = angleDiff > 0 ? 1 : 0;
                          
                          const arcPath = `M ${startX} ${startY} A ${arcRadius} ${arcRadius} 0 ${largeArcFlag} ${sweepFlag} ${endX} ${endY}`;
                          
                          const angleText = angles[index].toFixed(1);
                          
                          return (
                            <g key={`angle-${room.id}-${index}`}>
                              {/* Arc indicator at the vertex */}
                              <path
                                d={arcPath}
                                fill="none"
                                stroke="#4CAF50"
                                strokeWidth="1.5"
                                strokeOpacity="0.8"
                                style={{ pointerEvents: "none" }}
                              />
                              
                              {/* Angle text without background box */}
                              <text
                                x={transformedLabelPos.x}
                                y={transformedLabelPos.y}
                                fontSize="9"
                                fontWeight="700"
                                fill="#2E7D32"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  pointerEvents: "none",
                                  fontFamily: "system-ui, -apple-system, sans-serif",
                                  textShadow: "0 0 3px white, 0 0 3px white, 0 0 3px white, 0 0 3px white"
                                }}
                              >
                                {angleText}°
                              </text>
                            </g>
                          );
                        });
                      })()}
                    {isSelected && !isWall && !room.isBoundary && shouldShowRotationIcon() && (
                      <foreignObject
                        x={centroid.x - (isMobile ? 8 : 10)}
                        y={centroid.y - (isMobile ? 8 : 10)}
                        width={isMobile ? "20" : "20"}
                        height={isMobile ? "20" : "20"}
                      >
                        <button
                          className="rotate-button"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const direction =
                              (navigator.platform.indexOf("Mac") !== -1
                                ? e.metaKey
                                : e.ctrlKey || e.metaKey) || e.altKey
                                ? "left"
                                : "right";

                            if (selectedRoomIds.length > 1) {
                              rotateAllSelectedRooms(direction);
                            } else {
                              handleRotateRoomWithHistory(
                                room.id,
                                direction,
                                roomRotations,
                                setRoomRotations,
                                setHasChanges,
                                checkAndUpdateOverlaps
                              );
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (selectedRoomIds.length > 1) {
                              rotateAllSelectedRooms("left");
                            } else {
                              handleRotateRoomWithHistory(
                                room.id,
                                "left",
                                roomRotations,
                                setRoomRotations,
                                setHasChanges,
                                checkAndUpdateOverlaps
                              );
                            }
                          }}
                          style={{
                            width: isMobile ? "20px" : "30px",
                            height: isMobile ? "20px" : "30px",
                            position: "relative",
                            right: isMobile ? "8px" : "12px",
                            bottom: "5px",
                            borderRadius: "50%",
                            zIndex: "100",
                            background: "transparent",
                            color: "#333333",
                            fontWeight: "bolder",
                            fontSize: isMobile ? "15px" : "20px",
                            border: "none",
                            cursor: "pointer",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            MozUserSelect: "none",
                            msUserSelect: "none",
                          }}
                          title={
                            selectedRoomIds.length > 1
                              ? "Rotate all selected rooms"
                              : "Left-click to rotate clockwise, Right-click to rotate counter-clockwise"
                          }
                        >
                          <b>↻</b>
                        </button>
                      </foreignObject>
                    )}
                    {isSelected && !isWall && !room.isBoundary && isMobile && (
                      <>
                        {(() => {
                          const topLeftVertex = transformedPoints.reduce((topLeft, point) => {
                            if (point.x <= topLeft.x && point.y <= topLeft.y) {
                              return point;
                            }
                            return topLeft;
                          }, transformedPoints[0]);

                          const handleDeleteClick = (e: React.MouseEvent) => {
                            if (viewOnly) {
                              return;
                            }
                            e.stopPropagation();
                            e.preventDefault();

                            captureStateBeforeChange();

                            const roomIds = selectedRoomIds.filter(
                              (id) => !id.includes("-wall-") && !floorPlanData.rooms.find(r => r.id === id)?.isBoundary
                            );

                            setFloorPlanData((prevData) => {
                              let updatedRooms = prevData.rooms.filter(
                                (r) => !roomIds.includes(r.id)
                              );

                              const tempData = {
                                ...prevData,
                                rooms: updatedRooms,
                              };

                              const updatedData = ensureCoordinateSystem(tempData);

                              setTimeout(() => {
                                updateContextFloorPlanData(updatedData);
                              }, 0);

                              return updatedData;
                            });

                            setSelectedRoomIds([]);
                            setSelectedRoomId(null);
                            setHasChanges(true);
                            checkAndUpdateOverlaps();
                          };

                          return (
                            <g
                              className="delete-room-icon"
                              transform={`translate(${topLeftVertex.x - 15}, ${topLeftVertex.y - 15})`}
                              onMouseDown={handleDeleteClick}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(e as any);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="12"
                                fill="#ff4444"
                                stroke="white"
                                strokeWidth="2"
                              />
                              <svg
                                x="-8"
                                y="-8"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="white"
                              >
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                              </svg>
                            </g>
                          );
                        })()}
                      </>
                    )}
                    {isSelected && room.isBoundary && (
                      <>
                        {(() => {
                          const bottomRightVertex = transformedPoints.reduce((bottomRight, point) => {
                            if (point.x >= bottomRight.x && point.y >= bottomRight.y) {
                              return point;
                            }
                            return bottomRight;
                          }, transformedPoints[0]);

                          const handleDeleteClick = (e: React.MouseEvent) => {
                            if (viewOnly) {
                              return;
                            }
                            e.stopPropagation();
                            e.preventDefault();

                            captureStateBeforeChange();

                            setFloorPlanData((prevData) => {
                              const updatedRooms = prevData.rooms.filter(r => r.id !== room.id);
                              const nonBoundaryRooms = updatedRooms.filter(r =>
                                r.room_type !== "Wall" && r.room_type !== "Reference" && !r.isBoundary
                              );

                              const updatedData = {
                                ...prevData,
                                rooms: updatedRooms,
                                room_count: nonBoundaryRooms.length,
                                total_area: nonBoundaryRooms.reduce((sum, r) => sum + r.area, 0),
                              };

                              setTimeout(() => {
                                updateContextFloorPlanData(updatedData);
                              }, 0);

                              return updatedData;
                            });

                            setSelectedRoomIds([]);
                            setHasChanges(true);
                          };
                          if (viewOnly) return null;
                          else {

                            return (
                              <g
                                className="delete-boundary-icon"
                                transform={`translate(${bottomRightVertex.x + 15}, ${bottomRightVertex.y + 15})`}
                                onMouseDown={handleDeleteClick}
                                onTouchStart={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteClick(e as any);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <circle
                                  cx="0"
                                  cy="0"
                                  r="12"
                                  fill="#ff4444"
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <svg
                                  x="-8"
                                  y="-8"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </g>
                            );
                          }
                        })()}
                      </>
                    )}

                    {options.showRoomLabels && !isWall && !room.isBoundary && (
                      <>
                        {(() => {
                          const adjustedPosition = findRoomNamePosition(
                            transformedPoints,
                            centroid,
                            room.room_type
                          );

                          return (
                            <text
                              className="room-label room-name1"
                              x={adjustedPosition.x}
                              y={adjustedPosition.y}
                              pointerEvents="none"
                              fill={"black"}
                              textAnchor="middle"
                            >
                              {room.room_type}
                            </text>
                          );
                        })()}
                      </>
                    )}

                    {options.showRoomLabels && !isWall && (
                      <>
                        {(() => {
                          const wallLabels = positionWallMeasurements(
                            transformedPoints,
                            room
                          );

                          if (wallLabels && wallLabels.length > 0) {
                            return wallLabels.map((label, index) => (
                              <text
                                key={`wall-label-${room.id}-${index}`}
                                className="room-label dimension-label"
                                x={label.position.x}
                                y={label.position.y}
                                textAnchor="middle"
                                dominantBaseline="central"
                                pointerEvents="none"
                                fill={"black"}
                                transform={`rotate(${label.angle}, ${label.position.x}, ${label.position.y})`}
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "bold",
                                }}
                              >
                                {formatImperialLength(coordToInches(parseFloat(label.value)), unitSystem)}
                              </text>
                            ));
                          }

                          return null;
                        })()}
                      </>
                    )}
                    {renderRoomWalls(room)}
                  </g>
                );
              })}

              {renderAllWallSegments()}

              {/* Wall drawing is now handled inline via event handlers - see renderPreviewWall() */}
              {/* {activeBuildTool === "drawWall" && (
                <WallDrawingTool
                  isActive={activeBuildTool === "drawWall"}
                  svgRef={svgRef}
                  reverseTransformCoordinates={reverseTransformCoordinates}
                  transformCoordinates={transformCoordinates}
                  scale={scale}
                  onWallCreated={handleWallCreated}
                  onDrawingStateChange={setIsDrawingActive}
                />
              )} */}
              {activeBuildTool === "drawRoom" && (
                <RoomDrawingTool
                  isActive={activeBuildTool === "drawRoom"}
                  svgRef={svgRef}
                  reverseTransformCoordinates={reverseTransformCoordinates}
                  transformCoordinates={transformCoordinates}
                  onRoomCreated={handleRoomCreated}
                  onDrawingStateChange={setIsDrawingActive}
                />
              )}
              {activeBuildTool === "drawBoundry" && (
                <BoundaryDrawingTool
                  isActive={activeBuildTool === "drawBoundry"}
                  svgRef={svgRef}
                  reverseTransformCoordinates={reverseTransformCoordinates}
                  transformCoordinates={transformCoordinates}
                  onBoundaryCreated={handleBoundaryCreated}
                  onDrawingStateChange={setIsDrawingActive}
                />
              )}

              {floorPlanData.labels &&
                floorPlanData.labels.map((label) => {
                  const position = transformCoordinates(label.position);
                  const currentFontSize = getResponsiveSize(
                    label.fontSize || 12,
                    scale
                  );

                  const isSvgPath =
                    label.text.startsWith("/Signs/") ||
                    label.text.startsWith("/Symbols/");

                  const handleLabelDrag = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();
                    if (selectedLabelId === label.id) {
                      if (!hasChanges) {
                        captureOriginalState();
                      }
                      captureStateBeforeChange();

                      if ("touches" in e) {
                        handleLabelTouchStart(
                          e,
                          label.id,
                          svgRef,
                          label,
                          setDragState,
                          setHasChanges
                        );
                      } else {
                        handleLabelMouseDown(
                          e,
                          label.id,
                          svgRef,
                          label,
                          setDragState,
                          setHasChanges
                        );
                      }
                    }
                  };

                  if (isSvgPath) {
                    return (
                      <g
                        key={label.id}
                        className={`floor-plan-label ${selectedLabelId === label.id ? "selected-label" : ""
                          } ${dragState.isLabelDragging &&
                            dragState.labelId === label.id
                            ? "dragging"
                            : ""
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLabelId(
                            selectedLabelId === label.id ? null : label.id
                          );
                        }}
                        onMouseDown={(e) => handleLabelDrag(e)}
                        onTouchStart={(e) => handleLabelDrag(e)}
                        style={{
                          cursor:
                            selectedLabelId === label.id ? "move" : "pointer",
                        }}
                      >
                        <image
                          href={label.text}
                          x={position.x - currentFontSize}
                          y={position.y - currentFontSize}
                          width={currentFontSize * 2}
                          height={currentFontSize * 2}
                          preserveAspectRatio="xMidYMid meet"
                          pointerEvents="all"
                        />
                        {selectedLabelId === label.id && (
                          <>
                            <rect
                              x={
                                position.x -
                                currentFontSize -
                                getResponsiveSize(5, scale)
                              }
                              y={
                                position.y -
                                currentFontSize -
                                getResponsiveSize(5, scale)
                              }
                              width={
                                currentFontSize * 2 +
                                getResponsiveSize(10, scale)
                              }
                              height={
                                currentFontSize * 2 +
                                getResponsiveSize(10, scale)
                              }
                              fill="none"
                              stroke="#2196F3"
                              strokeWidth={getResponsiveSize(1.5, scale)}
                              rx={getResponsiveSize(3, scale)}
                              ry={getResponsiveSize(3, scale)}
                            />

                            <g
                              onClick={(e) => {
                                e.stopPropagation();
                                if (updateLabel) {
                                  if (!hasChanges) {
                                    captureOriginalState();
                                  }
                                  captureStateBeforeChange();

                                  const updatedLabel = {
                                    ...label,
                                    fontSize: Math.max(
                                      8,
                                      (label.fontSize || 12) - 1
                                    ),
                                  };

                                  updateLabel(label.id, updatedLabel);
                                  setFloorPlanData((prevData) => {
                                    const updatedLabels = (
                                      prevData.labels || []
                                    ).map((l) =>
                                      l.id === label.id ? updatedLabel : l
                                    );
                                    return {
                                      ...prevData,
                                      labels: updatedLabels,
                                    };
                                  });
                                  setHasChanges(true);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={
                                  position.x -
                                  currentFontSize -
                                  getResponsiveSize(18, scale)
                                }
                                cy={position.y}
                                r={getResponsiveSize(8, scale)}
                                fill="white"
                                stroke="black"
                                strokeWidth={getResponsiveSize(1, scale)}
                              />
                              <text
                                x={
                                  position.x -
                                  currentFontSize -
                                  getResponsiveSize(18, scale)
                                }
                                y={position.y + getResponsiveSize(5, scale)}
                                fill="black"
                                fontSize={getResponsiveSize(14, scale)}
                                fontWeight="bold"
                                textAnchor="middle"
                                pointerEvents="none"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                }}
                              >
                                -
                              </text>
                            </g>

                            <g
                              onClick={(e) => {
                                e.stopPropagation();
                                if (updateLabel) {
                                  if (!hasChanges) {
                                    captureOriginalState();
                                  }
                                  captureStateBeforeChange();

                                  const updatedLabel = {
                                    ...label,
                                    fontSize: Math.min(
                                      48,
                                      (label.fontSize || 12) + 1
                                    ),
                                  };

                                  updateLabel(label.id, updatedLabel);
                                  setFloorPlanData((prevData) => {
                                    const updatedLabels = (
                                      prevData.labels || []
                                    ).map((l) =>
                                      l.id === label.id ? updatedLabel : l
                                    );
                                    return {
                                      ...prevData,
                                      labels: updatedLabels,
                                    };
                                  });
                                  setHasChanges(true);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={
                                  position.x +
                                  currentFontSize +
                                  getResponsiveSize(18, scale)
                                }
                                cy={position.y}
                                r={getResponsiveSize(8, scale)}
                                fill="white"
                                stroke="black"
                                strokeWidth={getResponsiveSize(1, scale)}
                              />
                              <text
                                x={
                                  position.x +
                                  currentFontSize +
                                  getResponsiveSize(18, scale)
                                }
                                y={position.y + getResponsiveSize(5, scale)}
                                fill="black"
                                fontSize={getResponsiveSize(14, scale)}
                                fontWeight="bold"
                                textAnchor="middle"
                                pointerEvents="none"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                }}
                              >
                                +
                              </text>
                            </g>

                            {/* Mobile delete button for labels */}
                            {isMobile && (
                              <g
                                className="delete-label-icon"
                                transform={`translate(${position.x - currentFontSize - 15}, ${position.y - currentFontSize - 15})`}
                                onMouseDown={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      labels: prevData.labels
                                        ? prevData.labels.filter((l) => l.id !== label.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });
                                  setSelectedLabelId(null);
                                  setHasChanges(true);
                                }}
                                onTouchStart={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      labels: prevData.labels
                                        ? prevData.labels.filter((l) => l.id !== label.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });
                                  setSelectedLabelId(null);
                                  setHasChanges(true);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <circle
                                  cx="0"
                                  cy="0"
                                  r="12"
                                  fill="#ff4444"
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <svg
                                  x="-8"
                                  y="-8"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </g>
                            )}
                          </>
                        )}
                      </g>
                    );
                  } else {
                    const charWidth = currentFontSize * 0.6;
                    const boxPadding = getResponsiveSize(8, scale);
                    const boxWidth =
                      label.text.length * charWidth + boxPadding * 2;
                    const boxHeight = currentFontSize + boxPadding;

                    return (
                      <g
                        key={label.id}
                        className={`floor-plan-label ${selectedLabelId === label.id ? "selected-label" : ""
                          } ${dragState.isLabelDragging &&
                            dragState.labelId === label.id
                            ? "dragging"
                            : ""
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLabelId(
                            selectedLabelId === label.id ? null : label.id
                          );
                        }}
                        onMouseDown={(e) => handleLabelDrag(e)}
                        onTouchStart={(e) => handleLabelDrag(e)}
                        style={{
                          cursor:
                            selectedLabelId === label.id ? "move" : "pointer",
                        }}
                      >
                        <rect
                          x={position.x - boxWidth / 2}
                          y={position.y - boxHeight / 2}
                          width={boxWidth}
                          height={boxHeight}
                          fill={
                            selectedLabelId === label.id
                              ? "rgba(33, 150, 243, 0.3)"
                              : "rgba(255, 255, 255, 0.7)"
                          }
                          rx={getResponsiveSize(3, scale)}
                          ry={getResponsiveSize(3, scale)}
                          stroke={
                            selectedLabelId === label.id ? "#2196F3" : "#000000"
                          }
                          strokeWidth={
                            selectedLabelId === label.id
                              ? getResponsiveSize(1.5, scale)
                              : getResponsiveSize(0.5, scale)
                          }
                          opacity="0.8"
                        />
                        <text
                          x={position.x}
                          y={position.y + currentFontSize * 0.35}
                          fill={label.color || "#000000"}
                          fontSize={currentFontSize}
                          fontWeight="bold"
                          textAnchor="middle"
                          pointerEvents="none"
                          style={{
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            MozUserSelect: "none",
                            msUserSelect: "none",
                          }}
                        >
                          {label.text}
                        </text>

                        {selectedLabelId === label.id && (
                          <>
                            <g
                              onClick={(e) => {
                                e.stopPropagation();
                                if (updateLabel) {
                                  if (!hasChanges) {
                                    captureOriginalState();
                                  }
                                  captureStateBeforeChange();

                                  const updatedLabel = {
                                    ...label,
                                    fontSize: Math.max(
                                      8,
                                      (label.fontSize || 12) - 1
                                    ),
                                  };

                                  updateLabel(label.id, updatedLabel);
                                  setFloorPlanData((prevData) => {
                                    const updatedLabels = (
                                      prevData.labels || []
                                    ).map((l) =>
                                      l.id === label.id ? updatedLabel : l
                                    );
                                    return {
                                      ...prevData,
                                      labels: updatedLabels,
                                    };
                                  });
                                  setHasChanges(true);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={
                                  position.x -
                                  boxWidth / 2 -
                                  getResponsiveSize(10, scale)
                                }
                                cy={position.y}
                                r={getResponsiveSize(8, scale)}
                                fill="white"
                                stroke="black"
                                strokeWidth={getResponsiveSize(1, scale)}
                              />
                              <text
                                x={
                                  position.x -
                                  boxWidth / 2 -
                                  getResponsiveSize(10, scale)
                                }
                                y={position.y + getResponsiveSize(5, scale)}
                                fill="black"
                                fontSize={getResponsiveSize(14, scale)}
                                fontWeight="bold"
                                textAnchor="middle"
                                pointerEvents="none"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                }}
                              >
                                -
                              </text>
                            </g>

                            <g
                              onClick={(e) => {
                                e.stopPropagation();
                                if (updateLabel) {
                                  if (!hasChanges) {
                                    captureOriginalState();
                                  }
                                  captureStateBeforeChange();

                                  const updatedLabel = {
                                    ...label,
                                    fontSize: Math.min(
                                      24,
                                      (label.fontSize || 12) + 1
                                    ),
                                  };

                                  updateLabel(label.id, updatedLabel);
                                  setFloorPlanData((prevData) => {
                                    const updatedLabels = (
                                      prevData.labels || []
                                    ).map((l) =>
                                      l.id === label.id ? updatedLabel : l
                                    );
                                    return {
                                      ...prevData,
                                      labels: updatedLabels,
                                    };
                                  });
                                  setHasChanges(true);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={
                                  position.x +
                                  boxWidth / 2 +
                                  getResponsiveSize(10, scale)
                                }
                                cy={position.y}
                                r={getResponsiveSize(8, scale)}
                                fill="white"
                                stroke="black"
                                strokeWidth={getResponsiveSize(1, scale)}
                              />
                              <text
                                x={
                                  position.x +
                                  boxWidth / 2 +
                                  getResponsiveSize(10, scale)
                                }
                                y={position.y + getResponsiveSize(5, scale)}
                                fill="black"
                                fontSize={getResponsiveSize(14, scale)}
                                fontWeight="bold"
                                textAnchor="middle"
                                pointerEvents="none"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                }}
                              >
                                +
                              </text>
                            </g>

                            {/* Mobile delete button for text labels */}
                            {isMobile && (
                              <g
                                className="delete-label-icon"
                                transform={`translate(${position.x - boxWidth / 2 - 15}, ${position.y - boxHeight / 2 - 15})`}
                                onMouseDown={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      labels: prevData.labels
                                        ? prevData.labels.filter((l) => l.id !== label.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });
                                  setSelectedLabelId(null);
                                  setHasChanges(true);
                                }}
                                onTouchStart={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      labels: prevData.labels
                                        ? prevData.labels.filter((l) => l.id !== label.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });
                                  setSelectedLabelId(null);
                                  setHasChanges(true);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <circle
                                  cx="0"
                                  cy="0"
                                  r="12"
                                  fill="#ff4444"
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <svg
                                  x="-8"
                                  y="-8"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </g>
                            )}
                          </>
                        )}
                      </g>
                    );
                  }
                })}

              {floorPlanData.objects &&
                (() => {
                  // Sort objects so selected object renders last (on top)
                  const sortedObjects = [...(floorPlanData.objects || [])].sort((a, b) => {
                    if (a.id === selectedObjectId) return 1; // Selected object goes to end
                    if (b.id === selectedObjectId) return -1;
                    return 0; // Keep other objects in original order
                  });

                  // Filter out objects in selected room
                  const filteredObjects = sortedObjects.filter((obj) => {
                    // If no room is selected, show all objects
                    if (!selectedRoomId && selectedRoomIds.length === 0) return true;

                    // Check if object is inside any selected room
                    const selectedRooms = floorPlanData.rooms?.filter(r =>
                      r.id === selectedRoomId || selectedRoomIds.includes(r.id)
                    ) || [];

                    const objPos = obj.position;

                    for (const room of selectedRooms) {
                      const poly = room.floor_polygon || [];
                      let inside = false;

                      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
                        if (((poly[i].z > objPos.z) !== (poly[j].z > objPos.z)) &&
                          (objPos.x < (poly[j].x - poly[i].x) * (objPos.z - poly[i].z) / (poly[j].z - poly[i].z) + poly[i].x)) {
                          inside = !inside;
                        }
                      }

                      if (inside) return false; // Object is in selected room, don't render it
                    }

                    return true; // Object not in selected room, render it
                  });

                  return filteredObjects;
                })().map((object) => {
                  const position = transformCoordinates(object.position);
                  let currentObjectSize;
                  if (object.objectPath.includes("chair")) {
                    currentObjectSize = getResponsiveSize(65, scale);
                  } else {
                    currentObjectSize = getResponsiveSize(100, scale);
                  }
                  const objectRotation =
                    objectRotations[object.id] || object.rotation || 0;

                  const handleObjectDrag = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();
                    if (viewOnly) return;
                    if (selectedObjectId === object.id && !isRotatingObject) {
                      if (!hasChanges) {
                        captureOriginalState();
                      }
                      captureStateBeforeChange();

                      if ("touches" in e) {
                        handleObjectTouchStart(
                          e,
                          object.id,
                          svgRef,
                          object,
                          setDragState,
                          setHasChanges
                        );
                      } else {
                        handleObjectMouseDown(
                          e,
                          object.id,
                          svgRef,
                          object,
                          setDragState,
                          setHasChanges
                        );
                      }
                    }
                  };

                  return (
                    <g
                      key={object.id}
                      className={`floor-plan-object ${selectedObjectId === object.id ? "selected-object" : ""
                        } ${dragState.isObjectDragging &&
                          dragState.objectId === object.id
                          ? "dragging"
                          : ""
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isRotatingObject && !viewOnly) {
                          // Get click coordinates
                          const rect = svgRef.current?.getBoundingClientRect();
                          if (!rect) return;

                          const clickX = e.clientX - rect.left;
                          const clickY = e.clientY - rect.top;

                          // Convert click position to floor plan coordinates
                          const floorPlanCoords = reverseTransformCoordinates(clickX, clickY);

                          // Find the room at the exact click position
                          const roomAtClick = findRoomAtPosition(
                            floorPlanCoords,
                            floorPlanData,
                            roomRotations
                          );

                          // Find the object closest to click point
                          let closestObjectId: string | null = null;
                          let closestDistance: number = Infinity;

                          (floorPlanData.objects || []).forEach((obj) => {
                            // Get object position in FLOOR PLAN coordinates (not transformed)
                            const objFloorPos = obj.position;

                            // Calculate distance in floor plan coordinate space
                            const dx = floorPlanCoords.x - objFloorPos.x;
                            const dz = floorPlanCoords.z - objFloorPos.z;
                            const distanceFromCenter = Math.sqrt(dx * dx + dz * dz);

                            // Object size in floor plan units (assuming 150 units base size / 10 = 15 feet)
                            const objSizeInFloorUnits = 25; // Adjust based on your actual object size

                            // Only consider if click is within the object bounds
                            if (distanceFromCenter <= objSizeInFloorUnits && distanceFromCenter < closestDistance) {
                              closestObjectId = obj.id;
                              closestDistance = distanceFromCenter;
                            }
                          });

                          // Threshold in floor plan units (not pixels)
                          const CLICK_THRESHOLD_FLOOR_UNITS = 15; // Adjust this value as needed
                          const isClickNearObjectCenter = closestDistance < CLICK_THRESHOLD_FLOOR_UNITS;

                          if (isClickNearObjectCenter && closestObjectId !== null) {
                            // Click is near the center of an object - select the object
                            setSelectedObjectId(
                              selectedObjectId === closestObjectId ? null : closestObjectId
                            );
                          } else if (roomAtClick) {
                            // Click is not near object center but is over a room - select the room
                            setSelectedObjectId(null);
                            setSelectedRoomId(roomAtClick.id);
                            setSelectedRoomIds([roomAtClick.id]);
                            if (setContextSelectedRoomIds) {
                              setContextSelectedRoomIds([roomAtClick.id]);
                            }
                          } else {
                            // Click is not near any object center and not over a room - deselect
                            setSelectedObjectId(null);
                          }
                        }
                      }}
                      onMouseDown={(e) => handleObjectDrag(e)}
                      onTouchStart={(e) => handleObjectDrag(e)}
                      style={{
                        cursor:
                          selectedObjectId === object.id && !isRotatingObject
                            ? "move"
                            : "pointer",
                      }}
                    >
                      <g
                        transform={`rotate(${objectRotation}, ${position.x}, ${position.y})`}
                      >
                        <image
                          href={object.objectPath}
                          x={position.x - currentObjectSize}
                          y={position.y - currentObjectSize}
                          width={currentObjectSize * 2}
                          height={currentObjectSize * 2}
                          preserveAspectRatio="xMidYMid meet"
                          pointerEvents="all"
                        />
                      </g>

                      {selectedObjectId === object.id && (
                        <>
                          {/* Center selection indicator circle with rotation control */}
                          <g
                            onMouseDown={(e) =>
                              handleRotationStart(
                                e,
                                object.id,
                                position.x,
                                position.y
                              )
                            }
                            onTouchStart={(e) =>
                              handleRotationTouchStart(
                                e,
                                object.id,
                                position.x,
                                position.y
                              )
                            }
                            style={{ cursor: "grab" }}
                          >
                            <circle
                              cx={position.x}
                              cy={position.y}
                              r={getResponsiveSize(12, scale)}
                              fill="white"
                              stroke="#2196F3"
                              strokeWidth={getResponsiveSize(4, scale)}
                            />
                            <text
                              x={position.x}
                              y={position.y + getResponsiveSize(5, scale)}
                              fill="#2196F3"
                              fontSize={getResponsiveSize(14, scale)}
                              fontWeight="bold"
                              textAnchor="middle"
                              pointerEvents="none"
                              style={{
                                userSelect: "none",
                                WebkitUserSelect: "none",
                                MozUserSelect: "none",
                                msUserSelect: "none",
                              }}
                            >
                              ↻
                            </text>
                          </g>

                          {isRotatingObject &&
                            rotatingObjectId === object.id && (
                              <line
                                x1={position.x}
                                y1={position.y}
                                x2={
                                  position.x +
                                  currentObjectSize +
                                  getResponsiveSize(5, scale)
                                }
                                y2={
                                  position.y -
                                  currentObjectSize -
                                  getResponsiveSize(5, scale)
                                }
                                stroke="#2196F3"
                                strokeWidth={getResponsiveSize(1, scale)}
                                strokeDasharray={`${getResponsiveSize(
                                  3,
                                  scale
                                )},${getResponsiveSize(3, scale)}`}
                                opacity="0.5"
                              />
                            )}

                          {/* Mobile delete button for objects */}
                          {isMobile && (
                            <g
                              className="delete-object-icon"
                              transform={`translate(${position.x - currentObjectSize - 15}, ${position.y - currentObjectSize - 15})`}
                              onMouseDown={(e) => {
                                if (viewOnly) return;
                                e.stopPropagation();
                                e.preventDefault();

                                if (isRotatingObject && rotatingObjectId === object.id) {
                                  setIsRotatingObject(false);
                                  setRotatingObjectId(null);
                                  setRotationStartAngle(0);
                                  setInitialObjectRotation(0);
                                }

                                captureStateBeforeChange();
                                setFloorPlanData((prevData) => {
                                  const updatedData = {
                                    ...prevData,
                                    objects: prevData.objects
                                      ? prevData.objects.filter((obj) => obj.id !== object.id)
                                      : [],
                                  };
                                  setTimeout(() => {
                                    updateContextFloorPlanData(updatedData);
                                  }, 0);
                                  return updatedData;
                                });
                                setSelectedObjectId(null);
                                setHasChanges(true);
                              }}
                              onTouchStart={(e) => {
                                if (viewOnly) return;
                                e.stopPropagation();
                                e.preventDefault();

                                if (isRotatingObject && rotatingObjectId === object.id) {
                                  setIsRotatingObject(false);
                                  setRotatingObjectId(null);
                                  setRotationStartAngle(0);
                                  setInitialObjectRotation(0);
                                }

                                captureStateBeforeChange();
                                setFloorPlanData((prevData) => {
                                  const updatedData = {
                                    ...prevData,
                                    objects: prevData.objects
                                      ? prevData.objects.filter((obj) => obj.id !== object.id)
                                      : [],
                                  };
                                  setTimeout(() => {
                                    updateContextFloorPlanData(updatedData);
                                  }, 0);
                                  return updatedData;
                                });
                                setSelectedObjectId(null);
                                setHasChanges(true);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="12"
                                fill="#ff4444"
                                stroke="white"
                                strokeWidth="2"
                              />
                              <svg
                                x="-8"
                                y="-8"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="white"
                              >
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                              </svg>
                            </g>
                          )}
                        </>
                      )}
                    </g>
                  );
                })}

              {floorPlanData.doors &&
                floorPlanData.doors.map((door) => {
                  const position = transformCoordinates(door.position);
                  // Use a fixed base size of 32, scaled consistently
                  const baseDoorSize = getResponsiveSize(32,scale);
                  const doorWidth = doorWidths[door.id] || door.width || 2;
                  const actualWidth = baseDoorSize * doorWidth;
                  const doorRotation = door.rotation || 0;
                  const flipState = doorFlipStates[door.id] || {
                    flipH: false,
                    flipV: false,
                  };
                  const scaleX = flipState.flipH ? -1 : 1;
                  const scaleY = flipState.flipV ? -1 : 1;
                  const resizeHandleY = position.y - baseDoorSize;
                  const resizeHandleHeight = baseDoorSize * 2;
                  const leftControlX =
                    position.x - actualWidth - getResponsiveSize(2, scale);
                  const rightControlX =
                    position.x + actualWidth + getResponsiveSize(2, scale);
                  const flipIconY =
                    resizeHandleY - getResponsiveSize(12, scale);

                  const handleDoorDrag = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (selectedDoorId === door.id) {
                      if (!hasChanges) {
                        captureOriginalState();
                      }
                      captureStateBeforeChange();

                      if (!svgRef.current) return;

                      const isTouch = "touches" in e;
                      const clientX = isTouch
                        ? e.touches[0].clientX
                        : e.clientX;
                      const clientY = isTouch
                        ? e.touches[0].clientY
                        : e.clientY;

                      const rect = svgRef.current.getBoundingClientRect();
                      const startX = clientX - rect.left;
                      const startY = clientY - rect.top;

                      setDragState((prevState) => ({
                        ...prevState,
                        active: true,
                        roomId: null,
                        roomIds: [],
                        vertexIndex: null,
                        edgeIndices: null,
                        startX,
                        startY,
                        lastX: startX,
                        lastY: startY,
                        isResizing: false,
                        isEdgeResizing: false,
                        isGroupOperation: false,
                        isDoorDragging: true,
                        doorId: door.id,
                        isWindowDragging: false,
                        windowId: null,
                        isLabelDragging: false,
                        labelId: null,
                        isObjectDragging: false,
                        objectId: null,
                      }));

                      const handleMove = (
                        moveEvent: MouseEvent | TouchEvent
                      ) => {
                        moveEvent.preventDefault();

                        if (!svgRef.current) return;

                        const rect = svgRef.current.getBoundingClientRect();
                        const isTouchMove = "touches" in moveEvent;
                        const clientX = isTouchMove
                          ? moveEvent.touches[0].clientX
                          : moveEvent.clientX;
                        const clientY = isTouchMove
                          ? moveEvent.touches[0].clientY
                          : moveEvent.clientY;

                        const currentX = clientX - rect.left;
                        const currentY = clientY - rect.top;

                        const floorPlanCoords = reverseTransformCoordinates(
                          currentX,
                          currentY
                        );

                        const snapResult = findNearestWall(
                          floorPlanCoords,
                          floorPlanData,
                          roomRotations,
                          8.0
                        );

                        if (
                          snapResult.isOnWall &&
                          snapResult.snappedPosition &&
                          snapResult.suggestedRotation !== undefined
                        ) {
                          setFloorPlanData((prevData) => {
                            const updatedDoors = (prevData.doors || []).map(
                              (d) => {
                                if (d.id === door.id) {
                                  return {
                                    ...d,
                                    position: snapResult.snappedPosition!,
                                    rotation: snapResult.suggestedRotation!,
                                  };
                                }
                                return d;
                              }
                            );
                            return { ...prevData, doors: updatedDoors };
                          });
                        }
                      };

                      const handleEnd = () => {
                        setDragState({
                          active: false,
                          roomId: null,
                          roomIds: [],
                          vertexIndex: null,
                          edgeIndices: null,
                          startX: 0,
                          startY: 0,
                          lastX: 0,
                          lastY: 0,
                          isResizing: false,
                          isEdgeResizing: false,
                          isGroupOperation: false,
                          isDoorDragging: false,
                          doorId: null,
                          isWindowDragging: false,
                          windowId: null,
                          isLabelDragging: false,
                          labelId: null,
                          isObjectDragging: false,
                          objectId: null,
                        });

                        // Use functional setState to get current state
                        setFloorPlanData((currentData) => {
                          setTimeout(
                            () => updateContextFloorPlanData(currentData),
                            0
                          );
                          return currentData;
                        });

                        setHasChanges(true);

                        document.removeEventListener(
                          "mousemove",
                          handleMove as any
                        );
                        document.removeEventListener("mouseup", handleEnd);
                        document.removeEventListener(
                          "touchmove",
                          handleMove as any
                        );
                        document.removeEventListener("touchend", handleEnd);
                        document.removeEventListener("touchcancel", handleEnd);
                      };

                      if (isTouch) {
                        document.addEventListener(
                          "touchmove",
                          handleMove as any,
                          { passive: false }
                        );
                        document.addEventListener("touchend", handleEnd);
                        document.addEventListener("touchcancel", handleEnd);
                      } else {
                        document.addEventListener(
                          "mousemove",
                          handleMove as any
                        );
                        document.addEventListener("mouseup", handleEnd);
                      }
                    }
                  };

                  /*     if (isTouch) {
                        document.addEventListener(
                          "touchmove",
                          handleMove as any,
                          { passive: false }
                        );
                        document.addEventListener("touchend", handleEnd);
                      } else {
                        document.addEventListener(
                          "mousemove",
                          handleMove as any
                        );
                        document.addEventListener("mouseup", handleEnd);
                      }
                    }
                  };
 */
                  const handleLeftResize = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();

                    if (!hasChanges) {
                      captureOriginalState();
                    }
                    captureStateBeforeChange();

                    const startX =
                      "touches" in e ? e.touches[0].clientX : e.clientX;
                    const startY =
                      "touches" in e ? e.touches[0].clientY : e.clientY;
                    const startWidth = doorWidth;

                    const handleMouseMove = (
                      moveE: MouseEvent | TouchEvent
                    ) => {
                      const currentX =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientX
                          : (moveE as MouseEvent).clientX;
                      const currentY =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientY
                          : (moveE as MouseEvent).clientY;

                      const deltaX =
                        (currentX - startX) / (scale * parentScale);
                      const deltaY =
                        (currentY - startY) / (scale * parentScale);

                      let distanceFromCenter;

                      if (
                        Math.abs(doorRotation - 90) < 10 ||
                        Math.abs(doorRotation - 270) < 10
                      ) {
                        distanceFromCenter = -deltaY;
                      } else {
                        distanceFromCenter = -deltaX;
                      }

                      const newWidth = Math.max(
                        0.5,
                        Math.min(
                          3,
                          startWidth + distanceFromCenter / baseDoorSize
                        )
                      );

                      setDoorWidths((prev) => ({
                        ...prev,
                        [door.id]: newWidth,
                      }));
                      updateDoor(door.id, { width: newWidth });
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener(
                        "mousemove",
                        handleMouseMove
                      );
                      document.removeEventListener("mouseup", handleMouseUp);
                      document.removeEventListener(
                        "touchmove",
                        handleMouseMove as any
                      );
                      document.removeEventListener("touchend", handleMouseUp);
                      setHasChanges(true);
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                    document.addEventListener(
                      "touchmove",
                      handleMouseMove as any,
                      { passive: false }
                    );
                    document.addEventListener("touchend", handleMouseUp);
                  };

                  const handleRightResize = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();

                    if (!hasChanges) {
                      captureOriginalState();
                    }
                    captureStateBeforeChange();

                    const startX =
                      "touches" in e ? e.touches[0].clientX : e.clientX;
                    const startY =
                      "touches" in e ? e.touches[0].clientY : e.clientY;
                    const startWidth = doorWidth;

                    const handleMouseMove = (
                      moveE: MouseEvent | TouchEvent
                    ) => {
                      const currentX =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientX
                          : (moveE as MouseEvent).clientX;
                      const currentY =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientY
                          : (moveE as MouseEvent).clientY;

                      const deltaX =
                        (currentX - startX) / (scale * parentScale);
                      const deltaY =
                        (currentY - startY) / (scale * parentScale);

                      let distanceFromCenter;

                      if (
                        Math.abs(doorRotation - 90) < 10 ||
                        Math.abs(doorRotation - 270) < 10
                      ) {
                        distanceFromCenter = deltaY;
                      } else {
                        distanceFromCenter = deltaX;
                      }

                      const newWidth = Math.max(
                        0.5,
                        Math.min(
                          3,
                          startWidth + distanceFromCenter / baseDoorSize
                        )
                      );

                      setDoorWidths((prev) => ({
                        ...prev,
                        [door.id]: newWidth,
                      }));
                      updateDoor(door.id, { width: newWidth });
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener(
                        "mousemove",
                        handleMouseMove
                      );
                      document.removeEventListener("mouseup", handleMouseUp);
                      document.removeEventListener(
                        "touchmove",
                        handleMouseMove as any
                      );
                      document.removeEventListener("touchend", handleMouseUp);
                      setHasChanges(true);
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                    document.addEventListener(
                      "touchmove",
                      handleMouseMove as any,
                      { passive: false }
                    );
                    document.addEventListener("touchend", handleMouseUp);
                  };

                  const handleHorizontalFlip = (e: React.MouseEvent) => {
                    e.stopPropagation();

                    setDoorFlipStates((prev) => ({
                      ...prev,
                      [door.id]: {
                        flipH: !flipState.flipH,
                        flipV: flipState.flipV,
                      },
                    }));
                  };

                  const handleVerticalFlip = (e: React.MouseEvent) => {
                    e.stopPropagation();

                    setDoorFlipStates((prev) => ({
                      ...prev,
                      [door.id]: {
                        flipH: flipState.flipH,
                        flipV: !flipState.flipV,
                      },
                    }));
                  };

                  return (
                    <g
                      key={door.id}
                      className={`floor-plan-door ${selectedDoorId === door.id ? "selected-door" : ""
                        } ${dragState.isDoorDragging && dragState.doorId === door.id
                          ? "dragging"
                          : ""
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDoorId(
                          selectedDoorId === door.id ? null : door.id
                        );
                      }}
                      onMouseDown={(e) => handleDoorDrag(e)}
                      onTouchStart={(e) => handleDoorDrag(e)}
                      style={{
                        cursor: selectedDoorId === door.id ? "move" : "pointer",
                      }}
                    >
                      <g
                        transform={`rotate(${doorRotation}, ${position.x}, ${position.y})`}
                      >
                        <g
                          transform={`translate(${position.x}, ${position.y
                            }) scale(${scaleX}, ${scaleY}) translate(${-position.x}, ${-position.y})`}
                        >
                          <image
                            href={door.doorPath}
                            x={position.x - actualWidth}
                            y={position.y - baseDoorSize}
                            width={actualWidth * 2}
                            height={baseDoorSize * 2}
                            preserveAspectRatio="none"
                            pointerEvents="all"
                          />
                        </g>

                        {selectedDoorId === door.id && (
                          <rect
                            x={
                              position.x -
                              actualWidth -
                              getResponsiveSize(1, scale)
                            }
                            y={
                              position.y -
                              baseDoorSize -
                              getResponsiveSize(1, scale)
                            }
                            width={
                              actualWidth * 2 + getResponsiveSize(4, scale)
                            }
                            height={
                              baseDoorSize * 2 + getResponsiveSize(4, scale)
                            }
                            fill="none"
                            stroke="#2196F3"
                            strokeWidth={getResponsiveSize(1.5, scale)}
                            rx={getResponsiveSize(3, scale)}
                            ry={getResponsiveSize(3, scale)}
                            pointerEvents="none"
                          />
                        )}

                        {selectedDoorId === door.id && (
                          <>
                            <g
                              onClick={handleHorizontalFlip}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={leftControlX}
                                cy={flipIconY}
                                r={getResponsiveSize(10, scale)}
                                fill="white"
                                stroke="#2196F3"
                                strokeWidth={getResponsiveSize(2, scale)}
                              />
                              <text
                                x={leftControlX}
                                y={flipIconY + getResponsiveSize(4, scale)}
                                fill="#2196F3"
                                fontSize={getResponsiveSize(12, scale)}
                                fontWeight="bold"
                                textAnchor="middle"
                                pointerEvents="none"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                }}
                              >
                                ↔
                              </text>
                            </g>

                            <g
                              onClick={handleVerticalFlip}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx={rightControlX}
                                cy={flipIconY}
                                r={getResponsiveSize(10, scale)}
                                fill="white"
                                stroke="#2196F3"
                                strokeWidth={getResponsiveSize(2, scale)}
                              />
                              <text
                                x={rightControlX}
                                y={flipIconY + getResponsiveSize(4, scale)}
                                fill="#2196F3"
                                fontSize={getResponsiveSize(12, scale)}
                                fontWeight="bold"
                                textAnchor="middle"
                                pointerEvents="none"
                                style={{
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                }}
                              >
                                ↕
                              </text>
                            </g>
                            <rect
                              x={
                                position.x -
                                actualWidth -
                                getResponsiveSize(4, scale)
                              }
                              y={resizeHandleY}
                              width={getResponsiveSize(4, scale)}
                              height={resizeHandleHeight}
                              fill="#2196F3"
                              stroke="#1976D2"
                              strokeWidth={getResponsiveSize(0.5, scale)}
                              rx={getResponsiveSize(1, scale)}
                              style={{
                                cursor:
                                  Math.abs(doorRotation - 90) < 10 ||
                                    Math.abs(doorRotation - 270) < 10
                                    ? "ns-resize"
                                    : "ew-resize",
                              }}
                              onMouseDown={handleLeftResize}
                              onTouchStart={handleLeftResize}
                            />

                            <rect
                              x={position.x + actualWidth}
                              y={resizeHandleY}
                              width={getResponsiveSize(4, scale)}
                              height={resizeHandleHeight}
                              fill="#2196F3"
                              stroke="#1976D2"
                              strokeWidth={getResponsiveSize(0.5, scale)}
                              rx={getResponsiveSize(1, scale)}
                              style={{
                                cursor:
                                  Math.abs(doorRotation - 90) < 10 ||
                                    Math.abs(doorRotation - 270) < 10
                                    ? "ns-resize"
                                    : "ew-resize",
                              }}
                              onMouseDown={handleRightResize}
                              onTouchStart={handleRightResize}
                            />

                            {/* Mobile delete button for doors */}
                            {isMobile && (
                              <g
                                className="delete-door-icon"
                                transform={`translate(${position.x - actualWidth - 15}, ${flipIconY - 30})`}
                                onMouseDown={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      doors: prevData.doors
                                        ? prevData.doors.filter((d) => d.id !== door.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });

                                  setDoorWidths((prev) => {
                                    const newWidths = { ...prev };
                                    delete newWidths[door.id];
                                    return newWidths;
                                  });

                                  setSelectedDoorId(null);
                                  setHasChanges(true);
                                }}
                                onTouchStart={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      doors: prevData.doors
                                        ? prevData.doors.filter((d) => d.id !== door.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });

                                  setDoorWidths((prev) => {
                                    const newWidths = { ...prev };
                                    delete newWidths[door.id];
                                    return newWidths;
                                  });

                                  setSelectedDoorId(null);
                                  setHasChanges(true);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <circle
                                  cx="0"
                                  cy="0"
                                  r="12"
                                  fill="#ff4444"
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <svg
                                  x="-8"
                                  y="-8"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </g>
                            )}
                          </>
                        )}
                      </g>
                    </g>
                  );
                })}
              {floorPlanData.windows &&
                floorPlanData.windows.map((window) => {
                  const position = transformCoordinates(window.position);
                  // Use a fixed base size of 24, scaled consistently
                  const baseWindowSize = getResponsiveSize(24,scale);
                  const windowWidth =
                    windowWidths[window.id] || window.width || 1;
                  const actualWidth = baseWindowSize * windowWidth;
                  const windowRotation = window.rotation || 0;

                  const handleWindowDrag = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (selectedWindowId === window.id) {
                      if (!hasChanges) {
                        captureOriginalState();
                      }
                      captureStateBeforeChange();

                      if (!svgRef.current) return;

                      const isTouch = "touches" in e;
                      const clientX = isTouch
                        ? e.touches[0].clientX
                        : e.clientX;
                      const clientY = isTouch
                        ? e.touches[0].clientY
                        : e.clientY;

                      const rect = svgRef.current.getBoundingClientRect();
                      const startX = clientX - rect.left;
                      const startY = clientY - rect.top;

                      setDragState((prevState) => ({
                        ...prevState,
                        active: true,
                        roomId: null,
                        roomIds: [],
                        vertexIndex: null,
                        edgeIndices: null,
                        startX,
                        startY,
                        lastX: startX,
                        lastY: startY,
                        isResizing: false,
                        isEdgeResizing: false,
                        isGroupOperation: false,
                        isDoorDragging: false,
                        doorId: null,
                        isWindowDragging: true,
                        windowId: window.id,
                        isLabelDragging: false,
                        labelId: null,
                        isObjectDragging: false,
                        objectId: null,
                      }));

                      const handleMove = (
                        moveEvent: MouseEvent | TouchEvent
                      ) => {
                        moveEvent.preventDefault();

                        if (!svgRef.current) return;

                        const rect = svgRef.current.getBoundingClientRect();
                        const isTouchMove = "touches" in moveEvent;
                        const clientX = isTouchMove
                          ? moveEvent.touches[0].clientX
                          : moveEvent.clientX;
                        const clientY = isTouchMove
                          ? moveEvent.touches[0].clientY
                          : moveEvent.clientY;

                        const currentX = clientX - rect.left;
                        const currentY = clientY - rect.top;

                        const floorPlanCoords = reverseTransformCoordinates(
                          currentX,
                          currentY
                        );

                        const snapResult = findNearestWall(
                          floorPlanCoords,
                          floorPlanData,
                          roomRotations,
                          8.0
                        );

                        if (
                          snapResult.isOnWall &&
                          snapResult.snappedPosition &&
                          snapResult.suggestedRotation !== undefined
                        ) {
                          setFloorPlanData((prevData) => {
                            const updatedWindows = (prevData.windows || []).map(
                              (w) => {
                                if (w.id === window.id) {
                                  return {
                                    ...w,
                                    position: snapResult.snappedPosition!,
                                    rotation: snapResult.suggestedRotation!,
                                  };
                                }
                                return w;
                              }
                            );
                            return { ...prevData, windows: updatedWindows };
                          });
                        }
                      };

                      const handleEnd = () => {
                        setDragState({
                          active: false,
                          roomId: null,
                          roomIds: [],
                          vertexIndex: null,
                          edgeIndices: null,
                          startX: 0,
                          startY: 0,
                          lastX: 0,
                          lastY: 0,
                          isResizing: false,
                          isEdgeResizing: false,
                          isGroupOperation: false,
                          isDoorDragging: false,
                          doorId: null,
                          isWindowDragging: false,
                          windowId: null,
                          isLabelDragging: false,
                          labelId: null,
                          isObjectDragging: false,
                          objectId: null,
                        });

                        // Use functional setState to get current state
                        setFloorPlanData((currentData) => {
                          setTimeout(
                            () => updateContextFloorPlanData(currentData),
                            0
                          );
                          return currentData;
                        });

                        setHasChanges(true);

                        document.removeEventListener(
                          "mousemove",
                          handleMove as any
                        );
                        document.removeEventListener("mouseup", handleEnd);
                        document.removeEventListener(
                          "touchmove",
                          handleMove as any
                        );
                        document.removeEventListener("touchend", handleEnd);
                        document.removeEventListener("touchcancel", handleEnd);
                      };

                      if (isTouch) {
                        document.addEventListener(
                          "touchmove",
                          handleMove as any,
                          { passive: false }
                        );
                        document.addEventListener("touchend", handleEnd);
                        document.addEventListener("touchcancel", handleEnd);
                      } else {
                        document.addEventListener(
                          "mousemove",
                          handleMove as any
                        );
                        document.addEventListener("mouseup", handleEnd);
                      }
                    }
                  };

                  const handleLeftResize = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();

                    if (!hasChanges) {
                      captureOriginalState();
                    }
                    captureStateBeforeChange();

                    const startX =
                      "touches" in e ? e.touches[0].clientX : e.clientX;
                    const startY =
                      "touches" in e ? e.touches[0].clientY : e.clientY;
                    const startWidth = windowWidth;

                    const handleMouseMove = (
                      moveE: MouseEvent | TouchEvent
                    ) => {
                      const currentX =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientX
                          : (moveE as MouseEvent).clientX;
                      const currentY =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientY
                          : (moveE as MouseEvent).clientY;

                      const deltaX =
                        (currentX - startX) / (scale * parentScale);
                      const deltaY =
                        (currentY - startY) / (scale * parentScale);

                      let distanceFromCenter;

                      if (
                        Math.abs(windowRotation - 90) < 10 ||
                        Math.abs(windowRotation - 270) < 10
                      ) {
                        distanceFromCenter = -deltaY;
                      } else {
                        distanceFromCenter = -deltaX;
                      }

                      const newWidth = Math.max(
                        0.5,
                        Math.min(
                          3,
                          startWidth + distanceFromCenter / baseWindowSize
                        )
                      );

                      setWindowWidths((prev) => ({
                        ...prev,
                        [window.id]: newWidth,
                      }));
                      updateWindow(window.id, { width: newWidth });
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener(
                        "mousemove",
                        handleMouseMove
                      );
                      document.removeEventListener("mouseup", handleMouseUp);
                      document.removeEventListener(
                        "touchmove",
                        handleMouseMove as any
                      );
                      document.removeEventListener("touchend", handleMouseUp);
                      setHasChanges(true);
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                    document.addEventListener(
                      "touchmove",
                      handleMouseMove as any,
                      { passive: false }
                    );
                    document.addEventListener("touchend", handleMouseUp);
                  };

                  const handleRightResize = (
                    e: React.MouseEvent | React.TouchEvent
                  ) => {
                    e.stopPropagation();

                    if (!hasChanges) {
                      captureOriginalState();
                    }
                    captureStateBeforeChange();

                    const startX =
                      "touches" in e ? e.touches[0].clientX : e.clientX;
                    const startY =
                      "touches" in e ? e.touches[0].clientY : e.clientY;
                    const startWidth = windowWidth;

                    const handleMouseMove = (
                      moveE: MouseEvent | TouchEvent
                    ) => {
                      const currentX =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientX
                          : (moveE as MouseEvent).clientX;
                      const currentY =
                        "touches" in moveE
                          ? (moveE as TouchEvent).touches[0].clientY
                          : (moveE as MouseEvent).clientY;

                      const deltaX =
                        (currentX - startX) / (scale * parentScale);
                      const deltaY =
                        (currentY - startY) / (scale * parentScale);

                      let distanceFromCenter;

                      if (
                        Math.abs(windowRotation - 90) < 10 ||
                        Math.abs(windowRotation - 270) < 10
                      ) {
                        distanceFromCenter = deltaY;
                      } else {
                        distanceFromCenter = deltaX;
                      }

                      const newWidth = Math.max(
                        0.5,
                        Math.min(
                          3,
                          startWidth + distanceFromCenter / baseWindowSize
                        )
                      );

                      setWindowWidths((prev) => ({
                        ...prev,
                        [window.id]: newWidth,
                      }));
                      updateWindow(window.id, { width: newWidth });
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener(
                        "mousemove",
                        handleMouseMove
                      );
                      document.removeEventListener("mouseup", handleMouseUp);
                      document.removeEventListener(
                        "touchmove",
                        handleMouseMove as any
                      );
                      document.removeEventListener("touchend", handleMouseUp);
                      setHasChanges(true);
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                    document.addEventListener(
                      "touchmove",
                      handleMouseMove as any,
                      { passive: false }
                    );
                    document.addEventListener("touchend", handleMouseUp);
                  };

                  return (
                    <g
                      key={window.id}
                      className={`floor-plan-window ${selectedWindowId === window.id ? "selected-window" : ""
                        } ${dragState.isWindowDragging &&
                          dragState.windowId === window.id
                          ? "dragging"
                          : ""
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedWindowId(
                          selectedWindowId === window.id ? null : window.id
                        );
                      }}
                      onMouseDown={(e) => handleWindowDrag(e)}
                      onTouchStart={(e) => handleWindowDrag(e)}
                      style={{
                        cursor:
                          selectedWindowId === window.id ? "move" : "pointer",
                      }}
                    >
                      <g
                        transform={`rotate(${windowRotation}, ${position.x}, ${position.y})`}
                      >
                        <image
                          href={window.windowPath}
                          x={position.x - actualWidth}
                          y={position.y - baseWindowSize}
                          width={actualWidth * 2}
                          height={baseWindowSize * 2}
                          preserveAspectRatio="none"
                          pointerEvents="all"
                        />

                        {selectedWindowId === window.id && (
                          <>
                            <rect
                              x={
                                position.x -
                                actualWidth -
                                getResponsiveSize(2, scale)
                              }
                              y={
                                position.y -
                                baseWindowSize -
                                getResponsiveSize(2, scale)
                              }
                              width={
                                actualWidth * 2 + getResponsiveSize(4, scale)
                              }
                              height={
                                baseWindowSize * 2 +
                                getResponsiveSize(4, scale)
                              }
                              fill="none"
                              stroke="#2196F3"
                              strokeWidth={getResponsiveSize(1.5, scale)}
                              rx={getResponsiveSize(3, scale)}
                              ry={getResponsiveSize(3, scale)}
                              pointerEvents="none"
                            />

                            <rect
                              x={
                                position.x -
                                actualWidth -
                                getResponsiveSize(4, scale)
                              }
                              y={position.y - baseWindowSize}
                              width={getResponsiveSize(4, scale)}
                              height={baseWindowSize * 2}
                              fill="#2196F3"
                              stroke="#1976D2"
                              strokeWidth={getResponsiveSize(0.5, scale)}
                              rx={getResponsiveSize(1, scale)}
                              style={{
                                cursor:
                                  Math.abs(windowRotation - 90) < 10 ||
                                    Math.abs(windowRotation - 270) < 10
                                    ? "ns-resize"
                                    : "ew-resize",
                              }}
                              onMouseDown={handleLeftResize}
                              onTouchStart={handleLeftResize}
                            />

                            <rect
                              x={position.x + actualWidth}
                              y={position.y - baseWindowSize}
                              width={getResponsiveSize(4, scale)}
                              height={baseWindowSize * 2}
                              fill="#2196F3"
                              stroke="#1976D2"
                              strokeWidth={getResponsiveSize(0.5, scale)}
                              rx={getResponsiveSize(1, scale)}
                              style={{
                                cursor:
                                  Math.abs(windowRotation - 90) < 10 ||
                                    Math.abs(windowRotation - 270) < 10
                                    ? "ns-resize"
                                    : "ew-resize",
                              }}
                              onMouseDown={handleRightResize}
                              onTouchStart={handleRightResize}
                            />

                            {/* Mobile delete button for windows */}
                            {isMobile && (
                              <g
                                className="delete-window-icon"
                                transform={`translate(${position.x - actualWidth - 15}, ${position.y - baseWindowSize - 30})`}
                                onMouseDown={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      windows: prevData.windows
                                        ? prevData.windows.filter((w) => w.id !== window.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });

                                  setWindowWidths((prev) => {
                                    const newWidths = { ...prev };
                                    delete newWidths[window.id];
                                    return newWidths;
                                  });

                                  setSelectedWindowId(null);
                                  setHasChanges(true);
                                }}
                                onTouchStart={(e) => {
                                  if (viewOnly) return;
                                  e.stopPropagation();
                                  e.preventDefault();

                                  captureStateBeforeChange();
                                  setFloorPlanData((prevData) => {
                                    const updatedData = {
                                      ...prevData,
                                      windows: prevData.windows
                                        ? prevData.windows.filter((w) => w.id !== window.id)
                                        : [],
                                    };
                                    setTimeout(() => {
                                      updateContextFloorPlanData(updatedData);
                                    }, 0);
                                    return updatedData;
                                  });

                                  setWindowWidths((prev) => {
                                    const newWidths = { ...prev };
                                    delete newWidths[window.id];
                                    return newWidths;
                                  });

                                  setSelectedWindowId(null);
                                  setHasChanges(true);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <circle
                                  cx="0"
                                  cy="0"
                                  r="12"
                                  fill="#ff4444"
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <svg
                                  x="-8"
                                  y="-8"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                              </g>
                            )}
                          </>
                        )}
                      </g>
                    </g>
                  );
                })}

              {renderPreviewDimensionLine()}
              {renderPreviewWall()}
              {renderAlignmentGuides()}
              {renderWallAngle()}

              {floorPlanData.dimensionLines &&
                floorPlanData.dimensionLines.map((dimension) => {
                  const startPos = transformCoordinates(dimension.startPoint);
                  const endPos = transformCoordinates(dimension.endPoint);
                  const midPos = transformCoordinates(dimension.midPoint);

                  const dx = endPos.x - startPos.x;
                  const dy = endPos.y - startPos.y;
                  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

                  const offsetStartX = startPos.x;
                  const offsetStartY = startPos.y;
                  const offsetEndX = endPos.x;
                  const offsetEndY = endPos.y;
                  const offsetMidX = midPos.x;
                  const offsetMidY = midPos.y;

                  const isSelected = selectedDimensionId === dimension.id;

                  return (
                    <g
                      key={dimension.id}
                      className="dimension-line"
                      style={{ cursor: "pointer" }}
                    >
                      <line
                        x1={offsetStartX}
                        y1={offsetStartY}
                        x2={offsetEndX}
                        y2={offsetEndY}
                        stroke="transparent"
                        strokeWidth={getResponsiveSize(10, scale)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDimensionId(
                            selectedDimensionId === dimension.id
                              ? null
                              : dimension.id
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      />

                      <line
                        x1={offsetStartX}
                        y1={offsetStartY}
                        x2={offsetEndX}
                        y2={offsetEndY}
                        stroke={isSelected ? "#2196F3" : "#000"}
                        strokeWidth={
                          isSelected
                            ? getResponsiveSize(1.5, scale)
                            : getResponsiveSize(0.8, scale)
                        }
                        markerStart="url(#dimensionArrowStart)"
                        markerEnd="url(#dimensionArrowEnd)"
                        pointerEvents="none"
                      />

                      <text
                        x={offsetMidX}
                        y={
                          offsetMidY +
                          (Math.abs(angle) < 45 || Math.abs(angle) > 135
                            ? getResponsiveSize(15, scale)
                            : getResponsiveSize(-10, scale))
                        }
                        fill={isSelected ? "#2196F3" : "#000"}
                        fontSize={getResponsiveSize(9, scale)}
                        fontWeight="500"
                        textAnchor="middle"
                        transform={`rotate(${angle > 90 || angle < -90 ? angle + 180 : angle
                          }, ${offsetMidX}, ${offsetMidY - getResponsiveSize(8, scale)
                          })`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDimensionId(
                            selectedDimensionId === dimension.id
                              ? null
                              : dimension.id
                          );
                        }}
                        style={{
                          userSelect: "none",
                          WebkitUserSelect: "none",
                          MozUserSelect: "none",
                          msUserSelect: "none",
                          cursor: "pointer",
                        }}
                      >
                        {formatImperialLength(coordToInches(dimension.distance / 10), unitSystem)}
                      </text>

                      {isSelected && (
                        <>
                          <rect
                            x={
                              Math.min(offsetStartX, offsetEndX) -
                              getResponsiveSize(10, scale)
                            }
                            y={
                              Math.min(offsetStartY, offsetEndY) -
                              getResponsiveSize(10, scale)
                            }
                            width={
                              Math.abs(offsetEndX - offsetStartX) +
                              getResponsiveSize(20, scale)
                            }
                            height={
                              Math.abs(offsetEndY - offsetStartY) +
                              getResponsiveSize(20, scale)
                            }
                            fill="none"
                            stroke="#2196F3"
                            strokeWidth={getResponsiveSize(1, scale)}
                            strokeDasharray={`${getResponsiveSize(
                              3,
                              scale
                            )},${getResponsiveSize(3, scale)}`}
                            opacity="0.5"
                            pointerEvents="none"
                          />

                          {/* Mobile delete button for dimension lines */}
                          {isMobile && (
                            <g
                              className="delete-dimension-icon"
                              transform={`translate(${Math.min(offsetStartX, offsetEndX) - 15}, ${Math.min(offsetStartY, offsetEndY) - 15})`}
                              onMouseDown={(e) => {
                                if (viewOnly) return;
                                e.stopPropagation();
                                e.preventDefault();

                                captureStateBeforeChange();
                                setFloorPlanData((prevData) => {
                                  const updatedData = {
                                    ...prevData,
                                    dimensionLines: prevData.dimensionLines
                                      ? prevData.dimensionLines.filter((d) => d.id !== dimension.id)
                                      : [],
                                  };
                                  setTimeout(() => {
                                    updateContextFloorPlanData(updatedData);
                                  }, 0);
                                  return updatedData;
                                });
                                setSelectedDimensionId(null);
                                setHasChanges(true);
                              }}
                              onTouchStart={(e) => {
                                if (viewOnly) return;
                                e.stopPropagation();
                                e.preventDefault();

                                captureStateBeforeChange();
                                setFloorPlanData((prevData) => {
                                  const updatedData = {
                                    ...prevData,
                                    dimensionLines: prevData.dimensionLines
                                      ? prevData.dimensionLines.filter((d) => d.id !== dimension.id)
                                      : [],
                                  };
                                  setTimeout(() => {
                                    updateContextFloorPlanData(updatedData);
                                  }, 0);
                                  return updatedData;
                                });
                                setSelectedDimensionId(null);
                                setHasChanges(true);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="12"
                                fill="#ff4444"
                                stroke="white"
                                strokeWidth="2"
                              />
                              <svg
                                x="-8"
                                y="-8"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="white"
                              >
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                              </svg>
                            </g>
                          )}
                        </>
                      )}
                    </g>
                  );
                })}
              {renderWallHighlights()}
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}